/**
 * JobDetailCockpit — lean Cockpit-skin replacement for /jobs/:id.
 *
 * Replaces the 2,109-line classic JobDetail with a focused tabbed surface:
 *   • Masthead — customer + critical pills + contract value + state from deriveJobState
 *   • KPI strip — Permit / Materials / Schedule / QA / Payment dots
 *   • Overview tab — Action Required (single panel) + 3-col Execution strip
 *   • Activity tab — JobAuditTimeline (real Supabase job_audit_log)
 *   • Images tab — JobPhotoGallery (grouped uploads + lightbox)
 *   • Quality tab — QA + Closeout + Issues + Build Integrity
 *   • Financials tab — JobFinancialsPanel + JobEconomicsBreakdown + ChangeOrderHistory + Credits (role-gated)
 *   • Timeline tab — single UnifiedJobTimeline (retires JobActivityTimeline + JobCommPanel here)
 *   • Documents tab — DocumentCenter
 *
 * Heavy in-view modals (ordering, reschedule, assign, resolve issue) reuse
 * the same canonical action plumbing as the classic page.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Phone, Mail, MapPin, AlertTriangle, DollarSign, CheckCircle, XCircle,
  Clock, Shield, Package, Truck, ClipboardCheck, Camera, FileText,
  OctagonAlert, Users, ExternalLink, ArrowRight, Zap, Calendar, FileCheck,
  Wrench, Sparkles, RefreshCw, ChevronLeft, ChevronRight, MoreHorizontal,
  Pencil, Plus, Trash2, Undo2, ShoppingBag, CreditCard, PencilLine, Lock,
  UserCheck, Flag, PhoneCall, Loader2, ChevronDown, X,
} from "lucide-react";

import { useJobDetailQuery, type JobLeadAttribution, type JobLeadAttributionLead } from "@/hooks/useJobDetailQuery";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { PermissionGate } from "@/components/PermissionGate";
import { MaterialsGateCard } from "@/components/MaterialsGateCard";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSchedulingConflicts } from "@/hooks/useSchedulingConflicts";
import { LifecycleTracker } from "@/components/LifecycleTracker";
import { JobSpecExecutionPanel } from "@/components/jobspec/JobSpecExecutionPanel";
import { MaterialKitPanel } from "@/components/jobspec/MaterialKitPanel";
import { getOrderedStages, getStageConfig } from "@/data/workflowEngine";
import { JobSpecQAPanel } from "@/components/jobspec/JobSpecQAPanel";
import { SpecReconciliationGate } from "@/components/jobspec/SpecReconciliationGate";
import { computeServiceCallRisk, RISK_CONFIG } from "@/data/serviceCallPrevention";
import { ChangeOrderDialog } from "@/components/ChangeOrderDialog";
import type { ChangeOrder } from "@/data/changeOrderTypes";
import { PermitQuickViewModal } from "@/components/quick-actions/PermitQuickViewModal";
import { usePushChain } from "@/hooks/usePushChain";
import { useExecutionProofs } from "@/hooks/useExecutionProofs";

import { getJobBlockers } from "@/data/workflowBlockers";
import { isExternalManagedImport, migrationBypasses } from "@/data/import/migrationVisibilityGuard";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import { getJobNextAction } from "@/data/nextActionEngine";
import { getJobSuggestedActions, getLastActivity, formatRelativeTime, type SuggestedAction } from "@/data/suggestedActions";
import { getJobHealth } from "@/data/jobHealth";
import { getJobPriority, getJobFinancialSummary } from "@/data/jobPriority";
import { getFinancialSummary as getCollectionSummary } from "@/data/invoiceEngine";
import { isInRescission, getRescissionDaysRemaining, formatRescissionEnd } from "@/data/rescission";
import { deriveJobState } from "@/data/jobStateDerived";
import { runAction, type ExecutableAction } from "@/lib/actionRunner";
import { deriveLedgerQaReadiness } from "@/data/execution/qaReadiness";
import { buildJobDocuments } from "@/data/documentTypes";
import { formatAddress } from "@/lib/addressUtils";
import { formatStandardDate } from "@/lib/dateFormat";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { StorageThumbnailImage } from "@/components/media/StorageThumbnailImage";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

import { CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip, SectionEyebrow } from "@/components/cockpit/CockpitShell";

import { JobFinancialsPanel } from "@/components/JobFinancialsPanel";
import { JobBomPanel } from "@/components/jobspec/JobBomPanel";
import { JobSpecDriftBadge } from "@/components/job-detail/JobSpecDriftBadge";
import { JobEconomicsBreakdown } from "@/components/JobEconomicsBreakdown";
import { JobOtherExpensesCard } from "@/components/JobOtherExpensesCard";
import { UnifiedJobTimeline } from "@/components/UnifiedJobTimeline";
import { DocumentCenter } from "@/components/DocumentCenter";
import { JobPhotoGallery } from "@/components/JobPhotoGallery";
import { ContractDocumentView } from "@/components/ContractDocumentView";
import { ChangeOrderHistory } from "@/components/ChangeOrderHistory";
import { CreateCreditForm } from "@/components/CreateCreditForm";

import { OrderingModal } from "@/components/OrderingModal";
import { OrderPrepCard } from "@/components/OrderPrepCard";
import { RescheduleModal } from "@/components/quick-actions/RescheduleModal";
import { QuickAssignModal } from "@/components/quick-actions/QuickAssignModal";
import { QuickResolveIssueModal } from "@/components/quick-actions/QuickResolveIssueModal";
import { IntakeReviewWorkspace } from "@/components/IntakeReviewWorkspace";
import { REVISION_REASON_LABELS, type BuildReviewEvent, type RevisionReason } from "@/data/buildReviewTypes";
import { BuildReviewService } from "@/services/build-review.service";
import { QAService } from "@/services/qa.service";
import { JobService } from "@/services/job.service";
import { CustomerService } from "@/services/customer.service";
import { parseAddressPartsFromText } from "@/types/mappers/quote.mapper";
import { deriveBoundSpecForJob } from "@/services/bound-spec.service";
import { CreditService } from "@/services/credit.service";
import { syncProductRegistryStock, syncStockLevels, syncWarehouseStock, syncJobMaterialState } from "@/services/inventory.service";
import * as ProductRegistryService from "@/services/productRegistry.service";
import { SubcontractorAssignmentService, type SubcontractorAssignment } from "@/services/subcontractor-assignment.service";
import { getJobAuditLog, writeJobMaterialsAuditEntry, type JobAuditEntry, type MaterialsAuditDiff } from "@/services/job-operations.service";
import { LeadService } from "@/services/lead.service";
import { AppointmentResultService } from "@/services/appointment-result.service";
import { ConsultationService, type ConsultationSessionData } from "@/services/consultation.service";
import { MarketplaceService } from "@/services/marketplace.service";
import { AccountingService } from "@/services/accounting.service";
import type { MarketplaceOffer } from "@/data/contractor/marketplaceOffer";
import type { ServiceTicket } from "@/data/serviceTicketTypes";
import { toUiServiceTicket } from "@/mappers/service-ticket.mapper";
import type { DbLead, DbAppointmentResult } from "@/types/database";
import type { PurchaseOrder } from "@/data/VendorPOContext";
import type { JobAllocation } from "@/data/WarehouseInventoryContext";
import { WarehouseService } from "@/services/warehouse.service";
import { useAppData } from "@/data/AppDataContext";
import { getAttachments, createSignedAttachmentUrl, uploadAttachment, deleteAttachment } from "@/services/attachment.service";
import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { IMPORT_PHOTO_BUCKET, getImportPhotoStableKey, resolveImportPhotoUrls } from "@/data/import/importPhotoUrls";
import {
  createSignedStorageFullImageUrl,
  getStorageStableKey,
} from "@/lib/storageImageUrls";
import { getJobBriefing } from "@/data/contractor/jobBriefing";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { PROJECT_TYPES, type CustomerCredit, type FieldMaterialItem, type FieldMaterialReceiptReference, type Job, type JobChecklistItem, type JobChecklistPhaseKey, type JobPhotoGroup, type MaterialLineItem, type MaterialReadiness, type ProjectType } from "@/data/types";
import { DEFAULT_JOB_CHECKLIST_PHASE_LABELS, JOB_CHECKLIST_AUTHORING_PHASES, JOB_CHECKLIST_PHASES, loadDefaultJobChecklist, loadPersistedJobChecklist, normalizeChecklistItems, resolveJobChecklist, syncJobChecklistItems } from "@/services/job-checklist.service";
import type { ExecutionProof } from "@/lib/supabase/executions";
import type { Attachment } from "@/data/attachmentTypes";
import { useProductRegistry, type RegistryProduct } from "@/data/ProductRegistryContext";
import { QuickRegisterProductModal } from "@/components/product-registry/QuickRegisterProductModal";
import { getUnmatchedSkus } from "@/lib/skuValidation";
import type { MigrationAiScopeDraft } from "@/data/import/migrationTypes";

/** Title-Case a hyphen/underscore-separated key for display, e.g. "payment_pending" → "Payment Pending". */
const titleCase = (s: string) =>
  s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function getFinancingApplicationPaymentDisplay(job: Job): {
  label: string;
  kpiTone: "good" | "warn" | "bad" | "default";
  textTone: string;
} | null {
  const invoices = job.financials?.invoices ?? [];
  if (invoices.length > 0) return null;

  const plan = job.financials?.paymentPlan;
  const financing = job.financials?.financing;
  const financingStatus = financing?.financingStatus;
  const hasFinancedStructure =
    plan?.type === "financed" ||
    plan?.type === "mixed" ||
    (plan?.lineItems ?? []).some((line) => line.method === "financed") ||
    financingStatus === "approved" ||
    financingStatus === "pending" ||
    financingStatus === "declined";

  if (!hasFinancedStructure) return null;

  if (financingStatus === "approved") {
    return { label: "Financing Approved", kpiTone: "good", textTone: "text-success" };
  }
  if (financingStatus === "declined") {
    return { label: "Financing Rejected", kpiTone: "bad", textTone: "text-destructive" };
  }
  return { label: "Financing Pending", kpiTone: "warn", textTone: "text-warning" };
}

const getLeadSourceDisplay = (lead?: JobLeadAttributionLead | null) =>
  lead?.leadSource ||
  lead?.sourceName ||
  lead?.leadSourceLookupName ||
  lead?.provider ||
  "Unknown source";

const getLeadEnteredAt = (lead?: JobLeadAttributionLead | null) =>
  lead?.receivedAt || lead?.acceptedAt || lead?.createdAt || null;

const formatLeadAttributionDate = (value?: string | null) =>
  value ? formatStandardDate(value) : "Unknown";

const formatLeadCost = (value?: number | null) =>
  typeof value === "number" && Number.isFinite(value) ? `$${value.toLocaleString()}` : "—";
const formatCurrency = (value: number) =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const createEditableMaterialLine = (material?: Partial<MaterialLineItem>): EditableMaterialLine => ({
  editorId: crypto.randomUUID(),
  sku: String(material?.sku ?? "").trim(),
  itemName: String(material?.itemName ?? "").trim(),
  quantityRequired: Math.max(0, Number(material?.quantityRequired ?? 1) || 0),
  quantityAllocated: Math.max(0, Number(material?.quantityAllocated ?? 0) || 0),
  quantityStaged: Math.max(0, Number(material?.quantityStaged ?? 0) || 0),
  quantityInstalled: Math.max(0, Number(material?.quantityInstalled ?? 0) || 0),
  missingItems: Math.max(0, Number(material?.missingItems ?? 0) || 0),
  fieldPurchase: Boolean(material?.fieldPurchase),
  inventoryStatus: material?.inventoryStatus ?? "In Stock",
  inventoryAssignment: material?.inventoryAssignment ?? "Assigned",
  lifecycleStage: material?.lifecycleStage ?? "Ordered",
  unitCost: typeof material?.unitCost === "number" ? material.unitCost : undefined,
  taxPercent: typeof material?.taxPercent === "number" ? material.taxPercent : undefined,
  unitPrice: typeof material?.unitPrice === "number" ? material.unitPrice : undefined,
  externalRef: material?.externalRef,
  externalItemId: material?.externalItemId,
});

const createEditableFieldMaterialLine = (material?: Partial<FieldMaterialItem>): EditableFieldMaterialLine => ({
  editorId: crypto.randomUUID(),
  id: String(material?.id ?? crypto.randomUUID()),
  itemName: String(material?.itemName ?? "").trim(),
  sku: String(material?.sku ?? "").trim() || undefined,
  quantity: Math.max(1, Number(material?.quantity ?? 1) || 1),
  unitCost: typeof material?.unitCost === "number" ? material.unitCost : undefined,
  vendorName: String(material?.vendorName ?? "").trim() || undefined,
  purchasedAt: String(material?.purchasedAt ?? "").trim() || undefined,
  notes: String(material?.notes ?? "").trim() || undefined,
  receiptClientIds: [],
  fieldPurchaseLogId: material?.fieldPurchaseLogId,
  inventoryPostedAt: material?.inventoryPostedAt,
  inventoryPostedBy: material?.inventoryPostedBy,
});

const createEditableFieldMaterialReceipt = (
  receipt?: Partial<NonNullable<FieldMaterialItem["receipts"]>[number]>,
): EditableFieldMaterialReceipt => ({
  clientId: crypto.randomUUID(),
  attachmentId: receipt?.attachmentId,
  fileName: String(receipt?.fileName ?? "Receipt"),
  mimeType: receipt?.mimeType,
  createdAt: receipt?.createdAt,
  invoiceNumber: String(receipt?.invoiceNumber ?? "").trim() || undefined,
  vendorName: String(receipt?.vendorName ?? "").trim() || undefined,
  purchasedAt: String(receipt?.purchasedAt ?? "").trim() || undefined,
  tax: typeof receipt?.tax === "number" ? receipt.tax : undefined,
  total: typeof receipt?.total === "number" ? receipt.total : undefined,
});

const formatReceiptEventAmountKey = (value?: number) =>
  typeof value === "number" && Number.isFinite(value) ? value.toFixed(2) : "";

const getMaterialTaxMultiplier = (taxPercent?: number) => {
  const normalized = typeof taxPercent === "number" && Number.isFinite(taxPercent)
    ? Math.max(0, taxPercent)
    : 0;
  return 1 + (normalized / 100);
};

const getCoreMaterialLineTotal = (material: Pick<MaterialLineItem, "quantityRequired" | "unitCost" | "taxPercent">) => {
  const qty = Math.max(0, Number(material.quantityRequired) || 0);
  const unit = typeof material.unitCost === "number" ? Math.max(0, material.unitCost) : 0;
  return qty * unit * getMaterialTaxMultiplier(material.taxPercent);
};

const getFieldMaterialLineSubtotal = (material: Pick<FieldMaterialItem, "quantity" | "unitCost">) => {
  const qty = Math.max(1, Number(material.quantity) || 1);
  const unit = typeof material.unitCost === "number" ? Math.max(0, material.unitCost) : 0;
  return qty * unit;
};

const extractReceiptReferenceToken = (value?: string | null): string | null => {
  const text = String(value ?? "").trim();
  if (!text) return null;

  const normalized = text
    .replace(/\.[a-z0-9]{2,5}$/i, "")
    .replace(/order details/gi, " ")
    .replace(/receipt/gi, " ")
    .replace(/order\s*#/gi, " ")
    .replace(/invoice\s*#/gi, " ")
    .replace(/[_\s]+/g, " ")
    .trim();

  const patterns = [
    /\b[A-Z]\d{4,}-\d{4,}\b/i,
    /\b[A-Z0-9]{2,}-[A-Z0-9-]{3,}\b/i,
    /\b\d{5,}-\d{4,}\b/,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match?.[0]) return match[0].toLowerCase();
  }

  return null;
};

const getEditableFieldMaterialReceiptEventIdentity = (
  receipt: Pick<EditableFieldMaterialReceipt, "invoiceNumber" | "vendorName" | "purchasedAt" | "total" | "tax" | "fileName">,
): string | null => {
  const invoiceNumber = String(receipt.invoiceNumber ?? "").trim().toLowerCase();
  const vendorName = String(receipt.vendorName ?? "").trim().toLowerCase();
  const purchasedAt = String(receipt.purchasedAt ?? "").trim();
  const total = formatReceiptEventAmountKey(receipt.total);
  const tax = formatReceiptEventAmountKey(receipt.tax);
  const referenceToken =
    extractReceiptReferenceToken(receipt.invoiceNumber) ||
    extractReceiptReferenceToken(receipt.fileName);

  if (referenceToken && total) {
    return [
      "event-ref",
      referenceToken,
      vendorName || "no-vendor",
      purchasedAt || "no-date",
      total,
      tax || "no-tax",
    ].join(":");
  }

  if (!vendorName || !purchasedAt || !total) return null;
  return ["event", vendorName, purchasedAt, invoiceNumber || "no-invoice", total, tax || "no-tax"].join(":");
};

const getEditableFieldMaterialReceiptIdentity = (receipt: EditableFieldMaterialReceipt): string => {
  const eventIdentity = getEditableFieldMaterialReceiptEventIdentity(receipt);
  if (eventIdentity) return eventIdentity;

  const attachmentId = String(receipt.attachmentId ?? "").trim();
  if (attachmentId) return `attachment:${attachmentId}`;

  if (receipt.file) {
    return [
      "file",
      receipt.file.name,
      receipt.file.size,
      receipt.file.lastModified,
      receipt.file.type,
    ].join(":");
  }

  return [
    "meta",
    String(receipt.fileName ?? "").trim().toLowerCase(),
    String(receipt.mimeType ?? "").trim().toLowerCase(),
    String(receipt.createdAt ?? "").trim(),
  ].join(":");
};

const getEditableFieldMaterialReceiptPreferenceScore = (receipt: EditableFieldMaterialReceipt) => {
  let score = 0;
  if (receipt.attachmentId) score += 8;
  if (receipt.file) score += 4;
  if (receipt.invoiceNumber) score += 4;
  if (receipt.vendorName) score += 3;
  if (receipt.purchasedAt) score += 3;
  if (typeof receipt.total === "number") score += 2;
  if (typeof receipt.tax === "number") score += 1;
  return score;
};

const mergeEditableFieldMaterialReceipt = (
  current: EditableFieldMaterialReceipt,
  incoming: EditableFieldMaterialReceipt,
): EditableFieldMaterialReceipt => {
  const preferred =
    getEditableFieldMaterialReceiptPreferenceScore(incoming) > getEditableFieldMaterialReceiptPreferenceScore(current)
      ? incoming
      : current;
  const fallback = preferred === incoming ? current : incoming;

  return {
    ...preferred,
    attachmentId: preferred.attachmentId ?? fallback.attachmentId,
    fileName: preferred.fileName || fallback.fileName,
    mimeType: preferred.mimeType ?? fallback.mimeType,
    createdAt: preferred.createdAt ?? fallback.createdAt,
    invoiceNumber: preferred.invoiceNumber ?? fallback.invoiceNumber,
    vendorName: preferred.vendorName ?? fallback.vendorName,
    purchasedAt: preferred.purchasedAt ?? fallback.purchasedAt,
    tax: preferred.tax ?? fallback.tax,
    total: preferred.total ?? fallback.total,
    file: preferred.file ?? fallback.file,
  };
};

const isFieldMaterialReceiptPlaceholder = (material?: Partial<FieldMaterialItem>) => {
  const itemName = String(material?.itemName ?? "").trim().toLowerCase();
  const notes = String(material?.notes ?? "").trim().toLowerCase();
  return itemName === "receipt uploaded — categorize later".toLowerCase()
    || notes.includes("auto-created receipt placeholder");
};

const getFieldMaterialReceiptReferences = (material?: Partial<FieldMaterialItem>): FieldMaterialReceiptReference[] => {
  if (Array.isArray(material?.receipts) && material.receipts.length > 0) {
    return material.receipts;
  }

  const attachmentIds = Array.isArray(material?.receiptAttachmentIds) ? material.receiptAttachmentIds : [];
  const fileNames = Array.isArray(material?.receiptFileNames) ? material.receiptFileNames : [];
  if (attachmentIds.length === 0) return [];

  return attachmentIds
    .map((attachmentId, index) => {
      const normalizedAttachmentId = String(attachmentId ?? "").trim();
      if (!normalizedAttachmentId) return null;
      const fileName = String(fileNames[index] ?? fileNames[0] ?? "Receipt").trim() || "Receipt";
      return {
        attachmentId: normalizedAttachmentId,
        fileName,
        vendorName: String(material?.vendorName ?? "").trim() || undefined,
        purchasedAt: String(material?.purchasedAt ?? "").trim() || undefined,
      } satisfies FieldMaterialReceiptReference;
    })
    .filter((receipt): receipt is FieldMaterialReceiptReference => Boolean(receipt));
};

const mapRawJobMaterialToLine = (row: Record<string, unknown>): MaterialLineItem | null => {
  const sku = String(row.sku ?? "").trim();
  const itemName = String(row.item_name ?? sku).trim() || sku;
  if (!sku && !itemName) return null;
  return {
    sku,
    itemName,
    quantityRequired: Math.max(0, Number(row.quantity_required ?? 0)),
    quantityAllocated: Math.max(0, Number(row.quantity_allocated ?? 0)),
    quantityStaged: Math.max(0, Number(row.quantity_staged ?? 0)),
    quantityInstalled: Math.max(0, Number(row.quantity_installed ?? 0)),
    missingItems: Math.max(0, Number(row.missing_items ?? 0)),
    fieldPurchase: Boolean(row.field_purchase),
    inventoryStatus: String(row.inventory_status ?? "In Stock") as MaterialLineItem["inventoryStatus"],
    inventoryAssignment: (row.inventory_assignment === "Assigned" ? "Assigned" : "Stock") as MaterialLineItem["inventoryAssignment"],
    lifecycleStage: String(row.lifecycle_stage ?? "Ordered") as MaterialLineItem["lifecycleStage"],
    unitCost: Number.isFinite(Number(row.unit_cost_snapshot ?? row.prices_snapshot))
      ? Number(row.unit_cost_snapshot ?? row.prices_snapshot)
      : undefined,
    taxPercent: Number.isFinite(Number(row.tax_percent))
      ? Math.max(0, Number(row.tax_percent))
      : undefined,
    unitPrice: Number.isFinite(Number(row.unit_price_snapshot ?? row.prices_snapshot))
      ? Number(row.unit_price_snapshot ?? row.prices_snapshot)
      : undefined,
  };
};

const normalizeMaterialSku = (value?: string | null) => String(value ?? "").trim().toUpperCase();
const formatOptionalCurrency = (value?: number) =>
  typeof value === "number" && Number.isFinite(value) ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

type ImportScopeGenerationResponse = {
  ok?: boolean;
  model?: string;
  usage?: unknown;
  generation?: MigrationAiScopeDraft;
  error?: string;
  detail?: string;
};

type ReceiptParseLineDraft = {
  itemName: string;
  sku: string;
  upc: string;
  quantity: number;
  unitCost: number;
  lineTotal: number;
  confidence: number;
  notes: string;
};

type ReceiptParsePayload = {
  vendorName: string;
  invoiceNumber: string;
  purchasedAt: string;
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  paymentMethod: string;
  warnings: string[];
  items: ReceiptParseLineDraft[];
};

type ReceiptParseResponse = {
  ok?: boolean;
  model?: string;
  usage?: unknown;
  receipt?: ReceiptParsePayload;
  error?: string;
  detail?: string;
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const marker = ";base64,";
      const markerIndex = result.indexOf(marker);
      resolve(markerIndex >= 0 ? result.slice(markerIndex + marker.length) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read file."));
    reader.readAsDataURL(file);
  });
}

function isPdfContractDocument(contract: Job["contractDocument"] | undefined): boolean {
  if (!contract?.filePath) return false;
  const mime = String(contract.mimeType ?? "").toLowerCase();
  const fileName = String(contract.fileName ?? contract.filePath ?? "").toLowerCase();
  return mime === "application/pdf" || fileName.endsWith(".pdf");
}

function normalizeAiScopeDraft(
  generation: MigrationAiScopeDraft,
  model?: string,
  contractFileName?: string,
): MigrationAiScopeDraft {
  return {
    ...generation,
    scopeSummary: generation.scopeSummary ?? "",
    projectType: generation.projectType ?? "",
    workArea: generation.workArea ?? "",
    installerScopeItems: generation.installerScopeItems ?? [],
    materialsAndSelections: generation.materialsAndSelections ?? [],
    colorSelections: generation.colorSelections ?? [],
    panelBreakdown: generation.panelBreakdown ?? [],
    fieldNotes: generation.fieldNotes ?? [],
    exclusionsOrUnknowns: generation.exclusionsOrUnknowns ?? [],
    extractedMaterials: generation.extractedMaterials ?? [],
    warnings: generation.warnings ?? [],
    missingFields: generation.missingFields ?? [],
    confidence: Number.isFinite(Number(generation.confidence)) ? Number(generation.confidence) : 0,
    model: model ?? generation.model,
    contractFileName: contractFileName ?? generation.contractFileName,
    generatedAt: generation.generatedAt ?? new Date().toISOString(),
  };
}

function buildJobEditContractDocument(
  job: Job,
  attachment: Attachment,
  file: File,
): NonNullable<Job["contractDocument"]> {
  const existing = job.contractDocument;
  const now = new Date().toISOString();
  const customerAddress =
    existing?.customerAddress ??
    [job.address?.street, job.address?.city, job.address?.state, job.address?.zip].filter(Boolean).join(", ");

  return {
    id: existing?.id ?? `job-contract-${job.id}`,
    quoteId: existing?.quoteId ?? job.convertedFromQuoteId ?? job.jobNumber ?? job.id,
    customerName: existing?.customerName ?? job.customerName ?? "Unknown Customer",
    customerEmail: existing?.customerEmail ?? job.email ?? "",
    customerAddress,
    contractTotal: existing?.contractTotal ?? job.financials?.contractValue ?? 0,
    homeownerSignature: existing?.homeownerSignature,
    homeownerSignedAt: existing?.homeownerSignedAt,
    repSignature: existing?.repSignature,
    repSignedAt: existing?.repSignedAt,
    termsAccepted: existing?.termsAccepted ?? Boolean(existing?.homeownerSignedAt),
    rescissionAcknowledged: existing?.rescissionAcknowledged ?? Boolean(existing?.homeownerSignedAt),
    status: existing?.status ?? (existing?.homeownerSignedAt ? "Signed" : "Pending"),
    createdAt: existing?.createdAt ?? now,
    approvedAt: existing?.approvedAt,
    sentToCustomerAt: existing?.sentToCustomerAt,
    dispositionNotes: existing?.dispositionNotes ?? "Contract uploaded from Edit Job for AI scope generation.",
    ownershipType: existing?.ownershipType,
    poaDocumentUploaded: existing?.poaDocumentUploaded,
    hoaInvolved: existing?.hoaInvolved,
    cancellation: existing?.cancellation,
    selectedOptionsApproved: existing?.selectedOptionsApproved,
    configSnapshotHash: existing?.configSnapshotHash,
    configSnapshotAt: existing?.configSnapshotAt,
    paymentMethod: existing?.paymentMethod,
    depositAmount: existing?.depositAmount,
    filePath: attachment.filePath,
    fileName: attachment.fileName || file.name,
    mimeType: attachment.mimeType || file.type || "application/pdf",
    fileSize: attachment.fileSize || file.size,
    uploadedAt: now,
    attachmentId: attachment.id,
  };
}

type EditableMaterialLine = MaterialLineItem & { editorId: string };
type EditableFieldMaterialReceipt = {
  clientId: string;
  attachmentId?: string;
  fileName: string;
  mimeType?: string;
  createdAt?: string;
  invoiceNumber?: string;
  vendorName?: string;
  purchasedAt?: string;
  tax?: number;
  total?: number;
  file?: File;
};
type EditableFieldMaterialLine = Omit<FieldMaterialItem, "receipts"> & {
  editorId: string;
  receiptClientIds: string[];
};

function normalizeEditableFieldMaterialLineValues(row: EditableFieldMaterialLine): EditableFieldMaterialLine {
  return {
    ...row,
    itemName: row.itemName.trim(),
    sku: String(row.sku ?? "").trim() || undefined,
    vendorName: String(row.vendorName ?? "").trim() || undefined,
    purchasedAt: String(row.purchasedAt ?? "").trim() || undefined,
    notes: String(row.notes ?? "").trim() || undefined,
    quantity: Math.max(1, Number(row.quantity) || 1),
    unitCost: typeof row.unitCost === "number" ? Math.max(0, row.unitCost) : undefined,
  };
}

function isFieldMaterialInventoryPosted(material: Partial<FieldMaterialItem> | undefined): boolean {
  return Boolean(material?.inventoryPostedAt);
}

function parseStoredAttachmentPath(value: string): { bucket: string; filePath: string } | null {
  if (!value) return null;
  if (value.includes("::")) {
    const [bucket, filePath] = value.split("::", 2);
    if (bucket && filePath) return { bucket, filePath };
  }
  return null;
}

function formatDispatchTimeSlot(
  timeSlot?: string,
  scheduledStartTime?: string,
  scheduledEndTime?: string,
): string {
  const explicit = String(timeSlot ?? "").trim();
  if (explicit) return explicit;
  if (!scheduledStartTime) return "Not set";

  const start = new Date(scheduledStartTime);
  if (Number.isNaN(start.getTime())) return "Not set";
  const startLabel = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (scheduledEndTime) {
    const end = new Date(scheduledEndTime);
    if (!Number.isNaN(end.getTime())) {
      const endLabel = end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      return `${startLabel} - ${endLabel}`;
    }
  }

  return startLabel;
}

function formatDispatchDateRange(
  scheduledInstallDate?: string,
  scheduledStartTime?: string,
  scheduledEndTime?: string,
  targetInstallDate?: string,
): string {
  const startSource = scheduledStartTime || scheduledInstallDate;
  if (!startSource) return "TBD";

  const start = new Date(startSource);
  if (Number.isNaN(start.getTime())) return formatStandardDate(scheduledInstallDate ?? startSource);

  // Multi-day range: targetInstallDate is the end date (different calendar day from start)
  const endDateSource = targetInstallDate || scheduledEndTime;
  if (endDateSource) {
    const end = new Date(endDateSource);
    if (!Number.isNaN(end.getTime())) {
      const startKey = (scheduledInstallDate ?? startSource).slice(0, 10);
      const endKey = endDateSource.slice(0, 10);
      if (startKey !== endKey && endKey > startKey) {
        // Count calendar days inclusive
        const msPerDay = 86400000;
        const days = Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
        const startLabel = start.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
        const endLabel = end.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
        return `${startLabel} – ${endLabel} (${days}d)`;
      }
    }
  }

  return formatStandardDate(scheduledInstallDate ?? startSource);
}

function isRenderablePhoto(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:") || v.startsWith("blob:") || v.startsWith("/")) return true;
  return /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(v);
}

function collectJobPhotoUrls(job: { photos?: string[] }, issues: { photo: string }[]): string[] {
  const merged = [
    ...(Array.isArray(job.photos) ? job.photos : []),
    ...issues.map((i) => i.photo),
  ].filter(isRenderablePhoto);

  const seen = new Set<string>();
  return merged.filter((url) => {
    if (seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function displayIdentity(value: unknown, fallback: string): string {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return fallback;
  return looksLikeUuid(text) ? fallback : text;
}

function buildPortalPhotoMeta(job: Job): Array<{ src: string; caption: string }> {
  return (Array.isArray(job.photos) ? job.photos : [])
    .filter(isRenderablePhoto)
    .map((src, index) => ({
      src,
      caption: `Job photo ${index + 1}`,
    }));
}

function buildPortalJobProjection(job: Job) {
  const photoMeta = buildPortalPhotoMeta(job);
  const briefSnapshot = getJobBriefing(job, undefined, photoMeta.map((p) => p.src));

  return {
    briefSnapshot,
    jobMetadata: {
      customerName: job.customerName,
      address: job.address,
      projectType: job.projectType,
      scheduledInstallDate: job.scheduledInstallDate,
      scheduledStartTime: job.scheduledStartTime,
      scheduledEndTime: job.scheduledEndTime,
      timeSlot: job.timeSlot,
      scopeSummary: job.scopeSummary,
      notes: job.notes,
      permitRequired: job.permitRequired,
      currentStage: job.currentStage,
      photos: photoMeta,
      materials: (job.materialsList ?? []).map((item) => item.itemName).filter(Boolean),
      site_constraints: briefSnapshot.riskFlags.map((f) => f.label),
      materialsList: job.materialsList ?? null,
      financials: job.financials ?? null,
    },
  };
}

function deriveEffectiveLifecycleStage(job: Job): Job["currentStage"] {
  const candidates: Job["currentStage"][] = [job.currentStage];
  const add = (condition: boolean, stage: Job["currentStage"]) => {
    if (condition) candidates.push(stage);
  };

  const buildReviewBypassed = migrationBypasses(job, "build_review");
  const orderingBypassed = migrationBypasses(job, "ordering");
  const hasMaterialsOrdered =
    orderingBypassed ||
    job.procurementStatus !== "Not Ordered" ||
    job.orderStatus !== "Not Ordered" ||
    !["Not Ordered", "Field Purchase Required"].includes(job.materialReadiness);
  const materialsReady = ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(job.materialReadiness);
  const hasSchedule = Boolean(String(job.scheduledInstallDate ?? "").trim());
  const hasAssignment =
    job.assignmentType !== "Unassigned" ||
    Boolean(String(job.assignedCrew ?? "").trim()) ||
    Boolean(String(job.assignedSubcontractor ?? "").trim()) ||
    ["Accepted", "Assigned", "Closed"].includes(job.dispatchStatus) ||
    String(job.contractorAssignmentStatus ?? "").toLowerCase() === "accepted";
  const installStarted = ["En Route", "On Site", "In Progress", "Install Complete"].includes(job.installStatus ?? "");
  const qaStarted =
    job.qaSubmitted ||
    ["Submitted", "Passed", "Failed"].includes(job.qaStatus ?? "") ||
    job.installStatus === "Install Complete" ||
    job.closeoutStatus !== "Not Started";
  const completed =
    job.currentStage === "Completed" ||
    job.closeoutStatus === "Closed";

  add(Boolean(buildReviewBypassed || job.intakeApproved || job.approvedForOrdering), "Ready for Ordering");
  add(hasMaterialsOrdered, "Ordered");
  add(materialsReady, "Ready for Scheduling");
  add(hasSchedule, "Scheduled Confirmed");
  add(hasAssignment && hasSchedule, "Assigned and Dispatched");
  add(installStarted, "In Progress");
  add(qaStarted, "QA Review");
  add(completed, "Completed");

  return candidates.reduce((best, next) => {
    const bestOrder = getStageConfig(best)?.order ?? 0;
    const nextOrder = getStageConfig(next)?.order ?? 0;
    return nextOrder > bestOrder ? next : best;
  }, job.currentStage);
}

async function refreshInstallerOpportunityProjection(job: Job): Promise<number> {
  const organizationId = await getCurrentOrganizationId();
  const { briefSnapshot, jobMetadata } = buildPortalJobProjection(job);
  const { data, error } = await supabase.functions.invoke<{
    ok?: boolean;
    refreshed?: number;
    skipped?: boolean;
    error?: string;
  }>("opportunity-publish", {
    body: {
      organizationId,
      appJobId: job.id,
      appJobNumber: job.jobNumber ?? undefined,
      refreshOnly: true,
      projectType: (job.projectType ?? "other").toLowerCase(),
      city: job.address?.city,
      zip: job.address?.zip,
      scheduledDate: job.scheduledInstallDate ?? undefined,
      scheduledWindow: job.timeSlot ?? undefined,
      scopeSummary: job.scopeSummary ?? job.notes ?? undefined,
      briefSnapshot,
      jobMetadata,
    },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data?.refreshed ?? 0;
}

const ACTION_ICONS: Record<string, React.ElementType> = {
  crew: Users, materials: Package, permit: Shield, schedule: Calendar,
  issue: AlertTriangle, dispatch: Truck, qa: ClipboardCheck, closeout: FileCheck,
  review: ClipboardCheck,
};

// ─────────────────────────────────────────────────────────────────────────────
// JobAuditTimeline — real-data activity log from job_audit_log (Supabase)
// ─────────────────────────────────────────────────────────────────────────────

// Fields that are internal JSONB blobs — too noisy to display as diffs
const AUDIT_SKIP_FIELDS = new Set(["metadata", "financials"]);

const AUDIT_FIELD_LABELS: Record<string, string> = {
  title: "Job Title",
  status: "Status",
  current_stage: "Stage",
  priority: "Priority",
  scheduled_start_at: "Scheduled Start",
  scheduled_end_at: "Scheduled End",
  contractor_id: "Contractor",
  crew_id: "Crew",
  customer_id: "Customer",
  notes: "Notes",
  material_readiness: "Material Readiness",
  permit_required: "Permit Required",
  address_line1: "Address",
  city: "City",
  state: "State",
  postal_code: "Postal Code",
  materials_list: "Materials",
};

const AUDIT_DATE_FIELDS = new Set(["scheduled_start_at", "scheduled_end_at"]);
const AUDIT_BOOL_FIELDS = new Set(["permit_required"]);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function formatAuditFieldValue(field: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (AUDIT_BOOL_FIELDS.has(field)) return value ? "Yes" : "No";
  if (AUDIT_DATE_FIELDS.has(field)) {
    const d = new Date(value as string);
    return isNaN(d.getTime())
      ? String(value)
      : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
  if (typeof value === "string" && UUID_RE.test(value)) return `…${value.slice(-8)}`;
  if (typeof value === "string" && value.length > 60) return `${value.slice(0, 60)}…`;
  if (typeof value === "object") return "[ updated ]";
  return String(value);
}

function formatAuditTimestamp(iso: string): string {
  const d = new Date(iso);
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const year = d.getFullYear();
  const h = d.getHours() % 12 || 12;
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = d.getHours() >= 12 ? "PM" : "AM";
  return `${month} ${day}, ${year} · ${h}:${m} ${ampm}`;
}

// Pipeline stage display config
const PIPELINE_STAGE_CONFIG: Record<string, { label: string; icon: React.ElementType; iconColor: string; high?: boolean }> = {
  "Lead":                  { label: "Lead Created",            icon: UserCheck,  iconColor: "text-sky-500" },
  "Quote Created":         { label: "Quote Created",           icon: FileText,   iconColor: "text-violet-500" },
  "Appointment Scheduled": { label: "Appointment Scheduled",   icon: Calendar,   iconColor: "text-amber-500", high: true },
  "Appointment Completed": { label: "Appointment Completed",   icon: PhoneCall,  iconColor: "text-blue-500",  high: true },
  "Follow-Up Needed":      { label: "Follow-Up Needed",        icon: Clock,      iconColor: "text-orange-500" },
  "Closed Won":            { label: "Closed Won",              icon: CheckCircle, iconColor: "text-emerald-500", high: true },
  "Closed Lost":           { label: "Closed Lost",             icon: XCircle,   iconColor: "text-rose-500",  high: true },
};

const APPT_RESULT_LABELS: Record<string, string> = {
  booked:           "Booked",
  not_interested:   "Not Interested",
  needs_more_time:  "Needs More Time",
  wrong_number:     "Wrong Number",
  voicemail:        "Voicemail",
  deferred:         "Deferred",
};

const BUILD_REVIEW_ACTION_LABELS: Record<string, { label: string; high?: boolean }> = {
  submitted_for_review:  { label: "Submitted for Review" },
  approved:              { label: "Build Review Approved",   high: true },
  final_approved:        { label: "Build Review Final Approved", high: true },
  revision_requested:    { label: "Revision Requested",     high: true },
  sent_to_sales:         { label: "Sent to Sales" },
  sales_confirmed:       { label: "Sales Confirmed" },
  sales_resubmitted:     { label: "Resubmitted for Review" },
  change_order_created:  { label: "Change Order Created" },
  customer_signed:       { label: "Customer Signed" },
};

function JobAuditTimeline({
  jobId,
  job: jobData,
  purchaseOrders,
  jobAllocations,
  fieldMaterials,
  linkedLead,
  appointmentResults,
  consultations,
  buildReviewEvents,
  permitDetail,
  schedulingEvents,
  installerOffers,
  phaseExecutions,
  jobCredits,
  jobIssues,
  serviceTickets,
  refreshToken,
}: {
  jobId: string;
  job?: import("@/data/types").Job;
  refreshToken?: number;
  purchaseOrders: PurchaseOrder[];
  jobAllocations: JobAllocation[];
  fieldMaterials: FieldMaterialItem[];
  linkedLead: DbLead | null;
  appointmentResults: DbAppointmentResult[];
  consultations: ConsultationSessionData[];
  buildReviewEvents: BuildReviewEvent[];
  permitDetail?: import("@/data/types").PermitDetail;
  schedulingEvents?: Record<string, unknown>[];
  installerOffers?: MarketplaceOffer[];
  phaseExecutions?: { phaseName: string; status: string; completedAt?: string; completedBy?: string; qaReviewedAt?: string; qaReviewer?: string; qaFailReason?: string }[];
  jobCredits?: CustomerCredit[];
  jobIssues?: import("@/data/types").Issue[];
  serviceTickets?: ServiceTicket[];
}) {
  const [auditEntries, setAuditEntries] = useState<JobAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getJobAuditLog(jobId).then((result) => {
      if (cancelled) return;
      setAuditEntries(
        result.filter((e) => {
          if (e.action === "create") return true;
          const meaningful = Object.keys(e.changedFields ?? {}).filter(
            (f) => !AUDIT_SKIP_FIELDS.has(f),
          );
          return meaningful.length > 0;
        }),
      );
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [jobId, refreshToken]);

  type ActivityFilter = "all" | "changes" | "pipeline" | "build_review" | "permitting" | "scheduling" | "offers" | "closeout" | "financial" | "ordering" | "materials" | "field" | "execution" | "issues";
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");

  // Synthetic domain events from ordering, warehouse, field materials, and pipeline
  type DomainEvent = {
    id: string; label: string; detail: string; actor: string; timestamp: string;
    icon: React.ElementType; iconColor: string; category: Exclude<ActivityFilter, "all" | "changes">;
    high?: boolean;
  };

  const PHASE_LABEL: Record<string, string> = {
    prep: "Prep", demo: "Demo", rough: "Rough", install: "Install",
    finish: "Finish", qa: "QA", rework: "Rework", custom: "Custom",
  };

  const domainEvents = useMemo((): DomainEvent[] => {
    const evts: DomainEvent[] = [];

    // ── Pipeline events from lead stage_history ──────────────────────
    if (linkedLead) {
      // Lead created
      evts.push({
        id: `lead-created-${linkedLead.id}`,
        label: "Lead Created",
        detail: `${linkedLead.primary_name}${linkedLead.lead_source ? ` · ${linkedLead.lead_source}` : ""}`,
        actor: linkedLead.assigned_rep ? "Sales" : "System",
        timestamp: linkedLead.created_at,
        icon: UserCheck,
        iconColor: "text-sky-500",
        category: "pipeline",
      });

      // Stage transitions from stage_history
      const stageHistory = Array.isArray(linkedLead.stage_history) ? linkedLead.stage_history as Array<{
        id: string; fromStage: string | null; toStage: string; changedAt: string; changedBy: string;
      }> : [];

      for (const change of stageHistory) {
        if (!change.toStage || !change.changedAt) continue;
        const cfg = PIPELINE_STAGE_CONFIG[change.toStage];
        const Icon = cfg?.icon ?? Flag;
        const iconColor = cfg?.iconColor ?? "text-muted-foreground";
        evts.push({
          id: `stage-${change.id}`,
          label: cfg?.label ?? change.toStage,
          detail: change.fromStage ? `From: ${change.fromStage}` : "Initial stage",
          actor: change.changedBy || "System",
          timestamp: change.changedAt,
          icon: Icon,
          iconColor,
          category: "pipeline",
          high: cfg?.high,
        });
      }

      // Appointment results
      for (const appt of appointmentResults) {
        const statusLabel = appt.status.charAt(0).toUpperCase() + appt.status.slice(1).replace(/_/g, " ");
        const resultLabel = appt.result ? ` · ${APPT_RESULT_LABELS[appt.result] ?? appt.result}` : "";
        evts.push({
          id: `appt-${appt.id}`,
          label: `Appointment ${statusLabel}`,
          detail: `${appt.contact_name ?? linkedLead.primary_name}${resultLabel}${appt.notes ? ` · ${appt.notes.slice(0, 60)}` : ""}`,
          actor: (appt as DbAppointmentResult & { assigned_rep_name?: string }).assigned_rep_name ?? appt.created_by ?? "Sales",
          timestamp: appt.scheduled_at,
          icon: appt.status === "completed" ? PhoneCall : Calendar,
          iconColor: appt.status === "completed" ? "text-blue-500" : "text-amber-500",
          category: "pipeline",
          high: appt.status === "completed",
        });
      }
    }

    // ── Consultation events ──────────────────────────────────────────
    for (const cs of consultations) {
      evts.push({
        id: `cs-start-${cs.id}`,
        label: "Consultation Started",
        detail: `${cs.completed_steps?.length ?? 0} of 4 steps · ${cs.status}`,
        actor: cs.rep_id ? `Rep · …${cs.rep_id.slice(-6)}` : "Sales",
        timestamp: cs.started_at,
        icon: ClipboardCheck,
        iconColor: "text-violet-500",
        category: "pipeline",
      });
      if (cs.completed_at) {
        evts.push({
          id: `cs-done-${cs.id}`,
          label: "Consultation Completed",
          detail: `Customer Info · Scope · Pricing · Review`,
          actor: cs.rep_id ? `Rep · …${cs.rep_id.slice(-6)}` : "Sales",
          timestamp: cs.completed_at,
          icon: CheckCircle,
          iconColor: "text-emerald-500",
          category: "pipeline",
          high: true,
        });
      }
    }

    // ── Build Review events ──────────────────────────────────────────
    for (const ev of buildReviewEvents) {
      const cfg = BUILD_REVIEW_ACTION_LABELS[ev.action] ?? { label: ev.action };
      const isApproval = ev.action === "approved" || ev.action === "final_approved";
      const isRevision = ev.action === "revision_requested";
      const detail = [
        ev.notes,
        ev.revisionNotes,
        ev.revisionReason ? REVISION_REASON_LABELS[ev.revisionReason as import("@/data/buildReviewTypes").RevisionReason] : null,
      ].filter(Boolean).join(" · ") || cfg.label;
      evts.push({
        id: `br-${ev.id}`,
        label: cfg.label,
        detail,
        actor: ev.performedBy || "Ops",
        timestamp: ev.timestamp,
        icon: isApproval ? CheckCircle : isRevision ? AlertTriangle : ClipboardCheck,
        iconColor: isApproval ? "text-emerald-500" : isRevision ? "text-orange-500" : "text-violet-500",
        category: "build_review",
        high: cfg.high,
      });
    }

    // ── Permitting events ────────────────────────────────────────────
    if (permitDetail) {
      if (permitDetail.submittedDate) {
        evts.push({
          id: "permit-submitted",
          label: "Permit Submitted",
          detail: `${permitDetail.permitType}${permitDetail.municipality ? ` · ${permitDetail.municipality}` : ""}${permitDetail.permitNumber ? ` · #${permitDetail.permitNumber}` : ""}`,
          actor: permitDetail.assignedCoordinator || "Permitting",
          timestamp: permitDetail.submittedDate,
          icon: FileCheck,
          iconColor: "text-violet-500",
          category: "permitting",
          high: true,
        });
      }
      const MUNI_TYPE_LABEL: Record<string, string> = {
        call: "Municipality Call", email: "Municipality Email",
        portal_message: "Municipality Portal Message", in_person: "Municipality Visit", voicemail: "Municipality Voicemail",
      };
      const MUNI_TYPE_ICON: Record<string, React.ElementType> = {
        call: PhoneCall, email: Mail, portal_message: FileText, in_person: Users, voicemail: PhoneCall,
      };
      for (const entry of permitDetail.municipalityLog ?? []) {
        evts.push({
          id: `permit-muni-${entry.id}`,
          label: MUNI_TYPE_LABEL[entry.type] ?? "Municipality Contact",
          detail: [entry.summary, entry.contactPerson].filter(Boolean).join(" · "),
          actor: entry.loggedBy || "Permitting",
          timestamp: entry.createdAt,
          icon: MUNI_TYPE_ICON[entry.type] ?? PhoneCall,
          iconColor: entry.type === "email" || entry.type === "portal_message" ? "text-violet-500" : "text-sky-500",
          category: "permitting",
        });
      }
      for (const insp of permitDetail.inspectionLifecycle?.inspections ?? []) {
        if (insp.scheduledDate) {
          evts.push({
            id: `permit-insp-sched-${insp.id}`,
            label: `Inspection Scheduled: ${insp.inspectionType}`,
            detail: insp.assignedOwner ? `Assigned to ${insp.assignedOwner}` : insp.inspectionType,
            actor: insp.assignedOwner || "Permitting",
            timestamp: insp.scheduledDate,
            icon: Calendar,
            iconColor: "text-amber-500",
            category: "permitting",
          });
        }
        if (insp.completedDate) {
          const passed = insp.result === "passed";
          evts.push({
            id: `permit-insp-done-${insp.id}`,
            label: passed ? `Inspection Passed: ${insp.inspectionType}` : `Inspection Failed: ${insp.inspectionType}`,
            detail: insp.failureReason || (passed ? "All items cleared" : insp.inspectionType),
            actor: insp.assignedOwner || "Permitting",
            timestamp: insp.completedDate,
            icon: passed ? CheckCircle : XCircle,
            iconColor: passed ? "text-emerald-500" : "text-rose-500",
            category: "permitting",
            high: true,
          });
        }
      }
    }

    // ── Scheduling / Dispatch events ─────────────────────────────────
    const SCHED_TYPE_LABEL: Record<string, string> = {
      install: "Installation Scheduled",
      inspection: "Inspection Scheduled",
      followup: "Follow-up Scheduled",
      delivery: "Delivery Scheduled",
      consultation: "Consultation Scheduled",
      sales_appointment: "Sales Appointment Scheduled",
      service_call: "Service Call Scheduled",
      pre_install: "Pre-Install Scheduled",
      schedule_set: "Schedule Set",
      schedule_removed: "Schedule Removed",
    };
    const SCHED_STATUS_LABEL: Record<string, string> = {
      draft: "Draft", scheduled: "Scheduled", confirmed: "Confirmed",
      in_progress: "In Progress", completed: "Completed", cancelled: "Cancelled",
    };
    for (const ev of schedulingEvents ?? []) {
      const eventType = String(ev.event_type ?? "");
      const status = String(ev.status ?? "scheduled");
      const startAt = String(ev.start_at ?? "");
      const createdAt = String(ev.created_at ?? "");
      if (!createdAt) continue;
      const dateStr = startAt
        ? new Date(startAt).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
        : "";
      const statusPart = status && status !== "scheduled" ? ` · ${SCHED_STATUS_LABEL[status] ?? status}` : "";
      const notesPart = ev.notes ? ` · ${String(ev.notes).slice(0, 60)}` : "";
      const isInstall = eventType === "install";
      const isCancelled = status === "cancelled";
      const isCompleted = status === "completed";
      evts.push({
        id: `sched-${ev.id}`,
        label: SCHED_TYPE_LABEL[eventType] ?? "Event Scheduled",
        detail: [dateStr, statusPart.replace(" · ", ""), notesPart.replace(" · ", "")].filter(Boolean).join(" · "),
        actor: String(ev.created_by ?? "Scheduling"),
        timestamp: createdAt,
        icon: isCompleted ? CheckCircle : isCancelled ? XCircle : Calendar,
        iconColor: isCompleted ? "text-emerald-500" : isCancelled ? "text-rose-500" : "text-primary",
        category: "scheduling",
        high: isInstall || isCompleted,
      });
    }

    // ── Installer Offer events ───────────────────────────────────────
    const OFFER_EVENT_LABEL: Record<string, string> = {
      offer_created:    "Offer Sent",
      offer_accepted:   "Offer Accepted",
      offer_declined:   "Offer Declined",
      offer_expired:    "Offer Expired",
      offer_withdrawn:  "Offer Withdrawn",
      offer_superseded: "Offer Superseded",
      offer_extended: "Offer Extended",
      offer_window_changed: "Offer Window Changed",
    };
    const OFFER_EVENT_ICON: Record<string, { icon: React.ElementType; color: string; high?: boolean }> = {
      offer_created:    { icon: Zap,         color: "text-violet-500" },
      offer_accepted:   { icon: CheckCircle,  color: "text-emerald-500", high: true },
      offer_declined:   { icon: XCircle,      color: "text-rose-500",    high: true },
      offer_expired:    { icon: Clock,        color: "text-muted-foreground" },
      offer_withdrawn:  { icon: Undo2,        color: "text-orange-500" },
      offer_superseded: { icon: RefreshCw,    color: "text-muted-foreground" },
    };
    for (const offer of installerOffers ?? []) {
      const contractorRef = `…${offer.contractorId.slice(-6)}`;
      const payout = offer.payoutAmountSnapshot != null ? ` · $${offer.payoutAmountSnapshot.toLocaleString()}` : "";
      // Each audit entry becomes an event (these are the canonical append-only records)
      for (const entry of offer.audit) {
        if (!entry.at) continue;
        const cfg = OFFER_EVENT_ICON[entry.event] ?? { icon: Zap, color: "text-violet-500" };
        const reasonPart = entry.reason ? ` · ${entry.reason}` : "";
        evts.push({
          id: `offer-${offer.id}-${entry.event}`,
          label: OFFER_EVENT_LABEL[entry.event] ?? entry.event,
          detail: `${contractorRef}${entry.event === "offer_created" ? payout : ""}${reasonPart}`,
          actor: entry.actor || "Dispatch",
          timestamp: entry.at,
          icon: cfg.icon,
          iconColor: cfg.color,
          category: "offers",
          high: cfg.high,
        });
      }
      // Fallback: if audit is empty, use sent_at for the offer_created event
      if (offer.audit.length === 0 && offer.sentAt) {
        evts.push({
          id: `offer-sent-${offer.id}`,
          label: "Offer Sent",
          detail: `${contractorRef}${payout}`,
          actor: offer.sentBy || "Dispatch",
          timestamp: offer.sentAt,
          icon: Zap,
          iconColor: "text-violet-500",
          category: "offers",
        });
      }
    }

    // ── Closeout events ──────────────────────────────────────────────
    if (jobData) {
      const signoff = jobData.closeoutSignoff;
      if (signoff?.signedAt) {
        evts.push({
          id: "closeout-signoff",
          label: signoff.approved !== false ? "Customer Signed Off" : "Signoff Declined",
          detail: [signoff.signerName, signoff.notes].filter(Boolean).join(" · ") || "Customer signoff recorded",
          actor: signoff.signerName || "Customer",
          timestamp: signoff.signedAt,
          icon: signoff.approved !== false ? CheckCircle : XCircle,
          iconColor: signoff.approved !== false ? "text-emerald-500" : "text-rose-500",
          category: "closeout",
          high: true,
        });
      }
      if (jobData.reviewSentAt) {
        evts.push({
          id: "closeout-review-sent",
          label: "Review Request Sent",
          detail: jobData.reviewSatisfied === true
            ? "Customer satisfied"
            : jobData.reviewSatisfied === false
              ? "Customer dissatisfied"
              : "Awaiting response",
          actor: "Ops",
          timestamp: jobData.reviewSentAt,
          icon: jobData.reviewSatisfied === true ? CheckCircle : jobData.reviewSatisfied === false ? AlertTriangle : Clock,
          iconColor: jobData.reviewSatisfied === true ? "text-emerald-500" : jobData.reviewSatisfied === false ? "text-orange-500" : "text-muted-foreground",
          category: "closeout",
        });
      }
      if (jobData.referralRequestedAt) {
        evts.push({
          id: "closeout-referral",
          label: "Referral Requested",
          detail: jobData.referralReceived ? "Referral received" : "Awaiting referral",
          actor: "Ops",
          timestamp: jobData.referralRequestedAt,
          icon: jobData.referralReceived ? CheckCircle : Users,
          iconColor: jobData.referralReceived ? "text-emerald-500" : "text-sky-500",
          category: "closeout",
        });
      }
      if (jobData.contractorRatingAt) {
        evts.push({
          id: "closeout-contractor-rating",
          label: "Contractor Rating Requested",
          detail: jobData.contractorRatingCompleted ? "Rating completed" : "Awaiting rating",
          actor: "Ops",
          timestamp: jobData.contractorRatingAt,
          icon: jobData.contractorRatingCompleted ? CheckCircle : Shield,
          iconColor: jobData.contractorRatingCompleted ? "text-emerald-500" : "text-amber-500",
          category: "closeout",
        });
      }
    }

    // ── Phase execution events ───────────────────────────────────────
    for (const phase of phaseExecutions ?? []) {
      const phaseLabel = PHASE_LABEL[phase.phaseName] ?? phase.phaseName;
      if (phase.completedAt) {
        evts.push({
          id: `phase-submitted-${phase.phaseName}`,
          label: `Phase Submitted: ${phaseLabel}`,
          detail: `Installer marked ${phaseLabel} phase complete`,
          actor: phase.completedBy || "Installer",
          timestamp: phase.completedAt,
          icon: ClipboardCheck,
          iconColor: "text-violet-500",
          category: "execution",
        });
      }
      if (phase.qaReviewedAt) {
        const passed = phase.status === "qa_passed";
        const failed = phase.status === "qa_failed";
        if (passed || failed) {
          evts.push({
            id: `phase-qa-${phase.phaseName}`,
            label: passed ? `Phase QA Passed: ${phaseLabel}` : `Phase QA Failed: ${phaseLabel}`,
            detail: failed && phase.qaFailReason ? phase.qaFailReason : `${phaseLabel} phase QA ${passed ? "approved" : "rejected"}`,
            actor: phase.qaReviewer || "QA",
            timestamp: phase.qaReviewedAt,
            icon: passed ? CheckCircle : XCircle,
            iconColor: passed ? "text-emerald-500" : "text-rose-500",
            category: "execution",
            high: true,
          });
        }
      }
    }

    // ── Issue events ─────────────────────────────────────────────────
    const SEVERITY_COLOR: Record<string, string> = { Low: "text-muted-foreground", Medium: "text-amber-500", Urgent: "text-rose-500" };
    for (const issue of jobIssues ?? []) {
      if (issue.dateCreated) {
        evts.push({
          id: `issue-created-${issue.id}`,
          label: `Issue: ${issue.issueType}`,
          detail: `${issue.severity} · ${issue.description.slice(0, 60)}${issue.description.length > 60 ? "…" : ""}`,
          actor: issue.owner || "Ops",
          timestamp: issue.dateCreated,
          icon: AlertTriangle,
          iconColor: SEVERITY_COLOR[issue.severity] ?? "text-amber-500",
          category: "issues",
          high: issue.severity === "Urgent",
        });
      }
      if (issue.dateInProgress) {
        evts.push({
          id: `issue-progress-${issue.id}`,
          label: `Issue In Progress: ${issue.issueType}`,
          detail: issue.description.slice(0, 60),
          actor: issue.owner || "Ops",
          timestamp: issue.dateInProgress,
          icon: Clock,
          iconColor: "text-amber-500",
          category: "issues",
        });
      }
      if (issue.dateResolved || issue.dateClosed) {
        evts.push({
          id: `issue-resolved-${issue.id}`,
          label: `Issue Resolved: ${issue.issueType}`,
          detail: issue.resolutionNotes?.slice(0, 60) ?? "Issue closed",
          actor: issue.owner || "Ops",
          timestamp: (issue.dateResolved ?? issue.dateClosed)!,
          icon: CheckCircle,
          iconColor: "text-emerald-500",
          category: "issues",
          high: true,
        });
      }
    }
    for (const svc of serviceTickets ?? []) {
      evts.push({
        id: `svc-created-${svc.id}`,
        label: `Service Ticket: ${svc.category}`,
        detail: `${svc.subject} · ${svc.priority}`,
        actor: svc.createdBy || "Ops",
        timestamp: svc.createdAt,
        icon: Wrench,
        iconColor: svc.priority === "Urgent" ? "text-rose-500" : svc.priority === "High" ? "text-amber-500" : "text-muted-foreground",
        category: "issues",
        high: svc.priority === "Urgent",
      });
      if (svc.resolvedAt) {
        evts.push({
          id: `svc-resolved-${svc.id}`,
          label: `Service Ticket Resolved: ${svc.category}`,
          detail: svc.resolution?.slice(0, 60) ?? svc.subject,
          actor: svc.resolvedBy || "Ops",
          timestamp: svc.resolvedAt,
          icon: CheckCircle,
          iconColor: "text-emerald-500",
          category: "issues",
          high: true,
        });
      }
    }

    // ── Financial events ─────────────────────────────────────────────
    const INVOICE_TYPE_LABEL: Record<string, string> = { deposit: "Deposit Invoice", progress: "Progress Invoice", final: "Final Invoice" };
    const CREDIT_TYPE_LABEL: Record<string, string> = { service_credit: "Service Credit", discount: "Discount Credit", rework_credit: "Rework Credit", goodwill: "Goodwill Credit", refund: "Refund" };
    for (const inv of jobData?.financials?.invoices ?? []) {
      if (inv.issuedAt) {
        evts.push({
          id: `inv-issued-${inv.id}`,
          label: INVOICE_TYPE_LABEL[inv.type] ?? "Invoice",
          detail: `$${inv.amount.toLocaleString()} · ${inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}`,
          actor: "Billing",
          timestamp: inv.issuedAt,
          icon: FileText,
          iconColor: "text-violet-500",
          category: "financial",
        });
      }
      if (inv.paidAt) {
        evts.push({
          id: `inv-paid-${inv.id}`,
          label: `${INVOICE_TYPE_LABEL[inv.type] ?? "Invoice"} Paid`,
          detail: `$${inv.amount.toLocaleString()}${inv.paymentMethod ? ` · ${inv.paymentMethod}` : ""}`,
          actor: "Customer",
          timestamp: inv.paidAt,
          icon: CheckCircle,
          iconColor: "text-emerald-500",
          category: "financial",
          high: true,
        });
      }
    }
    for (const pay of jobData?.financials?.payments ?? []) {
      if (!pay.timestamp) continue;
      evts.push({
        id: `pay-${pay.id}`,
        label: "Payment Received",
        detail: `$${pay.amount.toLocaleString()} · ${pay.method}`,
        actor: "Customer",
        timestamp: pay.timestamp,
        icon: DollarSign,
        iconColor: "text-emerald-500",
        category: "financial",
        high: true,
      });
    }
    for (const co of jobData?.changeOrders ?? []) {
      if (co.createdAt) {
        evts.push({
          id: `co-created-${co.id}`,
          label: `Change Order CO-${co.orderNumber} Created`,
          detail: `${co.summary} · ${co.changeAmount >= 0 ? "+" : ""}$${co.changeAmount.toLocaleString()}`,
          actor: co.createdBy || "Ops",
          timestamp: co.createdAt,
          icon: PencilLine,
          iconColor: "text-amber-500",
          category: "financial",
        });
      }
      if (co.signedAt) {
        evts.push({
          id: `co-signed-${co.id}`,
          label: `Change Order CO-${co.orderNumber} Signed`,
          detail: `Revised total $${co.revisedContractValue.toLocaleString()}`,
          actor: co.homeownerSignature ? "Customer" : "Rep",
          timestamp: co.signedAt,
          icon: FileCheck,
          iconColor: "text-emerald-500",
          category: "financial",
          high: true,
        });
      }
    }
    for (const cr of jobCredits ?? []) {
      evts.push({
        id: `cr-${cr.id}`,
        label: CREDIT_TYPE_LABEL[cr.creditType] ?? "Credit Issued",
        detail: `$${cr.creditAmount.toLocaleString()} · ${cr.reasonCategory}${cr.status === "approved" ? "" : ` · ${cr.status}`}`,
        actor: cr.createdBy || "Ops",
        timestamp: cr.dateCreated,
        icon: CreditCard,
        iconColor: cr.status === "approved" ? "text-emerald-500" : "text-amber-500",
        category: "financial",
      });
    }

    // ── Ordering events ──────────────────────────────────────────────
    for (const po of purchaseOrders) {
      const total = po.totalCost > 0 ? ` · $${po.totalCost.toLocaleString()}` : "";
      if (po.submittedAt) evts.push({ id: `po-sub-${po.poId}`, label: "PO Submitted", detail: `${po.poNumber} · ${po.vendorName}${total}`, actor: "Procurement", timestamp: po.submittedAt, icon: Truck, iconColor: "text-primary", category: "ordering" });
      if (po.confirmedAt) evts.push({ id: `po-con-${po.poId}`, label: "PO Confirmed", detail: `${po.poNumber} · ${po.vendorName}${po.confirmedEta ? ` · ETA ${new Date(po.confirmedEta).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}`, actor: po.vendorName, timestamp: po.confirmedAt, icon: CheckCircle, iconColor: "text-primary", category: "ordering" });
      if (po.deliveredAt) evts.push({ id: `po-del-${po.poId}`, label: "Materials Delivered", detail: `${po.poNumber} · ${po.vendorName}`, actor: po.vendorName, timestamp: po.deliveredAt, icon: Package, iconColor: "text-emerald-500", category: "ordering", high: true });
    }

    // ── Warehouse / materials ────────────────────────────────────────
    for (const a of jobAllocations) {
      if (!a.allocatedAt) continue;
      if (a.status === "Ready for Install") evts.push({ id: `al-rdy-${a.id}`, label: "Material Ready for Install", detail: `${a.productName || a.sku} · ${a.quantity} unit${a.quantity !== 1 ? "s" : ""}`, actor: a.allocatedBy || "Warehouse", timestamp: a.allocatedAt, icon: Shield, iconColor: "text-emerald-500", category: "materials", high: true });
      else if (a.status === "Reserved") evts.push({ id: `al-res-${a.id}`, label: "Material Allocated", detail: `${a.productName || a.sku} · ${a.quantity} unit${a.quantity !== 1 ? "s" : ""} reserved`, actor: a.allocatedBy || "Warehouse", timestamp: a.allocatedAt, icon: Package, iconColor: "text-primary", category: "materials" });
    }

    // ── Field materials ──────────────────────────────────────────────
    for (const fm of fieldMaterials) {
      if (!fm.inventoryPostedAt) continue;
      const qty = `${fm.quantity} unit${fm.quantity !== 1 ? "s" : ""}`;
      const skuPart = fm.sku ? ` · SKU ${fm.sku}` : "";
      evts.push({ id: `fm-${fm.id}`, label: "Field Material Posted", detail: `${fm.itemName}${skuPart} · ${qty}`, actor: fm.inventoryPostedBy || "Field", timestamp: fm.inventoryPostedAt, icon: ShoppingBag, iconColor: "text-amber-500", category: "field" });
    }

    return evts;
  }, [linkedLead, appointmentResults, consultations, buildReviewEvents, permitDetail, schedulingEvents, installerOffers, phaseExecutions, jobCredits, jobIssues, serviceTickets, jobData, purchaseOrders, jobAllocations, fieldMaterials]);

  // Merge audit + domain entries, newest first
  type MergedItem =
    | { kind: "audit"; entry: JobAuditEntry; timestamp: string }
    | { kind: "domain"; event: DomainEvent; timestamp: string };

  const mergedItems = useMemo((): MergedItem[] => {
    const items: MergedItem[] = [
      ...auditEntries.map((e) => ({ kind: "audit" as const, entry: e, timestamp: e.createdAt })),
      ...domainEvents.map((e) => ({ kind: "domain" as const, event: e, timestamp: e.timestamp })),
    ];
    return items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [auditEntries, domainEvents]);

  // Audit fields that belong to the financial domain
  const FINANCIAL_AUDIT_FIELDS = new Set([
    "contract_value", "contract_total", "deposit_amount", "deposit_collected",
    "deposit_method", "deposit_date", "final_payment_collected", "final_payment_method",
    "final_payment_date", "financing", "payment_plan", "change_orders",
    "total_cost", "total_price", "payout_amount",
  ]);

  // Audit fields that belong to the closeout domain
  const CLOSEOUT_AUDIT_FIELDS = new Set([
    "closeout_status", "qa_status", "qa_submitted", "final_documentation_complete",
    "customer_signoff", "closeout_notes", "review_sent", "review_satisfied",
    "review_completed", "referral_requested", "referral_received",
    "contractor_rating_sent", "contractor_rating_completed",
  ]);

  // Per-category counts for filter chip badges
  const countByCategory = useMemo(() => {
    const c: Record<string, number> = { pipeline: 0, build_review: 0, permitting: 0, scheduling: 0, offers: 0, closeout: 0, financial: 0, ordering: 0, materials: 0, field: 0, execution: 0, issues: 0 };
    for (const ev of domainEvents) c[ev.category] = (c[ev.category] ?? 0) + 1;
    // Count closeout/financial audit entries too (dual-source filter)
    for (const entry of auditEntries) {
      const fields = Object.keys(entry.changedFields ?? {});
      if (fields.some((f) => CLOSEOUT_AUDIT_FIELDS.has(f))) c.closeout = (c.closeout ?? 0) + 1;
      if (fields.some((f) => FINANCIAL_AUDIT_FIELDS.has(f))) c.financial = (c.financial ?? 0) + 1;
    }
    return c;
  }, [domainEvents, auditEntries]);

  // Apply filter
  const filteredItems = useMemo((): MergedItem[] => {
    if (activityFilter === "all") return mergedItems;
    if (activityFilter === "changes") return mergedItems.filter((m) => m.kind === "audit");
    if (activityFilter === "closeout") {
      return mergedItems.filter((m) =>
        (m.kind === "domain" && m.event.category === "closeout") ||
        (m.kind === "audit" && Object.keys(m.entry.changedFields ?? {}).some((f) => CLOSEOUT_AUDIT_FIELDS.has(f)))
      );
    }
    if (activityFilter === "financial") {
      return mergedItems.filter((m) =>
        (m.kind === "domain" && m.event.category === "financial") ||
        (m.kind === "audit" && Object.keys(m.entry.changedFields ?? {}).some((f) => FINANCIAL_AUDIT_FIELDS.has(f)))
      );
    }
    return mergedItems.filter((m) => m.kind === "domain" && m.event.category === activityFilter);
  }, [mergedItems, activityFilter]);

  // Group by calendar day (newest day first)
  const groupedByDay = useMemo(() => {
    const map = new Map<string, MergedItem[]>();
    for (const item of filteredItems) {
      const day = new Date(item.timestamp).toLocaleDateString("en-US", {
        weekday: "short", month: "short", day: "numeric", year: "numeric",
      });
      if (!map.has(day)) map.set(day, []);
      map.get(day)!.push(item);
    }
    return Array.from(map.entries());
  }, [filteredItems]);

  const FILTER_CHIPS: { key: ActivityFilter; label: string; count?: number }[] = [
    { key: "all", label: "All", count: mergedItems.length },
    { key: "changes", label: "Changes", count: auditEntries.length },
    { key: "pipeline", label: "Pipeline", count: countByCategory.pipeline },
    { key: "build_review", label: "Build Review", count: countByCategory.build_review },
    { key: "permitting", label: "Permitting", count: countByCategory.permitting },
    { key: "scheduling", label: "Scheduling", count: countByCategory.scheduling },
    { key: "offers", label: "Offers", count: countByCategory.offers },
    { key: "closeout", label: "Closeout", count: countByCategory.closeout },
    { key: "financial", label: "Financial", count: countByCategory.financial },
    { key: "issues", label: "Issues", count: countByCategory.issues },
    { key: "execution", label: "Execution", count: countByCategory.execution },
    { key: "ordering", label: "Ordering", count: countByCategory.ordering },
    { key: "materials", label: "Materials", count: countByCategory.materials },
    { key: "field", label: "Field", count: countByCategory.field },
  ];

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div className="w-8 h-8 rounded-full bg-muted shrink-0" />
            <div className="flex-1 space-y-1.5 pt-1">
              <div className="h-3 w-36 rounded bg-muted" />
              <div className="h-3 w-52 rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter chip bar */}
      <div className="flex flex-wrap gap-1.5">
        {FILTER_CHIPS.map((chip) => {
          const active = activityFilter === chip.key;
          return (
            <button
              key={chip.key}
              onClick={() => setActivityFilter(chip.key)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                active
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-border hover:border-foreground/30 hover:text-foreground"
              }`}
            >
              {chip.label}
              {chip.count !== undefined && chip.count > 0 && (
                <span className={`tabular-nums text-[10px] ${active ? "opacity-70" : "opacity-50"}`}>
                  {chip.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Activity feed */}
      {filteredItems.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">No activity in this category.</p>
      ) : (
        <div className="space-y-6">
          {groupedByDay.map(([day, items]) => (
            <div key={day}>
              {/* Day header */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-semibold tracking-widest uppercase text-muted-foreground/60 whitespace-nowrap">
                  {day}
                </span>
                <span className="text-[10px] text-muted-foreground/40 tabular-nums">· {items.length}</span>
                <div className="flex-1 h-px bg-border/40" />
              </div>

              {/* Events in this day */}
              <div className="relative pl-10">
                {/* Vertical connecting line */}
                <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border/50" />

                <ul className="space-y-0">
                  {items.map((item, idx) => {
                    const isLast = idx === items.length - 1;

                    if (item.kind === "domain") {
                      const ev = item.event;
                      const Icon = ev.icon;
                      return (
                        <li key={ev.id} className={`relative flex gap-3 ${isLast ? "pb-0" : "pb-4"}`}>
                          {/* Icon dot */}
                          <div className={`absolute -left-10 flex items-center justify-center rounded-full border bg-background shrink-0 ${ev.high ? "w-8 h-8 border-border" : "w-7 h-7 border-border/60"}`}>
                            <Icon className={`${ev.high ? "w-4 h-4" : "w-3.5 h-3.5"} ${ev.iconColor}`} />
                          </div>
                          <div className="flex-1 min-w-0 pt-0.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs font-semibold ${ev.high ? "text-foreground" : "text-foreground/80"}`}>
                                {ev.label}
                              </span>
                              <span className="text-[10px] tabular-nums text-muted-foreground">
                                {new Date(ev.timestamp).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                              </span>
                            </div>
                            <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{ev.detail}</p>
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-[10px] text-muted-foreground/60">{ev.actor}</span>
                            </div>
                          </div>
                        </li>
                      );
                    }

                    // Audit entry
                    const entry = item.entry;
                    const isCreate = entry.action === "create";
                    const changedFields = entry.changedFields ?? {};
                    const visibleKeys = Object.keys(changedFields).filter((f) => !AUDIT_SKIP_FIELDS.has(f));
                    const actor = entry.editedBy ?? (entry.userId ? "Unknown User" : "System");

                    return (
                      <li key={entry.id} className={`relative flex gap-3 ${isLast ? "pb-0" : "pb-4"}`}>
                        {/* Pencil dot for audit */}
                        <div className="absolute -left-10 flex items-center justify-center w-7 h-7 rounded-full border border-border/40 bg-background shrink-0">
                          <PencilLine className={`w-3.5 h-3.5 ${isCreate ? "text-emerald-500" : "text-muted-foreground/50"}`} />
                        </div>
                        <div className="flex-1 min-w-0 pt-0.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-semibold text-foreground/80">{actor}</span>
                            <span className="text-[10px] tabular-nums text-muted-foreground">
                              {new Date(entry.createdAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                            </span>
                          </div>
                          {isCreate ? (
                            <p className="mt-0.5 text-xs text-muted-foreground">Job record created</p>
                          ) : visibleKeys.length > 0 ? (
                            <div className="mt-1 space-y-1">
                              {visibleKeys.map((field) => {
                                const label = AUDIT_FIELD_LABELS[field] ?? field.replace(/_/g, " ");

                                // Materials list — structured added/removed/changed diff
                                if (field === "materials_list") {
                                  const matDiff = changedFields[field] as import("@/services/job-operations.service").MaterialsAuditDiff;
                                  const parts: string[] = [];
                                  if (matDiff.added?.length) parts.push(`${matDiff.added.length} added`);
                                  if (matDiff.removed?.length) parts.push(`${matDiff.removed.length} removed`);
                                  if (matDiff.changed?.length) parts.push(`${matDiff.changed.length} changed`);
                                  return (
                                    <div key={field} className="space-y-0.5">
                                      <p className="text-xs text-muted-foreground">
                                        <span className="font-medium text-foreground/70">{label} updated</span>
                                        {parts.length > 0 && (
                                          <span className="ml-1 text-muted-foreground">— {parts.join(", ")}</span>
                                        )}
                                      </p>
                                      {matDiff.added?.map((m) => (
                                        <p key={`add-${m.sku}`} className="text-[11px] text-muted-foreground pl-2">
                                          <span className="text-emerald-600">+</span>{" "}
                                          <span className="font-mono text-[10px]">{m.sku}</span>{" "}
                                          {m.name}{m.qty ? ` · qty ${m.qty}` : ""}
                                        </p>
                                      ))}
                                      {matDiff.removed?.map((m) => (
                                        <p key={`rem-${m.sku}`} className="text-[11px] text-muted-foreground pl-2">
                                          <span className="text-destructive">−</span>{" "}
                                          <span className="font-mono text-[10px] line-through opacity-60">{m.sku}</span>{" "}
                                          <span className="line-through opacity-60">{m.name}</span>
                                        </p>
                                      ))}
                                      {matDiff.changed?.map((m) => (
                                        <p key={`chg-${m.sku}`} className="text-[11px] text-muted-foreground pl-2">
                                          <span className="text-amber-500">~</span>{" "}
                                          <span className="font-mono text-[10px]">{m.sku}</span>{" "}
                                          {m.name}{" · qty "}
                                          <span className="line-through opacity-50">{m.oldQty}</span>
                                          {" → "}
                                          <span className="font-medium text-foreground">{m.newQty}</span>
                                        </p>
                                      ))}
                                    </div>
                                  );
                                }

                                // Standard old → new diff
                                const diff = changedFields[field] as { old: unknown; new: unknown };
                                const oldVal = formatAuditFieldValue(field, diff.old);
                                const newVal = formatAuditFieldValue(field, diff.new);
                                return (
                                  <p key={field} className="text-xs text-muted-foreground">
                                    <span className="font-medium text-foreground/70">{label}</span>
                                    {" "}
                                    <span className="line-through opacity-50">{oldVal}</span>
                                    {" → "}
                                    <span className="font-medium text-foreground">{newVal}</span>
                                  </p>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function JobDetailCockpit() {
  const { id } = useParams<{ id: string }>();
  const { products: registryProducts } = useProductRegistry();
  const {
    job,
    jobIssues: dbJobIssues,
    buildReviewEvents: dbBuildReviewEvents,
    executionProofs,
    jobCredits,
    jobMaterials,
    jobMaterialKit,
    purchaseOrders: jobPurchaseOrders,
    jobAllocations,
    schedulingEvents,
    leadAttribution,
    setLeadAttribution,
    setJob,
    loading,
    refetchJobMaterials,
    refetchPurchaseOrders,
    refetchJobAllocations,
   } = useJobDetailQuery(id);
  const { profile: currentUserProfile } = useCurrentUser();
  const handleMaterialsRefetch = async () => {
    await Promise.all([refetchPurchaseOrders(), refetchJobAllocations()]);
  };
  const [buildReviewEvents, setBuildReviewEvents] = useState(dbBuildReviewEvents);
  useEffect(() => { setBuildReviewEvents(dbBuildReviewEvents); }, [dbBuildReviewEvents]);
  // Fields not yet in single-job query — degrade gracefully
  const installerWrapRecords: import("@/data/installerWrapEngine").InstallerWrapRecord[] = [];
  const { role, roleInfo } = useRole();
  const CAN_REGISTER_PRODUCT = new Set(['super_admin', 'admin', 'owner', 'ops_manager']);
  const canRegisterProduct = CAN_REGISTER_PRODUCT.has(role ?? '');
  const { hasPermission: hasPerm } = usePermissions();
  const performedByLabel = roleInfo?.label ?? role;
  const { setCredits, refreshData, quotes } = useAppData();

  // Backfill a job's address from its source of truth when the record itself is
  // missing street/zip. This writes the address onto the job (the "jobs flow") so
  // Job detail and the Edit form show the correct value — no per-page display hack.
  // Source precedence: (1) the linked quote/contract, (2) the linked customer record.
  const addressBackfilledRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!job?.id) return;
    const own = job.address;
    const hasOwnStreetOrZip =
      !!own && (String(own.street ?? "").trim().length > 0 || String(own.zip ?? "").trim().length > 0);
    if (hasOwnStreetOrZip) return;
    if (addressBackfilledRef.current.has(job.id)) return;

    let cancelled = false;

    const writeAddress = (parsed: { street: string; city: string; state: string; zip: string }) => {
      if (cancelled || (!parsed.street && !parsed.zip)) return;
      addressBackfilledRef.current.add(job.id);
      JobService.update(job.id, { address: parsed } as Partial<Job>)
        .then(({ error }) => {
          if (!error) {
            setJob((prev) => (prev ? { ...prev, address: parsed } : prev));
          } else {
            addressBackfilledRef.current.delete(job.id);
          }
        })
        .catch(() => addressBackfilledRef.current.delete(job.id));
    };

    // (1) Linked quote / contract — covers opportunity-converted jobs.
    const linkedQuote = quotes.find(
      (q) => q.id === job.convertedFromQuoteId || q.convertedJobId === job.id || q.linkedJobId === job.id,
    );
    if (linkedQuote?.customerAddress && String(linkedQuote.customerAddress).trim()) {
      writeAddress(parseAddressPartsFromText(linkedQuote.customerAddress));
      return;
    }

    // (2) Linked customer record — covers migration-imported jobs.
    if (job.customerId) {
      CustomerService.getById(job.customerId)
        .then(({ data: cust }) => {
          if (cancelled || !cust) return;
          const c = cust as {
            address_line1?: string | null;
            street_address?: string | null;
            postal_code?: string | null;
            zip?: string | null;
          };
          const line1 = c.address_line1 || c.street_address || "";
          const zip = c.postal_code || c.zip || "";
          if (!String(line1).trim() && !String(zip).trim()) return;
          const parsedLine1 = parseAddressPartsFromText(line1);
          writeAddress({ ...parsedLine1, zip: String(zip).trim() || parsedLine1.zip });
        })
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
    };
  }, [job, quotes, setJob]);

  const [searchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    if (!t) return "overview";
    if (t === "execution") return "quality";
    return t;
  })();
  const fin$ = useFinancialPermissions();
  const navigate = useNavigate();
  const { getJobConflicts } = useSchedulingConflicts(job ? [job] : []);

  // Modal / tab state
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t === "execution" ? "quality" : t);
  }, [searchParams]);
  const [contractSheetOpen, setContractSheetOpen] = useState(false);
  const [orderingOpen, setOrderingOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [creditFormOpen, setCreditFormOpen] = useState(false);
  const [resolveIssueTarget, setResolveIssueTarget] = useState<import("@/data/types").Issue | null>(null);
  const [executionOpen, setExecutionOpen] = useState(false);
  const [changeOrderOpen, setChangeOrderOpen] = useState(false);
  const [rebuildingMaterials, setRebuildingMaterials] = useState(false);
  const [isGeneratingBoundSpec, setIsGeneratingBoundSpec] = useState(false);
  const generatingBoundSpec = isGeneratingBoundSpec;
  const [jobEditOpen, setJobEditOpen] = useState(false);
  const [materialsEditorOpen, setMaterialsEditorOpen] = useState(false);
  const [materialsEditorTab, setMaterialsEditorTab] = useState<"core" | "field">("core");
  const [materialsEditorDraft, setMaterialsEditorDraft] = useState<EditableMaterialLine[]>([]);
  const [quickRegisterOpen, setQuickRegisterOpen] = useState(false);
  const [quickRegisterPrefill, setQuickRegisterPrefill] = useState<{ name?: string; sku?: string }>({});
  const [fieldMaterialsDraft, setFieldMaterialsDraft] = useState<EditableFieldMaterialLine[]>([]);
  const [fieldReceiptLibraryDraft, setFieldReceiptLibraryDraft] = useState<EditableFieldMaterialReceipt[]>([]);
  const [materialsEditorSaving, setMaterialsEditorSaving] = useState(false);
  const [auditRefreshToken, setAuditRefreshToken] = useState(0);
  const [fieldMaterialPostingId, setFieldMaterialPostingId] = useState<string | null>(null);
  const [fieldMaterialReversingId, setFieldMaterialReversingId] = useState<string | null>(null);
  const [fieldReceiptParsingId, setFieldReceiptParsingId] = useState<string | null>(null);
  const [catalogProducts, setCatalogProducts] = useState<RegistryProduct[]>([]);
  const [catalogProductsLoading, setCatalogProductsLoading] = useState(false);
  const [activeMaterialMatcherId, setActiveMaterialMatcherId] = useState<string | null>(null);
  const [activeFieldMaterialMatcherId, setActiveFieldMaterialMatcherId] = useState<string | null>(null);
  const [jobAttachments, setJobAttachments] = useState<Attachment[]>([]);
  const [leadAttributionOpen, setLeadAttributionOpen] = useState(false);
  const [selectedLeadAttributionId, setSelectedLeadAttributionId] = useState("");
  const [savingLeadAttribution, setSavingLeadAttribution] = useState(false);
  const [subAssignments, setSubAssignments] = useState<SubcontractorAssignment[]>([]);
  const [scopedCredits, setScopedCredits] = useState<CustomerCredit[]>(jobCredits);
  useEffect(() => { setScopedCredits(jobCredits); }, [jobCredits]);

  useEffect(() => {
    if (!job?.id) return;
    void SubcontractorAssignmentService.listByJob(job.id).then(setSubAssignments);
  }, [job?.id]);

  const [invoiceRefs, setInvoiceRefs] = useState<Array<{ id: string; invoiceNumber: string | null; type: string; status: string }>>([]);
  useEffect(() => {
    if (!job?.id) return;
    let active = true;
    AccountingService.listInvoicesByJob(job.id).then(({ data }) => {
      if (!active) return;
      setInvoiceRefs(
        (data ?? [])
          .filter((r) => r.status !== "void")
          .map((r) => ({ id: r.id, invoiceNumber: r.invoice_number, type: r.status, status: r.status }))
      );
    });
    return () => { active = false; };
  }, [job?.id]);

  // Pipeline data — linked lead + appointment results + consultations (shared by Activity + Timeline tabs)
  const [pipelineLead, setPipelineLead] = useState<DbLead | null>(null);
  const [pipelineAppts, setPipelineAppts] = useState<DbAppointmentResult[]>([]);
  const [pipelineConsultations, setPipelineConsultations] = useState<ConsultationSessionData[]>([]);
  const [installerOffers, setInstallerOffers] = useState<MarketplaceOffer[]>([]);
  const [phaseExecutions, setPhaseExecutions] = useState<{ phaseName: string; status: string; completedAt?: string; completedBy?: string; qaReviewedAt?: string; qaReviewer?: string; qaFailReason?: string }[]>([]);
  const [serviceTickets, setServiceTickets] = useState<ServiceTicket[]>([]);
  useEffect(() => {
    if (!job?.id) return;
    let cancelled = false;
    void (async () => {
      const [{ data }, offers, execRes, svcRes] = await Promise.all([
        LeadService.getByJobId(job.id, job.customerId),
        MarketplaceService.getOffersByJobId(job.id),
        supabase.from("job_spec_executions").select("metadata").eq("job_id", job.id),
        supabase.from("service_tickets").select("id,ticket_id,job_id,customer_id,customer_name,customer_phone,customer_email,address,category,priority,status,subject,description,resolution,resolved_at,resolved_by,assigned_to,created_by,scheduled_date,scheduled_time_slot,activities,notes,created_at,updated_at").eq("job_id", job.id).order("created_at", { ascending: true }),
      ]);
      if (cancelled) return;
      const lead = data as DbLead | null;
      setPipelineLead(lead);
      setInstallerOffers(offers);
      const phases: { phaseName: string; status: string; completedAt?: string; completedBy?: string; qaReviewedAt?: string; qaReviewer?: string; qaFailReason?: string }[] = [];
      for (const row of (execRes.data ?? []) as Array<{ metadata?: unknown }>) {
        const meta = row.metadata as { phases?: Array<{ phaseName?: string; status?: string; completedAt?: string; completedBy?: string; qaReviewedAt?: string; qaReviewer?: string; qaFailReason?: string }> } | null;
        for (const p of meta?.phases ?? []) {
          if (!p.phaseName) continue;
          phases.push({ phaseName: p.phaseName, status: p.status ?? "not_started", completedAt: p.completedAt, completedBy: p.completedBy, qaReviewedAt: p.qaReviewedAt, qaReviewer: p.qaReviewer, qaFailReason: p.qaFailReason });
        }
      }
      setPhaseExecutions(phases);
      if (svcRes.error) {
        console.warn("[JobDetailCockpit] service_tickets fetch error:", svcRes.error);
      } else if (svcRes.data) {
        setServiceTickets((svcRes.data as Parameters<typeof toUiServiceTicket>[0][]).map((r) => toUiServiceTicket(r)));
      }
      if (lead?.id) {
        const [appts, consultations] = await Promise.all([
          AppointmentResultService.getByLeadId(lead.id),
          ConsultationService.getAllByLeadId(lead.id),
        ]);
        if (!cancelled) {
          setPipelineAppts(appts);
          setPipelineConsultations(consultations);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [job?.id]);

  useEffect(() => {
    setSelectedLeadAttributionId(leadAttribution.currentLead?.id ?? leadAttribution.candidateLeads[0]?.id ?? "");
  }, [leadAttribution.currentLead?.id, leadAttribution.candidateLeads]);

  useEffect(() => {
    if (!materialsEditorOpen) return;

    const availableRegistryProducts = registryProducts.filter((product) => product.active && normalizeMaterialSku(product.sku));
    if (availableRegistryProducts.length > 0) {
      setCatalogProducts(availableRegistryProducts);
      setCatalogProductsLoading(false);
      return;
    }

    if (catalogProducts.length > 0) return;
    let active = true;
    setCatalogProductsLoading(true);
    void ProductRegistryService.getProductsForSkuMatching()
      .then((products) => {
        if (!active) return;
        setCatalogProducts(products.filter((product) => product.active && normalizeMaterialSku(product.sku)));
      })
      .catch((error) => {
        console.error("[JobDetailCockpit] Failed to load product registry catalog for material matching:", error);
        if (active) {
          toast.error("Failed to load product catalog");
        }
      })
      .finally(() => {
        if (active) setCatalogProductsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [catalogProducts.length, materialsEditorOpen, registryProducts]);

  useEffect(() => {
    if (!materialsEditorOpen || !job?.id) return;
    let active = true;
    void getAttachments("job", job.id)
      .then((records) => {
        if (active) setJobAttachments(records);
      })
      .catch((error) => {
        console.error("[JobDetailCockpit] Failed to load job attachments for field materials:", error);
      });
    return () => {
      active = false;
    };
  }, [job?.id, materialsEditorOpen]);

  const catalogProductBySku = useMemo(() => {
    const map = new Map<string, RegistryProduct>();
    catalogProducts.forEach((product) => {
      map.set(normalizeMaterialSku(product.sku), product);
    });
    return map;
  }, [catalogProducts]);

  const jobAttachmentById = useMemo(() => {
    const map = new Map<string, Attachment>();
    jobAttachments.forEach((attachment) => {
      map.set(attachment.id, attachment);
    });
    return map;
  }, [jobAttachments]);

  const authoritativeJobMaterials = useMemo(
    () => {
      const taxPercentBySku = new Map(
        (job?.materialsList ?? [])
          .filter((row) => normalizeMaterialSku(row?.sku))
          .map((row) => [normalizeMaterialSku(row.sku), row.taxPercent] as const),
      );

      return jobMaterials
        .map(mapRawJobMaterialToLine)
        .filter((row): row is MaterialLineItem => Boolean(row))
        .map((row) => {
          const savedTaxPercent = taxPercentBySku.get(normalizeMaterialSku(row.sku));
          return typeof savedTaxPercent === "number"
            ? { ...row, taxPercent: savedTaxPercent }
            : row;
        });
    },
    [job?.materialsList, jobMaterials],
  );

  const fieldMaterialGroupsByReceipt = useMemo(() => {
    const groups = new Map<string, {
      receipt: EditableFieldMaterialReceipt;
      receiptClientIds: string[];
      materials: EditableFieldMaterialLine[];
    }>();

    for (const receipt of fieldReceiptLibraryDraft) {
      const identity = getEditableFieldMaterialReceiptIdentity(receipt);
      const existing = groups.get(identity);
      if (existing) {
        if (!existing.receipt.attachmentId && receipt.attachmentId) {
          existing.receipt = receipt;
        }
        existing.receiptClientIds.push(receipt.clientId);
      } else {
        groups.set(identity, {
          receipt,
          receiptClientIds: [receipt.clientId],
          materials: [],
        });
      }
    }

    for (const material of fieldMaterialsDraft) {
      const linkedGroup = Array.from(groups.values()).find((group) =>
        group.receiptClientIds.some((clientId) => material.receiptClientIds.includes(clientId)),
      );
      if (!linkedGroup) continue;
      if (!linkedGroup.materials.some((row) => row.editorId === material.editorId)) {
        linkedGroup.materials.push(material);
      }
    }

    return Array.from(groups.values()).filter((group) => group.materials.length > 0);
  }, [fieldMaterialsDraft, fieldReceiptLibraryDraft]);

  const fieldReceiptLibraryDisplayGroups = useMemo(() => {
    const groups = new Map<string, {
      receipt: EditableFieldMaterialReceipt;
      receiptClientIds: string[];
    }>();

    for (const receipt of fieldReceiptLibraryDraft) {
      const identity = getEditableFieldMaterialReceiptIdentity(receipt);
      const existing = groups.get(identity);
      if (existing) {
        existing.receipt = mergeEditableFieldMaterialReceipt(existing.receipt, receipt);
        if (!existing.receiptClientIds.includes(receipt.clientId)) {
          existing.receiptClientIds.push(receipt.clientId);
        }
      } else {
        groups.set(identity, {
          receipt,
          receiptClientIds: [receipt.clientId],
        });
      }
    }

    return Array.from(groups.values());
  }, [fieldReceiptLibraryDraft]);

  const unlinkedFieldMaterials = useMemo(
    () => fieldMaterialsDraft.filter((material) => material.receiptClientIds.length === 0),
    [fieldMaterialsDraft],
  );

  const coreMaterialsDraftTotalCost = useMemo(
    () => materialsEditorDraft.reduce((sum, row) => sum + getCoreMaterialLineTotal(row), 0),
    [materialsEditorDraft],
  );

  const fieldMaterialsDraftTaxTotal = useMemo(
    () => fieldMaterialGroupsByReceipt.reduce((sum, group) => sum + (typeof group.receipt.tax === "number" ? Math.max(0, group.receipt.tax) : 0), 0),
    [fieldMaterialGroupsByReceipt],
  );

  const fieldMaterialsDraftTotalCost = useMemo(
    () => fieldMaterialsDraft.reduce((sum, row) => sum + getFieldMaterialLineSubtotal(row), 0) + fieldMaterialsDraftTaxTotal,
    [fieldMaterialsDraft, fieldMaterialsDraftTaxTotal],
  );

  const overallMaterialsDraftTotalCost = coreMaterialsDraftTotalCost + fieldMaterialsDraftTotalCost;

  const renderFieldMaterialEditorRow = (
    material: EditableFieldMaterialLine,
    indexLabel: string,
  ) => {
    const normalizedSku = normalizeMaterialSku(material.sku);
    const matchedProduct = catalogProductBySku.get(normalizedSku);
    const isPosted = isFieldMaterialInventoryPosted(material);
    const isPostingThisRow = fieldMaterialPostingId === material.editorId;
    const isReversingThisRow = fieldMaterialReversingId === material.editorId;
    const isFieldMatcherOpen = activeFieldMaterialMatcherId === material.editorId;

    return (
      <div
        key={material.editorId}
        className="grid grid-cols-[minmax(180px,1.15fr)_minmax(180px,0.95fr)_84px_110px_120px_minmax(240px,1.25fr)_44px] gap-2 rounded-md border bg-background px-2 py-2"
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{indexLabel}</Label>
            {isPosted ? (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-300">
                Posted to Inventory
              </Badge>
            ) : null}
          </div>
          <Input
            className="h-8 text-xs"
            value={material.itemName}
            onChange={(event) => handleUpdateFieldMaterialDraft(material.editorId, { itemName: event.target.value })}
            placeholder="Adhesive, hardware, trim..."
            disabled={isPosted}
          />
          <Input
            className="h-8 text-xs"
            value={material.vendorName ?? ""}
            onChange={(event) => handleUpdateFieldMaterialDraft(material.editorId, { vendorName: event.target.value || undefined })}
            placeholder="Vendor / store"
            disabled={isPosted}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">SKU</Label>
          <div className="space-y-1">
            <Input
              className="h-8 text-xs font-mono"
              value={material.sku ?? ""}
              onChange={(event) => handleUpdateFieldMaterialDraft(material.editorId, { sku: event.target.value || undefined })}
              placeholder="Required to post"
              disabled={isPosted}
            />
            <div className="flex items-center gap-1">
              <Popover open={isFieldMatcherOpen} onOpenChange={(open) => setActiveFieldMaterialMatcherId(open ? material.editorId : null)}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-7 min-w-0 flex-1 justify-start px-2 text-[11px]"
                    disabled={isPosted}
                  >
                    <span className="truncate">{matchedProduct ? matchedProduct.sku : "Match SKU"}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[340px] p-0" align="end">
                  <Command className="overflow-visible">
                    <CommandInput placeholder="Search Product Registry name or SKU..." />
                    <CommandList className="max-h-[260px] overflow-y-scroll pointer-events-auto">
                      <CommandEmpty>
                        {catalogProductsLoading ? "Loading Product Registry..." : "No Product Registry SKU found"}
                      </CommandEmpty>
                      <CommandGroup>
                        {catalogProducts.map((product) => (
                          <CommandItem
                            key={product.productId}
                            value={`${product.sku} ${product.productName} ${product.category}`}
                            onSelect={() => handleMatchFieldMaterialSku(material.editorId, product)}
                          >
                            <div className="min-w-0">
                              <div className="text-xs font-medium">{product.productName}</div>
                              <div className="text-[10px] text-muted-foreground">{product.sku} · {product.category}</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {matchedProduct ? (
                <Badge variant="secondary" className="shrink-0 text-[10px]">Matched</Badge>
              ) : normalizedSku ? (
                <Badge variant="outline" className="shrink-0 border-warning/40 text-[10px] text-warning">Manual</Badge>
              ) : null}
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Qty</Label>
          <Input
            type="number"
            min={1}
            className="h-8 text-xs"
            value={material.quantity}
            onChange={(event) =>
              handleUpdateFieldMaterialDraft(material.editorId, {
                quantity: Math.max(1, Number(event.target.value) || 1),
              })
            }
            disabled={isPosted}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Unit Cost</Label>
          <Input
            type="number"
            min={0}
            step="0.01"
            className="h-8 text-xs"
            value={material.unitCost ?? ""}
            onChange={(event) =>
              handleUpdateFieldMaterialDraft(material.editorId, {
                unitCost: event.target.value === "" ? undefined : Math.max(0, Number(event.target.value) || 0),
              })
            }
            placeholder="0.00"
            disabled={isPosted}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Purchased</Label>
          <Input
            type="date"
            className="h-8 text-xs"
            value={material.purchasedAt ? material.purchasedAt.slice(0, 10) : ""}
            onChange={(event) => handleUpdateFieldMaterialDraft(material.editorId, { purchasedAt: event.target.value || undefined })}
            disabled={isPosted}
          />
        </div>
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={isPosted ? "outline" : "secondary"}
              size="sm"
              className="h-7 px-2 text-[11px]"
              onClick={() => (isPosted ? handleReverseFieldMaterialPosting(material.editorId) : handlePostFieldMaterialToInventory(material.editorId))}
              disabled={
                isReversingThisRow ||
                isPostingThisRow ||
                materialsEditorSaving ||
                (isPosted
                  ? false
                  : !String(material.itemName ?? "").trim() ||
                    !String(material.sku ?? "").trim() ||
                    material.receiptClientIds.length === 0)
              }
            >
              {isPostingThisRow
                ? "Posting..."
                : isReversingThisRow
                  ? "Reversing..."
                  : isPosted
                    ? "Reverse Posting"
                    : "Post to Inventory"}
            </Button>
            {isPosted ? (
              <p className="text-[11px] text-muted-foreground">
                {material.inventoryPostedAt
                  ? `Posted ${formatStandardDate(material.inventoryPostedAt)} by ${material.inventoryPostedBy ?? "team"}`
                  : "Already posted to inventory"}
              </p>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Receipt + SKU required. Posts on-hand stock only and stays out of ordering.
              </p>
            )}
            {isPosted ? (
              <p className="text-[11px] text-muted-foreground">
                Reversal removes the stock added by this field purchase and flags the audit row for review.
              </p>
            ) : null}
          </div>
          <div className="space-y-1">
            {fieldReceiptLibraryDraft.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">Upload a receipt in the shared library above, then link it here.</p>
            ) : (
              fieldReceiptLibraryDraft.map((receipt) => {
                const existingAttachment = receipt.attachmentId ? jobAttachmentById.get(receipt.attachmentId) : null;
                const isLinked = material.receiptClientIds.includes(receipt.clientId);
                return (
                  <div key={receipt.clientId} className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-[11px]">
                    <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={isLinked}
                        onChange={() => handleToggleFieldMaterialReceipt(material.editorId, receipt.clientId)}
                        disabled={isPosted}
                      />
                      <div className="min-w-0">
                        <p className="truncate font-medium">{receipt.fileName || existingAttachment?.fileName || "Receipt"}</p>
                        <p className="truncate text-muted-foreground">
                          {receipt.file ? "Pending upload" : "Saved receipt"}
                        </p>
                      </div>
                    </label>
                    <div className="flex items-center gap-1">
                      {!receipt.file && receipt.attachmentId && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-[11px]"
                          onClick={() => handleViewFieldMaterialReceipt(receipt.attachmentId!)}
                        >
                          View
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <Textarea
            value={material.notes ?? ""}
            onChange={(event) => handleUpdateFieldMaterialDraft(material.editorId, { notes: event.target.value || undefined })}
            placeholder="Notes about why this was purchased in the field..."
            className="min-h-[64px] text-xs"
          />
        </div>
        <div className="flex items-start justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => handleRemoveFieldMaterialDraft(material.editorId)}
            disabled={isPosted || isPostingThisRow || isReversingThisRow}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      </div>
    );
  };

  const handleGenerateBoundSpec = useCallback(async () => {
    if (!job || isGeneratingBoundSpec) return;
    if (authoritativeJobMaterials.length === 0) {
      toast.error("No job materials found", {
        description: "Generate Bound Spec requires rows from the job_materials table.",
      });
      return;
    }

    const boundJobSpec = deriveBoundSpecForJob({
      ...job,
      materialsList: authoritativeJobMaterials,
    });

    if (!boundJobSpec) {
      toast.error("Could not generate bound spec", {
        description: "Check the job project type and the job_materials SKU snapshot first.",
      });
      return;
    }

    setIsGeneratingBoundSpec(true);
    try {
      const { data, error } = await JobService.update(job.id, { boundJobSpec });
      if (error || !data) {
        console.error("[JobDetailCockpit] Failed to generate bound spec:", error);
        toast.error("Failed to generate bound spec");
        return;
      }
      setJob(data);
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after generating bound spec:", err);
      });
      toast.success("Bound spec generated", {
        description: `${boundJobSpec.specId} v${boundJobSpec.versionNumber} pinned from job_materials.`,
      });
    } finally {
      setIsGeneratingBoundSpec(false);
    }
  }, [authoritativeJobMaterials, isGeneratingBoundSpec, job, refreshData, setJob]);

  const canCreateChangeOrder = hasPerm("jobs_create") || hasPerm("jobs_edit");
  const canReviewQA = hasPerm("qa_approve");
  const canEditJob = hasPerm("jobs_edit");

  const findOpenIssueForJob = useCallback(
    (jobId: string) => (dbJobIssues).find((i) => i.jobId === jobId && i.status !== "Resolved" && i.status !== "Closed") ?? null,
    [dbJobIssues],
  );
  const pushChain = usePushChain({ navigate, findOpenIssueForJob });

  const jobIssues = dbJobIssues;
  const openIssues = useMemo(() => jobIssues.filter((i) => i.status === "Open" || i.status === "In Progress"), [jobIssues]);
  const wrapRecords = useMemo(
    (): import("@/data/installerWrapEngine").InstallerWrapRecord[] =>
      job ? installerWrapRecords.filter((w) => w.jobId === job.id) : [],
    [job, installerWrapRecords],
  );
  const derived = useMemo(() => job ? deriveJobState(job, openIssues, wrapRecords) : null, [job, openIssues, wrapRecords]);
  const isExternalManaged = useMemo(
    () => (job ? isExternalManagedImport(job) : false),
    [job],
  );
  const blockers = useMemo(() => {
    if (!job) return [];
    const base = getJobBlockers(job, openIssues);
    if (!isExternalManaged) return base;
    return base.filter((b) => {
      if (b.category === "permit") return !migrationBypasses(job, "permitting");
      if (b.category === "materials") return !migrationBypasses(job, "ordering");
      return true;
    });
  }, [job, openIssues, isExternalManaged]);
  const conflicts = useMemo(() => job ? getJobConflicts(job.id) : [], [job, getJobConflicts]);
  const linkedQuoteId = leadAttribution.linkedQuoteId;
  const documentQuoteId = linkedQuoteId ?? job?.convertedFromQuoteId ?? null;
  const documentJob = useMemo(
    () => (job && documentQuoteId ? ({ ...job, convertedFromQuoteId: documentQuoteId } as Job) : job),
    [documentQuoteId, job],
  );
  const documentQuotes = useMemo(
    () => documentQuoteId && job
      ? [{
          id: documentQuoteId,
          displayReference: documentQuoteId,
          quoteNumber: documentQuoteId,
          status: "Won",
          createdAt: job.createdAt,
          createdBy: job.salesRep || "System",
          updatedAt: job.updatedAt || job.createdAt,
          customerName: job.customerName,
        }]
      : [],
    [documentQuoteId, job],
  );

  const handleSaveLeadAttribution = useCallback(async () => {
    if (!job || !selectedLeadAttributionId) return;
    const selected = leadAttribution.candidateLeads.find((lead) => lead.id === selectedLeadAttributionId);
    if (!selected) return;

    const sourceLabel = getLeadSourceDisplay(selected);
    const enteredAt = getLeadEnteredAt(selected);
    const effectiveQuoteId = linkedQuoteId ?? selected.linkedQuoteId ?? leadAttribution.linkedQuoteId;

    setSavingLeadAttribution(true);
    try {
      const clearExisting = await supabase
        .from("leads")
        .update({ linked_job_id: null })
        .eq("linked_job_id", job.id);
      if (clearExisting.error) throw clearExisting.error;

      const linkLead = await supabase
        .from("leads")
        .update({
          linked_job_id: job.id,
          linked_quote_id: effectiveQuoteId ?? selected.linkedQuoteId ?? null,
        })
        .eq("id", selected.id);
      if (linkLead.error) throw linkLead.error;

      if (effectiveQuoteId) {
        const linkQuote = await supabase
          .from("quotes")
          .update({
            lead_id: selected.id,
            lead_source: sourceLabel === "Unknown source" ? null : sourceLabel,
            linked_job_id: job.id,
          })
          .eq("id", effectiveQuoteId);
        if (linkQuote.error) throw linkQuote.error;
      }

      const marketing = {
        leadSource: sourceLabel === "Unknown source" ? (job.marketing?.leadSource ?? "") : sourceLabel,
        campaignName: selected.sourceName ?? job.marketing?.campaignName ?? "",
        aggregatorPlatform: selected.provider ?? job.marketing?.aggregatorPlatform ?? "",
        callCenterRep: job.marketing?.callCenterRep ?? "",
        appointmentDate: enteredAt ?? job.marketing?.appointmentDate ?? "",
        saleOutcome: job.marketing?.saleOutcome ?? "Closed",
        leadCost: selected.leadCost ?? job.marketing?.leadCost,
        campaign: selected.sourceId ?? job.marketing?.campaign,
      };

      const { data, error } = await JobService.update(job.id, { marketing });
      if (error || !data) throw error || new Error("Failed to update job lead attribution");

      setJob(data);
      setLeadAttribution((prev) => ({
        ...prev,
        currentLead: {
          ...selected,
          linkedJobId: job.id,
          linkedQuoteId: effectiveQuoteId ?? selected.linkedQuoteId,
        },
        candidateLeads: prev.candidateLeads.map((lead) => ({
          ...lead,
          linkedJobId: lead.id === selected.id ? job.id : lead.linkedJobId === job.id ? null : lead.linkedJobId,
          linkedQuoteId: lead.id === selected.id ? effectiveQuoteId ?? lead.linkedQuoteId : lead.linkedQuoteId,
        })),
        linkedQuoteId: effectiveQuoteId ?? prev.linkedQuoteId,
      }));
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after lead attribution update:", err);
      });
      setLeadAttributionOpen(false);
      toast.success("Lead attribution updated", {
        description: `Crediting ${sourceLabel}${enteredAt ? ` from ${formatLeadAttributionDate(enteredAt)}` : ""}`,
      });
    } catch (error) {
      console.error("[JobDetailCockpit] Failed to update lead attribution:", error);
      toast.error(error instanceof Error ? error.message : "Could not update lead attribution");
    } finally {
      setSavingLeadAttribution(false);
    }
  }, [
    job,
    leadAttribution.candidateLeads,
    leadAttribution.linkedQuoteId,
    linkedQuoteId,
    refreshData,
    selectedLeadAttributionId,
    setLeadAttribution,
    setJob,
  ]);

  const handleActionClick = useCallback(
    async (action: SuggestedAction) => {
      if (!job) return;
      let executable: ExecutableAction;
      if (action.executionMode === "modal" && action.icon === "review") {
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => setReviewOpen(true) };
      } else if (action.executionMode === "modal" && action.icon === "materials") {
        executable = { label: action.cta || action.label, executionMode: "navigate", route: `/ordering-queue?job=${job.id}` };
      } else if (action.executionMode === "modal" && action.icon === "schedule") {
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => setRescheduleOpen(true) };
      } else if (action.executionMode === "modal" && (action.icon === "dispatch" || action.icon === "crew")) {
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => setAssignOpen(true) };
      } else if (action.executionMode === "modal" && action.icon === "issue") {
        const sevRank = (s: string) => (s === "Urgent" ? 0 : s === "High" ? 1 : s === "Medium" ? 2 : 3);
        const top = [...openIssues].sort((a, b) => sevRank(a.severity) - sevRank(b.severity))[0] || null;
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => setResolveIssueTarget(top) };
      } else {
        executable = { label: action.cta || action.label, executionMode: "navigate", route: action.link || `/jobs/${job.id}` };
      }
      const handled = await runAction(executable, { navigate });
      if (!handled && action.link) navigate(action.link);
    },
    [job, openIssues, navigate],
  );

  const handleOrderingSubmit = useCallback(
    async (data: { jobId: string; vendor: string; orderType: "Batch" | "Direct" | "Rush"; notes: string }) => {
      if (!job) return;
      const patch = {
        materialReadiness: "Ordered" as MaterialReadiness,
        planningStatus: "Ordered" as const,
        selectedVendor: data.vendor,
        activityHistory: [
          ...job.activityHistory,
          { id: `jd-order-${Date.now()}`, date: new Date().toISOString(), action: `Materials ordered — ${data.vendor} (${data.orderType})`, user: "Ops", noteType: "ops" as const },
        ],
      };
      const { data: persisted, error } = await JobService.update(job.id, patch);
      if (error || !persisted) {
        console.error("Failed to persist ordering update:", error);
        toast.error("Failed to place order");
        return;
      }
      setJob(persisted);
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after ordering submit:", err);
      });
      toast.success("Order placed", { description: `${data.vendor} — ${data.orderType}` });
    },
    [job, refreshData, setJob],
  );

  /**
   * Build Review approve — mirrors BuildReviewPanel.handleApprove so the
   * Push-Mode flow updates job state identically: scope locked, ordering
   * approved, planning advanced. Logs a build review event so the panel
   * elsewhere stays in sync.
   */
  const handleReviewApprove = useCallback(async (jobId: string) => {
    if (!job || job.id !== jobId) return;
    try {
      const lookup = job.jobNumber || job.id;
      const { data, error } = await BuildReviewService.approve(String(lookup), "ops_manager");
      if (error || !data) {
        throw error || new Error("Failed to approve build review");
      }

      const evt: BuildReviewEvent = {
        id: crypto.randomUUID(),
        jobId,
        action: "approved",
        timestamp: new Date().toISOString(),
        performedBy: "ops_manager",
      };
      setBuildReviewEvents((prev) => [...prev, evt]);
      setJob(data);
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after build review approve:", err);
      });
      toast.success("Build review approved", { description: "Job moved to Material Planning" });
      setReviewOpen(false);
      setTab("financials");
    } catch (error) {
      console.error("Failed to approve build review from job detail:", error);
      toast.error("Failed to approve build review");
    }
  }, [job, refreshData, setBuildReviewEvents, setJob]);

  const handleReviewApproveAndContinue = useCallback(async (jobId: string) => {
    if (!job || job.id !== jobId) return;
    try {
      const lookup = job.jobNumber || job.id;
      const { data, error } = await BuildReviewService.approve(String(lookup), "ops_manager");
      if (error || !data) {
        throw error || new Error("Failed to approve build review");
      }
      const evt: BuildReviewEvent = {
        id: crypto.randomUUID(),
        jobId,
        action: "approved",
        timestamp: new Date().toISOString(),
        performedBy: role,
      };
      setBuildReviewEvents((prev) => [...prev, evt]);
      setJob(data);
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after approve-and-continue:", err);
      });
      toast.success("Build review approved", { description: "Job moved to Material Planning" });
    } catch (error) {
      console.error("Failed to approve build review from job detail:", error);
      toast.error("Failed to approve build review");
    }
  }, [job, refreshData, role, setBuildReviewEvents, setJob]);

  const handleReviewRevision = useCallback(async (jobId: string, notes: string, materials: string, reason: RevisionReason) => {
    if (!job || job.id !== jobId) return;
    try {
      const lookup = job.jobNumber || job.id;
      const { error } = await BuildReviewService.requestRevision(String(lookup), "ops_manager", notes, reason, materials);
      if (error) throw error;
      const evt: BuildReviewEvent = {
        id: crypto.randomUUID(),
        jobId,
        action: "revision_requested",
        timestamp: new Date().toISOString(),
        performedBy: "ops_manager",
        revisionNotes: notes,
        revisionReason: reason,
        materialChanges: materials,
      };
      setBuildReviewEvents((prev) => [...prev, evt]);
      setJob((prev) =>
        prev && prev.id === jobId
          ? { ...prev, holdStatus: "On Hold — Sales Action Required" as const, holdReason: REVISION_REASON_LABELS[reason] }
          : prev,
      );
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after revision request:", err);
      });
      toast.info("Revision requested", { description: `On Hold — assigned to ${job.salesRep || "Sales Rep"} for resolution` });
      setReviewOpen(false);
    } catch (error) {
      console.error("Failed to request revision from job detail:", error);
      toast.error("Failed to request revision");
    }
  }, [job, refreshData, setBuildReviewEvents, setJob]);

  const handleRebuildMaterials = useCallback(async () => {
    if (!job || rebuildingMaterials) return;
    setRebuildingMaterials(true);
    try {
      const lookup = job.jobNumber || job.id;
      const { data, error } = await BuildReviewService.rebuildMaterials(String(lookup));
      if (error || !data) throw error || new Error("Failed to rebuild materials");
      setJob(data);
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after rebuild materials:", err);
      });
      toast.success("Materials rebuilt", { description: "BOM and DB materials snapshot are now re-synced." });
    } catch (error) {
      console.error("[JobDetailCockpit] Failed to rebuild materials:", error);
      toast.error("Failed to rebuild materials");
    } finally {
      setRebuildingMaterials(false);
    }
  }, [job, rebuildingMaterials, refreshData, setJob]);

  const handleOpenMaterialsEditor = useCallback(() => {
    const baseRows = authoritativeJobMaterials.length > 0
      ? authoritativeJobMaterials
      : (Array.isArray(job?.materialsList) ? job.materialsList : []);
    const existingFieldMaterials = Array.isArray(job?.fieldMaterials) ? job.fieldMaterials : [];
    const receiptIdentityToClientId = new Map<string, string>();
    const receiptLibrary: EditableFieldMaterialReceipt[] = [];

    const ensureReceiptReference = (receipt: NonNullable<FieldMaterialItem["receipts"]>[number]) => {
      const editableReceipt = createEditableFieldMaterialReceipt(receipt);
      const identity = getEditableFieldMaterialReceiptIdentity(editableReceipt);
      const existingClientId = receiptIdentityToClientId.get(identity);
      if (existingClientId) return existingClientId;
      receiptIdentityToClientId.set(identity, editableReceipt.clientId);
      receiptLibrary.push(editableReceipt);
      return editableReceipt.clientId;
    };

    setMaterialsEditorDraft(
      baseRows.length > 0
        ? baseRows.map((material) => createEditableMaterialLine(material))
        : [createEditableMaterialLine()],
    );
    setFieldMaterialsDraft(
      existingFieldMaterials.length > 0
        ? existingFieldMaterials.flatMap((material) => {
            const receiptClientIds = getFieldMaterialReceiptReferences(material)
              .map((receipt) => ensureReceiptReference(receipt));
            if (isFieldMaterialReceiptPlaceholder(material)) return [];
            const editableLine = createEditableFieldMaterialLine(material);
            editableLine.receiptClientIds = receiptClientIds;
            return [editableLine];
          })
        : [],
    );
    setFieldReceiptLibraryDraft(receiptLibrary);
    setMaterialsEditorTab("core");
    setActiveMaterialMatcherId(null);
    setActiveFieldMaterialMatcherId(null);
    setMaterialsEditorOpen(true);
  }, [authoritativeJobMaterials, job]);

  useEffect(() => {
    if (!materialsEditorOpen || jobAttachments.length === 0) return;

    setFieldReceiptLibraryDraft((prev) => {
      const existingAttachmentIds = new Set(prev.map((receipt) => receipt.attachmentId).filter(Boolean));
      const fallbackReceipts = jobAttachments
        .filter((attachment) => attachment.notes === "field_material_receipt")
        .filter((attachment) => !existingAttachmentIds.has(attachment.id))
        .map((attachment) => ({
          clientId: crypto.randomUUID(),
          attachmentId: attachment.id,
          fileName: attachment.fileName,
          mimeType: attachment.mimeType,
          createdAt: attachment.createdAt,
        } satisfies EditableFieldMaterialReceipt));

      if (fallbackReceipts.length === 0) return prev;
      return [...prev, ...fallbackReceipts];
    });
  }, [jobAttachments, materialsEditorOpen]);

  const handleUpdateMaterialDraft = useCallback((editorId: string, updates: Partial<EditableMaterialLine>) => {
    setMaterialsEditorDraft((prev) =>
      prev.map((row) => (row.editorId === editorId ? { ...row, ...updates } : row)),
    );
  }, []);

  const handleAddMaterialDraft = useCallback(() => {
    setMaterialsEditorDraft((prev) => [...prev, createEditableMaterialLine()]);
  }, []);

  const handleUpdateFieldMaterialDraft = useCallback((editorId: string, updates: Partial<EditableFieldMaterialLine>) => {
    setFieldMaterialsDraft((prev) =>
      prev.map((row) => (row.editorId === editorId ? { ...row, ...updates } : row)),
    );
  }, []);

  const handleAddFieldMaterialDraft = useCallback(() => {
    setFieldMaterialsDraft((prev) => [...prev, createEditableFieldMaterialLine()]);
  }, []);

  const handleRemoveFieldMaterialDraft = useCallback((editorId: string) => {
    setFieldMaterialsDraft((prev) => prev.filter((row) => row.editorId !== editorId));
    setActiveFieldMaterialMatcherId((current) => (current === editorId ? null : current));
  }, []);

  const handleAttachFieldMaterialReceipts = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    let skippedCount = 0;
    setFieldReceiptLibraryDraft((prev) => {
      const existingIdentities = new Set(prev.map((receipt) => getEditableFieldMaterialReceiptIdentity(receipt)));
      const nextReceipts: EditableFieldMaterialReceipt[] = [];
      Array.from(files).forEach((file) => {
        const candidate = {
          clientId: crypto.randomUUID(),
          file,
          fileName: file.name,
          mimeType: file.type,
          createdAt: new Date().toISOString(),
        } satisfies EditableFieldMaterialReceipt;
        const identity = getEditableFieldMaterialReceiptIdentity(candidate);
        if (existingIdentities.has(identity)) {
          skippedCount += 1;
          return;
        }
        existingIdentities.add(identity);
        nextReceipts.push(candidate);
      });
      return nextReceipts.length > 0 ? [...prev, ...nextReceipts] : prev;
    });
    if (skippedCount > 0) {
      toast.info("Duplicate receipt skipped", {
        description: `${skippedCount} receipt cop${skippedCount === 1 ? "y was" : "ies were"} already attached.`,
      });
    }
  }, []);

  const handleToggleFieldMaterialReceipt = useCallback((editorId: string, clientId: string) => {
    setFieldMaterialsDraft((prev) =>
      prev.map((row) => {
        if (row.editorId !== editorId) return row;
        const hasReceipt = row.receiptClientIds.includes(clientId);
        return {
          ...row,
          receiptClientIds: hasReceipt
            ? row.receiptClientIds.filter((value) => value !== clientId)
            : [...row.receiptClientIds, clientId],
        };
      }),
    );
  }, []);

  const handleRemoveFieldMaterialReceiptGroup = useCallback((clientIds: string[]) => {
    const clientIdSet = new Set(clientIds);
    setFieldReceiptLibraryDraft((prev) => prev.filter((receipt) => !clientIdSet.has(receipt.clientId)));
    setFieldMaterialsDraft((prev) =>
      prev.map((row) => ({
        ...row,
        receiptClientIds: row.receiptClientIds.filter((value) => !clientIdSet.has(value)),
      })),
    );
  }, []);

  const handleViewFieldMaterialReceipt = useCallback(async (attachmentId: string) => {
    const attachment = jobAttachmentById.get(attachmentId);
    const parsedPath = attachment ? parseStoredAttachmentPath(attachment.filePath) : null;
    if (!attachment || !parsedPath) {
      toast.error("Receipt preview unavailable");
      return;
    }
    try {
      const signedUrl = await createSignedAttachmentUrl(parsedPath.filePath, parsedPath.bucket);
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("[JobDetailCockpit] Failed to open field material receipt:", error);
      toast.error("Failed to open receipt");
    }
  }, [jobAttachmentById]);

  const uploadFieldMaterialReceipts = useCallback(async (
    receipts: EditableFieldMaterialReceipt[],
  ) => {
    if (!job) return [];

    const uniqueReceipts = Array.from(
      receipts.reduce((map, receipt) => {
        const identity = getEditableFieldMaterialReceiptIdentity(receipt);
        const existing = map.get(identity);
        map.set(identity, existing ? mergeEditableFieldMaterialReceipt(existing, receipt) : receipt);
        return map;
      }, new Map<string, EditableFieldMaterialReceipt>()).values(),
    );

    const uploadedReceipts: Array<{
      clientId: string;
      normalized: NonNullable<FieldMaterialItem["receipts"]>[number];
      editable: EditableFieldMaterialReceipt;
    }> = [];
    const newlyUploadedAttachments: Attachment[] = [];

    for (const receipt of uniqueReceipts) {
      if (receipt.file) {
        const attachment = await uploadAttachment(
          receipt.file,
          "job",
          job.id,
          "invoice",
          performedByLabel,
          "Field material receipt",
        );
        newlyUploadedAttachments.push(attachment);
        uploadedReceipts.push({
          clientId: receipt.clientId,
          normalized: {
            attachmentId: attachment.id,
            fileName: attachment.fileName,
            mimeType: attachment.mimeType,
            createdAt: attachment.createdAt,
            invoiceNumber: receipt.invoiceNumber,
            vendorName: receipt.vendorName,
            purchasedAt: receipt.purchasedAt,
            tax: receipt.tax,
            total: receipt.total,
          },
          editable: {
            clientId: receipt.clientId,
            attachmentId: attachment.id,
            fileName: attachment.fileName,
            mimeType: attachment.mimeType,
            createdAt: attachment.createdAt,
            invoiceNumber: receipt.invoiceNumber,
            vendorName: receipt.vendorName,
            purchasedAt: receipt.purchasedAt,
            tax: receipt.tax,
            total: receipt.total,
          },
        });
      } else if (receipt.attachmentId) {
        uploadedReceipts.push({
          clientId: receipt.clientId,
          normalized: {
            attachmentId: receipt.attachmentId,
            fileName: receipt.fileName,
            mimeType: receipt.mimeType,
            createdAt: receipt.createdAt,
            invoiceNumber: receipt.invoiceNumber,
            vendorName: receipt.vendorName,
            purchasedAt: receipt.purchasedAt,
            tax: receipt.tax,
            total: receipt.total,
          },
          editable: receipt,
        });
      }
    }

    if (newlyUploadedAttachments.length > 0) {
      setJobAttachments((prev) => {
        const existingIds = new Set(prev.map((attachment) => attachment.id));
        const merged = [...prev];
        newlyUploadedAttachments.forEach((attachment) => {
          if (!existingIds.has(attachment.id)) merged.push(attachment);
        });
        return merged;
      });
    }

    return uploadedReceipts;
  }, [job, performedByLabel]);

  const handleParseFieldMaterialReceipt = useCallback(async (clientId: string) => {
    if (!job || materialsEditorSaving || fieldMaterialPostingId || fieldReceiptParsingId) return;

    const receipt = fieldReceiptLibraryDraft.find((entry) => entry.clientId === clientId);
    if (!receipt) return;

    setFieldReceiptParsingId(clientId);
    try {
      const body: {
        organizationId?: string;
        attachmentId?: string;
        fileName?: string;
        mimeType?: string;
        fileBase64?: string;
      } = {
        organizationId: job.organizationId,
      };

      if (receipt.file) {
        body.fileName = receipt.file.name || receipt.fileName;
        body.mimeType = receipt.file.type || receipt.mimeType || "application/octet-stream";
        body.fileBase64 = await fileToBase64(receipt.file);
      } else if (receipt.attachmentId) {
        body.attachmentId = receipt.attachmentId;
      } else {
        throw new Error("Receipt file is not available for parsing.");
      }

      const { data, error } = await supabase.functions.invoke<ReceiptParseResponse>("receipt-parse", {
        body,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.detail || data.error);

      const parsedReceipt = data?.receipt;
      const enrichedReceiptCandidate = {
        ...receipt,
        invoiceNumber: String(parsedReceipt?.invoiceNumber ?? "").trim() || undefined,
        vendorName: String(parsedReceipt?.vendorName ?? "").trim() || undefined,
        purchasedAt: String(parsedReceipt?.purchasedAt ?? "").trim() || undefined,
        tax: Number.isFinite(Number(parsedReceipt?.tax)) ? Number(parsedReceipt?.tax) : receipt.tax,
        total: Number.isFinite(Number(parsedReceipt?.total)) ? Number(parsedReceipt?.total) : receipt.total,
      } satisfies EditableFieldMaterialReceipt;
      const parsedReceiptIdentity = getEditableFieldMaterialReceiptEventIdentity(enrichedReceiptCandidate);
      const canonicalReceipt =
        parsedReceiptIdentity
          ? fieldReceiptLibraryDraft.find((entry) =>
              entry.clientId !== clientId &&
              getEditableFieldMaterialReceiptEventIdentity(entry) === parsedReceiptIdentity,
            ) ?? null
          : null;
      const effectiveReceiptClientId = canonicalReceipt?.clientId ?? clientId;
      const parsedItems = Array.isArray(parsedReceipt?.items) ? parsedReceipt.items : [];

      if (parsedReceipt) {
        setFieldReceiptLibraryDraft((prev) => {
          const updated = prev.map((entry) =>
            entry.clientId === effectiveReceiptClientId
              ? {
                  ...entry,
                  invoiceNumber: enrichedReceiptCandidate.invoiceNumber ?? entry.invoiceNumber,
                  vendorName: enrichedReceiptCandidate.vendorName ?? entry.vendorName,
                  purchasedAt: enrichedReceiptCandidate.purchasedAt ?? entry.purchasedAt,
                  tax: enrichedReceiptCandidate.tax ?? entry.tax,
                  total: enrichedReceiptCandidate.total ?? entry.total,
                }
              : entry,
          );
          if (!canonicalReceipt) {
            return updated.map((entry) => (entry.clientId === clientId ? enrichedReceiptCandidate : entry));
          }
          return updated.filter((entry) => entry.clientId !== clientId);
        });

        if (canonicalReceipt) {
          setFieldMaterialsDraft((prev) =>
            prev.map((row) => ({
              ...row,
              receiptClientIds: row.receiptClientIds.includes(clientId)
                ? Array.from(new Set(row.receiptClientIds.map((value) => (value === clientId ? effectiveReceiptClientId : value))))
                : row.receiptClientIds,
            })),
          );
        }
      }

      if (parsedItems.length === 0) {
        toast.warning("No material items found on receipt", {
          description: "The receipt was read, but it did not produce any usable material lines.",
        });
        return;
      }

      const parsedItemSummaries = new Map<
        string,
        {
          itemName: string;
          sku: string | undefined;
          unitCost: number | undefined;
          quantity: number;
          notes: string | undefined;
        }
      >();
      const parseItemSignature = (itemName: string, sku: string | undefined, unitCost: number | undefined) =>
        `${itemName.toLowerCase()}|${normalizeMaterialSku(sku) ?? ""}|${typeof unitCost === "number" ? unitCost.toFixed(2) : ""}`;
      const mergeParsedNotes = (existing: string | undefined, next: string | undefined) => {
        const normalized = existing?.split(" • ").map((value) => value.trim()).filter(Boolean) ?? [];
        const nextText = String(next ?? "").trim();
        if (nextText && !normalized.some((value) => value.toLowerCase() === nextText.toLowerCase())) {
          normalized.push(nextText);
        }
        return normalized.length > 0 ? normalized.join(" • ") : undefined;
      };

      for (const item of parsedItems) {
        const itemName = String(item.itemName ?? "").trim();
        if (!itemName) continue;
        const sku = String(item.sku ?? "").trim() || undefined;
        const quantity = Math.max(1, Number(item.quantity) || 1);
        const unitCost = Number.isFinite(Number(item.unitCost)) ? Math.max(0, Number(item.unitCost)) : undefined;
        const parsedNotes = [item.notes, item.upc ? `UPC: ${item.upc}` : "", item.confidence > 0 && item.confidence < 0.9 ? `AI confidence ${(item.confidence * 100).toFixed(0)}%` : ""]
          .map((value) => String(value ?? "").trim())
          .filter(Boolean)
          .join(" • ") || undefined;
        const signature = parseItemSignature(itemName, sku, unitCost);
        const existingSummary = parsedItemSummaries.get(signature);
        if (existingSummary) {
          existingSummary.quantity += quantity;
          existingSummary.notes = mergeParsedNotes(existingSummary.notes, parsedNotes);
          if (!existingSummary.sku && sku) existingSummary.sku = sku;
          if (existingSummary.unitCost === undefined && unitCost !== undefined) {
            existingSummary.unitCost = unitCost;
          }
          continue;
        }
        parsedItemSummaries.set(signature, {
          itemName,
          sku,
          unitCost,
          quantity,
          notes: parsedNotes,
        });
      }

      let addedCount = 0;
      let mergedCount = 0;
      let updatedCount = 0;

      setFieldMaterialsDraft((prev) => {
        const enrichedRows = prev.map((row) => {
          const remappedReceiptClientIds = row.receiptClientIds.map((value) => (value === clientId ? effectiveReceiptClientId : value));
          if (!remappedReceiptClientIds.includes(effectiveReceiptClientId)) return row;
          const nextVendor = row.vendorName || parsedReceipt?.vendorName || undefined;
          const nextPurchasedAt = row.purchasedAt || parsedReceipt?.purchasedAt || undefined;
          const nextReceiptClientIds = Array.from(new Set(remappedReceiptClientIds));
          if (nextVendor !== row.vendorName || nextPurchasedAt !== row.purchasedAt || nextReceiptClientIds.length !== row.receiptClientIds.length) {
            updatedCount += 1;
            return {
              ...row,
              vendorName: nextVendor,
              purchasedAt: nextPurchasedAt,
              receiptClientIds: nextReceiptClientIds,
            };
          }
          return row;
        });

        const appendedRows: EditableFieldMaterialLine[] = [];

        for (const summary of parsedItemSummaries.values()) {
          const signature = parseItemSignature(summary.itemName, summary.sku, summary.unitCost);
          const matchIndex = enrichedRows.findIndex((row) =>
            row.receiptClientIds.includes(effectiveReceiptClientId)
              && parseItemSignature(row.itemName, row.sku, row.unitCost) === signature,
          );
          if (matchIndex >= 0) {
            const existingRow = enrichedRows[matchIndex];
            enrichedRows[matchIndex] = {
              ...existingRow,
              quantity: Math.max(1, Number(existingRow.quantity) || 1) + summary.quantity,
              notes: mergeParsedNotes(existingRow.notes, summary.notes),
            };
            mergedCount += 1;
            continue;
          }

          const nextRow = createEditableFieldMaterialLine({
            itemName: summary.itemName,
            sku: summary.sku,
            quantity: summary.quantity,
            unitCost: summary.unitCost,
            vendorName: parsedReceipt?.vendorName || undefined,
            purchasedAt: parsedReceipt?.purchasedAt || undefined,
            notes: summary.notes,
          });
          nextRow.receiptClientIds = [effectiveReceiptClientId];
          appendedRows.push(nextRow);
        }

        addedCount = appendedRows.length;
        return [...enrichedRows, ...appendedRows];
      });

      toast.success("Receipt parsed", {
        description:
          addedCount > 0 || mergedCount > 0
            ? `${addedCount > 0 ? `Added ${addedCount} new field material line${addedCount === 1 ? "" : "s"}` : ""}${mergedCount > 0 ? `${addedCount > 0 ? " and " : ""}Summarized ${mergedCount} similar parsed item${mergedCount === 1 ? "" : "s"}` : ""}${updatedCount > 0 ? `${addedCount > 0 || mergedCount > 0 ? " and " : ""}refreshed ${updatedCount} linked row${updatedCount === 1 ? "" : "s"}` : ""}${canonicalReceipt ? ". Duplicate receipt copy was consolidated." : ""}. Review before saving.`
            : updatedCount > 0
              ? `Refreshed ${updatedCount} linked row${updatedCount === 1 ? "" : "s"} from receipt metadata${canonicalReceipt ? " and consolidated a duplicate receipt copy" : ""}.`
              : "This receipt appears to have already been drafted into field materials.",
      });
    } catch (error) {
      console.error("[JobDetailCockpit] Failed to parse field material receipt:", error);
      toast.error("Receipt parsing failed", {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setFieldReceiptParsingId(null);
    }
  }, [fieldMaterialPostingId, fieldMaterialsDraft, fieldReceiptLibraryDraft, fieldReceiptParsingId, job, materialsEditorSaving]);

  const handlePostFieldMaterialToInventory = useCallback(async (editorId: string) => {
    if (!job || materialsEditorSaving || fieldMaterialPostingId) return;

    const currentDraft = fieldMaterialsDraft.find((row) => row.editorId === editorId);
    if (!currentDraft) return;

    const normalizedDraft = normalizeEditableFieldMaterialLineValues(currentDraft);
    if (!normalizedDraft.itemName) {
      toast.error("Field material name is required");
      return;
    }
    if (!normalizedDraft.sku) {
      toast.error("Match or enter a SKU first", {
        description: "Field materials can only post to inventory when they have a canonical SKU.",
      });
      return;
    }
    if (normalizedDraft.receiptClientIds.length === 0) {
      toast.error("Attach a receipt before posting", {
        description: "Store-bought materials need receipt proof before they can be added to inventory.",
      });
      return;
    }
    if (isFieldMaterialInventoryPosted(normalizedDraft)) {
      toast.message("This field material is already posted to inventory.");
      return;
    }

    setFieldMaterialPostingId(editorId);
    try {
      // Resolve the actual user's display name for traceability in Activity/Timeline
      const { data: authData } = await supabase.auth.getUser();
      const postedByName =
        (authData.user?.user_metadata?.full_name as string | undefined) ??
        authData.user?.email ??
        performedByLabel;

      let canonicalSku = normalizeMaterialSku(normalizedDraft.sku);
      if (!catalogProductBySku.has(canonicalSku)) {
        const ensured = await ProductRegistryService.ensureProductsForMaterialNames([
          {
            name: normalizedDraft.itemName,
            assignedSku: canonicalSku,
            vendorName: normalizedDraft.vendorName,
            unitCost: normalizedDraft.unitCost,
            unitPrice: normalizedDraft.unitCost,
            category: "Miscellaneous",
            subcategory: normalizedDraft.vendorName || "Field Purchase",
          },
        ]);

        const ensuredSku = normalizeMaterialSku(ensured.ensured[0]?.sku || canonicalSku);
        if (!ensuredSku) {
          throw new Error("Could not create Product Registry SKU for this field material.");
        }
        canonicalSku = ensuredSku;

        const refreshedCatalog = await ProductRegistryService.getProductsForSkuMatching();
        setCatalogProducts(refreshedCatalog.filter((product) => product.active && normalizeMaterialSku(product.sku)));

        toast.success("Product Registry SKU created", {
          description: `${canonicalSku} was created from this receipt item using the field-material unit cost for both cost and price.`,
        });
      }

      const selectedReceipts = fieldReceiptLibraryDraft.filter((receipt) => normalizedDraft.receiptClientIds.includes(receipt.clientId));
      const uploadedReceiptResults = await uploadFieldMaterialReceipts(selectedReceipts);
      const normalizedReceipts = uploadedReceiptResults.map((result) => result.normalized);
      const quantity = Math.max(1, Number(normalizedDraft.quantity) || 1);
      const sku = canonicalSku;
      const postedAt = new Date().toISOString();

      let fieldPurchaseLogId = normalizedDraft.fieldPurchaseLogId;
      if (!fieldPurchaseLogId) {
        const organizationId = await getCurrentOrganizationId().catch(() => null);
        if (organizationId) {
          const { data: purchaseRecord, error: purchaseError } = await supabase
            .from("field_purchases")
            .insert({
              organization_id: organizationId,
              job_id: job.id,
              vendor: normalizedDraft.vendorName ?? "Field purchase",
              amount: Number(((normalizedDraft.unitCost ?? 0) * quantity).toFixed(2)),
              reason: "other",
              notes: normalizedDraft.notes ?? null,
              purchased_by: performedByLabel,
              purchased_at: normalizedDraft.purchasedAt || postedAt,
              review_status: "pending",
            })
            .select("id")
            .maybeSingle();

          if (purchaseError) {
            console.warn("[JobDetailCockpit] Failed to log field purchase audit row:", purchaseError);
          } else {
            fieldPurchaseLogId = String(purchaseRecord?.id ?? "");
          }
        }
      }

      await Promise.all([
        syncProductRegistryStock(sku, { quantityOnHandDelta: quantity }),
        syncStockLevels(sku, { quantityOnHandDelta: quantity }),
        syncWarehouseStock(sku, { quantityOnHandDelta: quantity }),
      ]);

      // Auto-allocate field purchase directly as "Ready for Install" — no warehouse queue step needed.
      // The trigger bypass (allocated_by = 'Field Purchase') is applied in Supabase.
      {
        const jobLabel = job.customerName
          ? `${job.customerName}${job.projectType ? ` – ${job.projectType}` : ""}`
          : job.jobNumber || String(job.id);
        const { error: allocError } = await supabase
          .from("job_allocations")
          .insert({
            job_id: job.id,
            job_label: jobLabel,
            sku,
            product_name: normalizedDraft.itemName,
            quantity,
            status: "Ready for Install",
            allocated_at: postedAt,
            allocated_by: "Field Purchase",
          });
        if (!allocError) {
          await Promise.all([
            syncJobMaterialState(job.id, sku, {
              quantityAllocatedDelta: quantity,
              quantityStagedDelta: quantity,
              lifecycleStage: "Assigned",
              inventoryStatus: "Allocated",
            }),
            syncStockLevels(sku, { quantityReservedDelta: quantity }),
            syncWarehouseStock(sku, { quantityReservedDelta: quantity }),
          ]);
        } else {
          console.warn("[JobDetailCockpit] Failed to auto-allocate field purchase:", allocError);
        }
      }

      setFieldReceiptLibraryDraft((prev) =>
        prev.map((receipt) => {
          const uploaded = uploadedReceiptResults.find((result) => result.clientId === receipt.clientId);
          return uploaded ? uploaded.editable : receipt;
        }),
      );

      const existingFieldMaterials = Array.isArray(job.fieldMaterials) ? job.fieldMaterials : [];
      const postedRow: FieldMaterialItem = {
        id: normalizedDraft.id,
        itemName: normalizedDraft.itemName,
        sku,
        quantity,
        unitCost: normalizedDraft.unitCost,
        vendorName: normalizedDraft.vendorName,
        purchasedAt: normalizedDraft.purchasedAt,
        notes: normalizedDraft.notes,
        receipts: normalizedReceipts,
        fieldPurchaseLogId: fieldPurchaseLogId || undefined,
        inventoryPostedAt: postedAt,
        inventoryPostedBy: postedByName,
      };

      const nextFieldMaterials = [
        ...existingFieldMaterials.filter((material) => material.id !== postedRow.id),
        postedRow,
      ];

      const { data, error } = await JobService.update(job.id, {
        fieldMaterials: nextFieldMaterials,
      });
      if (error || !data) {
        throw error || new Error("Failed to save posted field material");
      }

      setJob(data);
      setFieldMaterialsDraft((prev) =>
        prev.map((row) =>
          row.editorId === editorId
            ? {
                ...normalizeEditableFieldMaterialLineValues(row),
                sku,
                fieldPurchaseLogId: fieldPurchaseLogId || undefined,
                inventoryPostedAt: postedAt,
                inventoryPostedBy: postedByName,
              }
            : row,
        ),
      );
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after posting field material:", err);
      });
      toast.success("Field material posted to inventory", {
        description: `${sku} is now on-hand inventory. This purchase stays out of the ordering queue and can be allocated later.`,
      });
    } catch (error) {
      console.error("[JobDetailCockpit] Failed to post field material to inventory:", error);
      toast.error("Failed to post field material to inventory");
    } finally {
      setFieldMaterialPostingId(null);
    }
  }, [catalogProductBySku, fieldMaterialPostingId, fieldMaterialsDraft, fieldReceiptLibraryDraft, job, materialsEditorSaving, performedByLabel, refreshData, setJob, uploadFieldMaterialReceipts]);

  const handleRemoveMaterialDraft = useCallback((editorId: string) => {
    setMaterialsEditorDraft((prev) => {
      const next = prev.filter((row) => row.editorId !== editorId);
      return next.length > 0 ? next : [createEditableMaterialLine()];
    });
    setActiveMaterialMatcherId((current) => (current === editorId ? null : current));
  }, []);

  const handleMatchMaterialSku = useCallback((editorId: string, product: RegistryProduct) => {
    handleUpdateMaterialDraft(editorId, {
      sku: normalizeMaterialSku(product.sku),
      itemName: product.productName,
      unitCost: product.cost,
      unitPrice: product.price,
    });
    setActiveMaterialMatcherId(null);
  }, [handleUpdateMaterialDraft]);

  const handleMatchFieldMaterialSku = useCallback((editorId: string, product: RegistryProduct) => {
    setFieldMaterialsDraft((prev) =>
      prev.map((row) => {
        if (row.editorId !== editorId) return row;
        return {
          ...row,
          sku: normalizeMaterialSku(product.sku),
          itemName: row.itemName.trim() || product.productName,
          unitCost: typeof row.unitCost === "number" ? row.unitCost : product.cost,
          vendorName: row.vendorName || product.vendor || undefined,
        };
      }),
    );
    setActiveFieldMaterialMatcherId(null);
  }, []);

  const handleReverseFieldMaterialPosting = useCallback(async (editorId: string) => {
    if (!job || materialsEditorSaving || fieldMaterialPostingId || fieldMaterialReversingId) return;

    const currentDraft = fieldMaterialsDraft.find((row) => row.editorId === editorId);
    if (!currentDraft) return;

    const normalizedDraft = normalizeEditableFieldMaterialLineValues(currentDraft);
    if (!isFieldMaterialInventoryPosted(normalizedDraft) || !normalizedDraft.sku) {
      toast.message("This field material has not been posted to inventory yet.");
      return;
    }

    const quantity = Math.max(1, Number(normalizedDraft.quantity) || 1);
    const confirmed = window.confirm(
      `Reverse inventory posting for ${normalizedDraft.sku}?\n\nThis will remove ${quantity} unit${quantity === 1 ? "" : "s"} from on-hand stock and flag the field purchase audit row for review.`,
    );
    if (!confirmed) return;

    setFieldMaterialReversingId(editorId);
    try {
      await Promise.all([
        syncProductRegistryStock(normalizedDraft.sku, { quantityOnHandDelta: -quantity }),
        syncStockLevels(normalizedDraft.sku, { quantityOnHandDelta: -quantity }),
        syncWarehouseStock(normalizedDraft.sku, { quantityOnHandDelta: -quantity }),
      ]);

      // Remove field purchase allocations for this job + SKU from warehouse
      const { data: fieldAllocations } = await supabase
        .from("job_allocations")
        .select("id")
        .eq("job_id", job.id)
        .eq("sku", normalizedDraft.sku)
        .eq("allocated_by", "Field Purchase");
      for (const alloc of fieldAllocations ?? []) {
        await WarehouseService.deleteJobAllocation(alloc.id);
      }

      if (normalizedDraft.fieldPurchaseLogId) {
        await supabase
          .from("field_purchases")
          .update({
            review_status: "flagged",
            reviewed_by: performedByLabel,
            reviewed_at: new Date().toISOString(),
          })
          .eq("id", normalizedDraft.fieldPurchaseLogId);
      }

      const existingFieldMaterials = Array.isArray(job.fieldMaterials) ? job.fieldMaterials : [];
      const nextFieldMaterials = existingFieldMaterials.map((material) =>
        material.id === normalizedDraft.id
          ? {
              ...material,
              inventoryPostedAt: undefined,
              inventoryPostedBy: undefined,
            }
          : material,
      );

      const { data, error } = await JobService.update(job.id, {
        fieldMaterials: nextFieldMaterials,
      });
      if (error || !data) {
        throw error || new Error("Failed to reverse field material posting");
      }

      setJob(data);
      setFieldMaterialsDraft((prev) =>
        prev.map((row) =>
          row.editorId === editorId
            ? {
                ...row,
                inventoryPostedAt: undefined,
                inventoryPostedBy: undefined,
              }
            : row,
        ),
      );
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after reversing field material posting:", err);
      });
      toast.success("Field material posting reversed", {
        description: `${normalizedDraft.sku} stock was backed out and the purchase audit row was flagged for review.`,
      });
    } catch (error) {
      console.error("[JobDetailCockpit] Failed to reverse field material posting:", error);
      toast.error("Failed to reverse field material posting");
    } finally {
      setFieldMaterialReversingId(null);
    }
  }, [fieldMaterialPostingId, fieldMaterialReversingId, fieldMaterialsDraft, job, materialsEditorSaving, performedByLabel, refreshData, setJob]);

  const handleSaveMaterialsEditor = useCallback(async () => {
    if (!job || materialsEditorSaving) return;

    const normalizedRows = materialsEditorDraft
      .map((row) => {
        const normalizedSku = normalizeMaterialSku(row.sku);
        const catalogMatch = catalogProductBySku.get(normalizedSku);
        const quantityRequired = Math.max(0, Number(row.quantityRequired) || 0);
        return {
          sku: normalizedSku,
          itemName: row.itemName.trim() || catalogMatch?.productName || normalizedSku,
          quantityRequired,
          quantityAllocated: Math.max(0, Number(row.quantityAllocated) || 0),
          quantityStaged: Math.max(0, Number(row.quantityStaged) || 0),
          quantityInstalled: Math.max(0, Number(row.quantityInstalled) || 0),
          missingItems: Math.max(0, Number(row.missingItems) || 0),
          fieldPurchase: Boolean(row.fieldPurchase),
          inventoryStatus: row.inventoryStatus,
          inventoryAssignment: row.inventoryAssignment,
          lifecycleStage: row.lifecycleStage,
          unitCost: Number.isFinite(row.unitCost) ? row.unitCost : catalogMatch?.cost,
          taxPercent: Number.isFinite(row.taxPercent) ? Math.max(0, row.taxPercent) : undefined,
          unitPrice: catalogMatch?.price ?? row.unitPrice,
          externalRef: row.externalRef,
          externalItemId: row.externalItemId,
        } satisfies MaterialLineItem;
      })
      .filter((row) => row.sku || row.itemName);

    const missingSkuRows = normalizedRows.filter((row) => !row.sku);
    if (missingSkuRows.length > 0) {
      toast.error("Every material needs a SKU", {
        description: "Use Match SKU before saving.",
      });
      return;
    }

    const duplicateSkus = normalizedRows.reduce<string[]>((dupes, row, index, rows) => {
      if (rows.findIndex((candidate) => candidate.sku === row.sku) !== index) dupes.push(row.sku);
      return dupes;
    }, []);
    if (duplicateSkus.length > 0) {
      toast.error("Duplicate material SKUs found", {
        description: `Each line should have a unique SKU. Duplicates: ${Array.from(new Set(duplicateSkus)).join(", ")}`,
      });
      return;
    }

    const unmappedSkusForReview = normalizedRows
      .filter((row) => !catalogProductBySku.has(normalizeMaterialSku(row.sku)))
      .map((row) => ({
        sku: row.sku,
        itemName: row.itemName,
        proposedSku: row.sku,
      }));

    const normalizedFieldMaterialsDraft = fieldMaterialsDraft
      .map((row) => normalizeEditableFieldMaterialLineValues(row))
      .filter((row) => row.itemName || row.sku || row.receiptClientIds.length > 0);

    const normalizedReceiptByClientIdDraft = new Map(
      fieldReceiptLibraryDraft.map((receipt) => [receipt.clientId, receipt] as const),
    );
    const fieldSalesTaxTotal = Array.from(
      new Set(normalizedFieldMaterialsDraft.flatMap((row) => row.receiptClientIds)),
    ).reduce((sum, clientId) => {
      const receipt = normalizedReceiptByClientIdDraft.get(clientId);
      return sum + (typeof receipt?.tax === "number" ? Math.max(0, receipt.tax) : 0);
    }, 0);

    const estimatedMaterialCost = Number(
      (
        normalizedRows.reduce((sum, row) => sum + getCoreMaterialLineTotal(row), 0) +
        normalizedFieldMaterialsDraft.reduce((sum, row) => sum + getFieldMaterialLineSubtotal(row), 0) +
        fieldSalesTaxTotal
      ).toFixed(2),
    );

    const invalidFieldMaterial = normalizedFieldMaterialsDraft.find((row) => !row.itemName);
    if (invalidFieldMaterial) {
      toast.error("Field materials need a name", {
        description: "Add a material name for every field-purchased line before saving.",
      });
      return;
    }

    // Snapshot old materials before saving for diff computation
    const prevMaterialsBySku = new Map(
      authoritativeJobMaterials.map((m) => [m.sku, m] as const),
    );

    setMaterialsEditorSaving(true);
    try {
      const uploadedReceiptResults = await uploadFieldMaterialReceipts(fieldReceiptLibraryDraft);
      const normalizedReceiptByClientId = new Map(
        uploadedReceiptResults.map((result) => [result.clientId, result.normalized] as const),
      );
      const normalizedFieldMaterials: FieldMaterialItem[] = [];
      for (const row of normalizedFieldMaterialsDraft) {
        normalizedFieldMaterials.push({
          id: row.id,
          itemName: row.itemName,
          sku: row.sku,
          quantity: Math.max(1, Number(row.quantity) || 1),
          unitCost: typeof row.unitCost === "number" ? row.unitCost : undefined,
          vendorName: row.vendorName,
          purchasedAt: row.purchasedAt,
          notes: row.notes,
          receipts: row.receiptClientIds
            .map((clientId) => normalizedReceiptByClientId.get(clientId))
            .filter((receipt): receipt is NonNullable<FieldMaterialItem["receipts"]>[number] => Boolean(receipt)),
          fieldPurchaseLogId: row.fieldPurchaseLogId,
          inventoryPostedAt: row.inventoryPostedAt,
          inventoryPostedBy: row.inventoryPostedBy,
        });
      }

      const { data, error } = await JobService.update(job.id, {
        materialsList: normalizedRows,
        fieldMaterials: normalizedFieldMaterials,
        unmappedSkusForReview,
        financials: {
          ...(job.financials ?? {
            contractValue: 0,
            estimatedMaterialCost: 0,
            estimatedLaborCost: 0,
            subcontractorPayout: 0,
            marginEstimate: 0,
            paymentStatus: "Not Invoiced" as const,
            payoutStatus: "Not Submitted" as const,
          }),
          estimatedMaterialCost,
        },
      });
      if (error || !data) {
        throw error || new Error("Failed to update materials");
      }
      setFieldReceiptLibraryDraft(uploadedReceiptResults.map((result) => result.editable));
      setJob(data);
      setMaterialsEditorOpen(false);
      await refetchJobMaterials();

      // Build and write materials audit diff
      const newSkuSet = new Set(normalizedRows.map((r) => r.sku));
      const diff: MaterialsAuditDiff = {
        added: normalizedRows
          .filter((r) => !prevMaterialsBySku.has(r.sku))
          .map((r) => ({ sku: r.sku, name: r.itemName, qty: r.quantityRequired })),
        removed: authoritativeJobMaterials
          .filter((m) => !newSkuSet.has(m.sku))
          .map((m) => ({ sku: m.sku, name: m.itemName })),
        changed: normalizedRows
          .filter((r) => {
            const prev = prevMaterialsBySku.get(r.sku);
            return prev && prev.quantityRequired !== r.quantityRequired;
          })
          .map((r) => ({
            sku: r.sku,
            name: r.itemName,
            oldQty: prevMaterialsBySku.get(r.sku)!.quantityRequired,
            newQty: r.quantityRequired,
          })),
      };
      if (diff.added.length > 0 || diff.removed.length > 0 || diff.changed.length > 0) {
        const orgId = data.organizationId ?? "";
        await writeJobMaterialsAuditEntry(job.id, orgId, diff);
        setAuditRefreshToken((t) => t + 1);
      }

      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after materials update:", err);
      });
      toast.success("Job materials updated", {
        description: unmappedSkusForReview.length > 0
          ? `${unmappedSkusForReview.length} SKU${unmappedSkusForReview.length === 1 ? "" : "s"} still need registry matching.`
          : normalizedFieldMaterials.length > 0
            ? `Saved ${normalizedFieldMaterials.length} field material purchase${normalizedFieldMaterials.length === 1 ? "" : "s"} separately from pre-ordered materials.`
            : "All materials are matched to catalog SKUs.",
      });
    } catch (error) {
      console.error("[JobDetailCockpit] Failed to save job materials:", error);
      toast.error("Failed to save materials");
    } finally {
      setMaterialsEditorSaving(false);
    }
  }, [catalogProductBySku, fieldMaterialsDraft, fieldReceiptLibraryDraft, job, materialsEditorDraft, materialsEditorSaving, refetchJobMaterials, refreshData, setJob, uploadFieldMaterialReceipts]);

  const handleSaveJobDetails = useCallback(async (patch: JobEditPatch) => {
    if (!job) return;
    const { data, error } = await JobService.update(job.id, patch);
    if (error || !data) {
      console.error("[JobDetailCockpit] Failed to save job details:", error);
      toast.error("Failed to save job details");
      throw error || new Error("Failed to save job details");
    }
    setJob(data);
    const refreshedOffers = await refreshInstallerOpportunityProjection(data).catch((err) => {
      console.warn("[JobDetailCockpit] offering projection refresh failed after job edit:", err);
      toast.warning("Job updated, but Offering sync failed", {
        description: "Published installer offers may still show old details until the portal projection is refreshed.",
      });
      return null;
    });
    await refreshData().catch((err) => {
      console.warn("[JobDetailCockpit] refreshData failed after job edit:", err);
    });
    if (typeof refreshedOffers === "number" && refreshedOffers > 0) {
      toast.success("Job details updated", {
        description: `Synced ${refreshedOffers} published Offering${refreshedOffers === 1 ? "" : "s"}.`,
      });
    } else {
      toast.success("Job details updated");
    }
  }, [job, refreshData, setJob]);

  const [restoringOrderingGate, setRestoringOrderingGate] = useState(false);

  const handleRestoreNativeMaterialsFlow = useCallback(async () => {
    if (!job?.sourceMeta || restoringOrderingGate) return;
    const nextBypassedStages = (job.sourceMeta.bypassedStages ?? ["build_review", "permitting", "ordering"])
      .filter((stage) => stage !== "ordering");
    const boundJobSpec =
      !job.boundJobSpec && !isExternalManagedImport(job)
        ? deriveBoundSpecForJob(job)
        : job.boundJobSpec;

    setRestoringOrderingGate(true);
    try {
      const { data, error } = await JobService.update(job.id, {
        sourceMeta: {
          ...job.sourceMeta,
          bypassedStages: nextBypassedStages,
        },
        ...(boundJobSpec ? { boundJobSpec } : {}),
      });
      if (error || !data) {
        console.error("[JobDetailCockpit] Failed to restore native materials flow:", error);
        toast.error("Failed to restore native materials flow");
        return;
      }
      setJob(data);
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after restoring native materials flow:", err);
      });
      if (!job.boundJobSpec && !boundJobSpec && !isExternalManagedImport(job)) {
        toast.warning("Materials returned to native flow", {
          description: "Bound spec could not be derived automatically from this job yet.",
        });
      } else {
        toast.success("Materials returned to native flow");
      }
    } finally {
      setRestoringOrderingGate(false);
    }
  }, [job, refreshData, restoringOrderingGate, setJob]);

  const [activatingPermitting, setActivatingPermitting] = useState(false);

  const handleActivatePermitting = useCallback(async () => {
    if (!job?.sourceMeta || activatingPermitting) return;
    const nextBypassedStages = (job.sourceMeta.bypassedStages ?? ["build_review", "permitting", "ordering"])
      .filter((stage) => stage !== "permitting");
    setActivatingPermitting(true);
    try {
      const { data, error } = await JobService.update(job.id, {
        sourceMeta: { ...job.sourceMeta, bypassedStages: nextBypassedStages },
        permitRequired: true,
      });
      if (error || !data) {
        console.error("[JobDetailCockpit] Failed to activate permitting:", error);
        toast.error("Failed to activate permitting");
        return;
      }
      setJob(data);
      await refreshData().catch((err) => {
        console.warn("[JobDetailCockpit] refreshData failed after activating permitting:", err);
      });
      toast.success("Permitting activated for this job");
    } finally {
      setActivatingPermitting(false);
    }
  }, [job, refreshData, activatingPermitting, setJob]);

  const credits = useMemo(
    () => job
      ? scopedCredits.map((credit) =>
          credit.jobId === job.jobNumber ? { ...credit, jobId: job.id } : credit,
        )
      : scopedCredits,
    [job, scopedCredits],
  );

  // Only run the check when the registry has actually loaded.
  // ProductRegistryContext is deferred on this route so registryProducts starts as [].
  // An empty registry would incorrectly flag every SKU as unmatched.
  const unmatchedSkus = useMemo(
    () => registryProducts.length > 0 ? getUnmatchedSkus(job?.materialsList, registryProducts) : [],
    [job?.materialsList, registryProducts],
  );
  const hasUnmatchedSkus = unmatchedSkus.length > 0;

  if (loading) {
    return (
      <CockpitPage>
        <div className="p-12 text-center text-muted-foreground">Loading…</div>
      </CockpitPage>
    );
  }

  if (!job || !derived) {
    return (
      <CockpitPage>
        <div className="p-12 text-center text-muted-foreground">Job not found.</div>
      </CockpitPage>
    );
  }

  const health = getJobHealth(job, openIssues, credits);
  const hasBlocker = blockers.some((b) => b.severity === "critical");
  const nextAction = getJobNextAction(job, openIssues);
  const suggested = getJobSuggestedActions(job, openIssues);
  const buildReviewBypassed = migrationBypasses(job, "build_review");
  const permittingBypassed = migrationBypasses(job, "permitting");
  const orderingBypassed = migrationBypasses(job, "ordering");
  const displayStage = deriveEffectiveLifecycleStage(job);
  const materialIsReady =
    orderingBypassed || ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(job.materialReadiness);
  const materialKpiValue = orderingBypassed ? "Ordering Bypassed" : (job.materialReadiness || "—");
  const displayStateLabel = isExternalManaged
    ? (getStageConfig(displayStage)?.label ?? displayStage)
    : derived.label;
  const lastAct = getLastActivity(job);
  const inRescission = isInRescission(job.rescissionEndsAt);
  const collection = getCollectionSummary(job);
  const financeApplicationPaymentDisplay = getFinancingApplicationPaymentDisplay(job);
  const paymentKpiLabel: Record<string, string> = {
    no_invoice_plan: "No Invoice Plan",
    deposit_pending: "Deposit Pending",
    deposit_paid: "Deposit Received",
    progress_pending: "Progress Pending",
    final_pending: "Final Pending",
    paid_in_full: "Paid in Full",
    overdue: "Overdue",
  };
  const paymentKpiTone =
    collection.paymentStatus === "paid_in_full"
      ? ("good" as const)
      : collection.paymentStatus === "overdue"
        ? ("bad" as const)
        : collection.paymentStatus === "no_invoice_plan"
          ? ("default" as const)
          : ("warn" as const);
  const resolvedPaymentKpiValue = financeApplicationPaymentDisplay?.label
    ?? paymentKpiLabel[collection.paymentStatus]
    ?? titleCase(derived.payment);
  const resolvedPaymentKpiTone = financeApplicationPaymentDisplay?.kpiTone ?? paymentKpiTone;

  // KPI strip — five canonical execution gates
  const kpis = [
    {
      label: "Permit",
      value: permittingBypassed ? "Bypassed" : !job.permitRequired ? "N/A" : (job.permitStatus || "—"),
      tone: permittingBypassed || !job.permitRequired ? "default" as const : job.permitStatus === "Approved" ? "good" as const : "warn" as const,
    },
    {
      label: "Materials",
      value: materialKpiValue,
      tone: materialIsReady ? "good" as const : "warn" as const,
    },
    {
      label: "Schedule",
      value: job.scheduledInstallDate
        ? formatDispatchDateRange(job.scheduledInstallDate, job.scheduledStartTime, job.scheduledEndTime, job.targetInstallDate)
        : "TBD",
      tone: job.scheduledInstallDate ? "good" as const : "warn" as const,
    },
    {
      label: "QA",
      value: job.qaSubmitted ? "Submitted" : "Pending",
      tone: job.qaSubmitted && openIssues.length === 0 ? "good" as const : job.qaSubmitted ? "warn" as const : "default" as const,
    },
    {
      label: "Payment",
      value: resolvedPaymentKpiValue,
      tone: resolvedPaymentKpiTone,
    },
  ];

  const heroRight = (
    <>
      <HeroMetric label="State" value={displayStateLabel} tone={hasBlocker ? "bad" : derived.canProgress ? "good" : "warn"} />
      {fin$.viewContractValue && (
        <>
          <HeroDivider />
          <HeroMetric label={fin$.label("Contract")} value={`$${getJobDisplayValue(job).toLocaleString()}`} hero tone="default" />
        </>
      )}
      {!fin$.viewContractValue && fin$.viewCrewPayout && job.financials?.subcontractorPayout && (
        <>
          <HeroDivider />
          <HeroMetric label="Install Payout" value={`$${job.financials.subcontractorPayout.toLocaleString()}`} hero tone="default" />
        </>
      )}
    </>
  );

  return (
    <CockpitPage>
      {/* Critical pills inline above masthead */}
      <div className="flex items-center justify-between gap-2 -mb-1">
        <div />
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          <StatusBadge type="stage" value={displayStage} />
          {hasBlocker && (
            <Badge variant="outline" className="text-[10px] border-destructive/30 text-destructive bg-destructive/5 gap-1">
              <OctagonAlert className="h-3 w-3" /> BLOCKED
            </Badge>
          )}
          {!hasBlocker && health.status === "At Risk" && (
            <Badge variant="outline" className="text-[10px] border-warning/30 text-warning bg-warning/5 gap-1">
              <AlertTriangle className="h-3 w-3" /> AT RISK
            </Badge>
          )}
          {inRescission && (
            <Badge variant="outline" className="text-[10px] border-warning/30 text-warning bg-warning/5 gap-1">
              <Shield className="h-3 w-3" /> RESCISSION · {getRescissionDaysRemaining(job.rescissionEndsAt)}d
            </Badge>
          )}
          <Link to={`/portal/${job.id}`} target="_blank">
            <Button variant="outline" size="sm" className="h-7 gap-1 text-[11px]">
              <ExternalLink className="h-3 w-3" /> Customer view
            </Button>
          </Link>
          <Button variant="outline" size="sm" className="h-7 gap-1 text-[11px]" onClick={() => setContractSheetOpen(true)}>
            <FileText className="h-3 w-3" /> Contract
          </Button>
        </div>
      </div>

      <Sheet open={contractSheetOpen} onOpenChange={setContractSheetOpen}>
        <SheetContent side="right" className="w-80 sm:w-96 overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-sm font-semibold">Contract Document</SheetTitle>
          </SheetHeader>
          <ContractDocumentView contract={job.contractDocument} />
        </SheetContent>
      </Sheet>

      <CockpitMasthead
        eyebrow={`${job.jobNumber || job.id} · ${job.projectType || "Project Type Missing"} · ${job.branch} · ${job.region} · Rep: ${displayIdentity(job.salesRep, "Sales Rep")}`}
        title={
          <Link to={`/customers/${encodeURIComponent(job.customerName)}`} className="hover:text-primary transition-colors">
            {job.customerName}
          </Link>
        }
        right={heroRight}
      />

      {/* Compact contact line */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap -mt-1">
        <span className="inline-flex items-center gap-1 rounded-full border bg-muted/20 px-2 py-0.5 text-foreground">
          <Wrench className="h-3.5 w-3.5 text-primary" />{job.projectType || "Project Type Missing"}
        </span>
        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{formatAddress(job.address)}</span>
        <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{job.phone}</span>
        {job.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{job.email}</span>}
      </div>

      <CockpitKpiStrip items={kpis} />

      {/* Rescission banner — only when active */}
      {inRescission && (
        <div className="rounded-lg border border-warning/30 bg-warning/5 px-4 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <Shield className="h-4 w-4 text-warning shrink-0" />
            <p className="text-xs text-foreground">
              <span className="font-semibold text-warning">Rescission active</span>
              <span className="text-muted-foreground"> · Material commits on hold until {formatRescissionEnd(job.rescissionEndsAt)}</span>
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="min-h-10 h-auto bg-transparent border-b w-full justify-start rounded-none p-0 gap-1 flex-wrap">
            {[
              { v: "overview", l: "Overview" },
              { v: "activity", l: "Activity" },
              { v: "images", l: "Images" },
              { v: "quality", l: "Execution Integrity" },
              { v: "financials", l: "Financials" },
              { v: "timeline", l: "Timeline" },
              { v: "documents", l: "Documents" },
            ].map((t) => (
            <TabsTrigger
              key={t.v}
              value={t.v}
              className="rounded-md border border-transparent data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:ring-1 data-[state=active]:ring-primary/30 data-[state=active]:shadow-none px-3 h-8 text-xs font-medium transition-colors whitespace-nowrap"
            >
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ───── OVERVIEW ───── */}
        <TabsContent value="overview" className="mt-5 space-y-5">
          {/* Lifecycle Tracker — full job stage progression */}
          <Card className="shadow-none">
            <CardContent className="p-3">
              <SectionEyebrow>Lifecycle Progress</SectionEyebrow>
              <div className="mt-1">
                <LifecycleTracker steps={(() => {
                  const HIDDEN = new Set(["Review Complete", "Ready for Ordering", "Permit and Order Pending", "Scheduled Tentative", "Ready for Dispatch", "Available for Contractor Assignment", "Assigned and Dispatched"]);
                  const displayStages = getOrderedStages(true)
                    .filter((s) => s.id !== "Issue")
                    .filter((s) => !HIDDEN.has(s.id));
                  const currentConfig = getStageConfig(displayStage);
                  const currentOrder = currentConfig?.order ?? 0;
                  const currentVisible = displayStages.some((s) => s.id === displayStage);
                  let fallbackId: string | null = null;
                  if (!currentVisible) {
                    // If the live stage is non-canonical (for example "Pre-Install"), still
                    // anchor the tracker to the nearest visible stage so one node is always active.
                    const upstream = currentConfig
                      ? [...displayStages].filter((s) => s.order <= currentOrder).pop()
                      : undefined;
                    fallbackId = (upstream ?? displayStages[0])?.id ?? null;
                  }
                  return displayStages.map((s) => {
                    const isActiveExact = s.id === displayStage;
                    const isActiveFallback = !currentVisible && s.id === fallbackId;
                    const completed = currentVisible
                      ? (s.order < currentOrder || displayStage === "Completed")
                      : (s.order < currentOrder && s.id !== fallbackId);
                    const baseLabel =
                      buildReviewBypassed && s.id === "Review and Validation"
                        ? "Build Review Bypassed"
                        : orderingBypassed && s.id === "Ordered"
                          ? "Ordering Bypassed"
                          : s.label;
                    const label = isActiveFallback
                      ? `${baseLabel} · (currently ${currentConfig?.label ?? displayStage})`
                      : baseLabel;
                    return { label, completed, active: isActiveExact || isActiveFallback };
                  });
                })()} />
              </div>
            </CardContent>
          </Card>

          <ScopeDetailsCard
            job={job}
            canEdit={canEditJob}
            onEdit={() => setJobEditOpen(true)}
            onManageMaterials={handleOpenMaterialsEditor}
          />

          {job.migrationImportDetail && (
            <Card className="shadow-none">
              <CardContent className="p-3 space-y-2">
                <SectionEyebrow>Migration Import Details</SectionEyebrow>
                <div className="grid gap-2 md:grid-cols-2 text-xs">
                  {job.migrationImportDetail.contractor && (
                    <div><span className="text-muted-foreground">Contractor:</span> <span className="font-medium">{job.migrationImportDetail.contractor}</span></div>
                  )}
                  {job.migrationImportDetail.projectSubtype && (
                    <div><span className="text-muted-foreground">Subtype:</span> <span className="font-medium">{job.migrationImportDetail.projectSubtype}</span></div>
                  )}
                  {job.migrationImportDetail.showerSystem?.type && (
                    <div className="md:col-span-2">
                      <span className="text-muted-foreground">Shower System:</span>{" "}
                      <span className="font-medium">{job.migrationImportDetail.showerSystem.type}</span>
                      {job.migrationImportDetail.showerSystem.pan && (
                        <span className="text-muted-foreground">
                          {" "}· Pan: {job.migrationImportDetail.showerSystem.pan.size} {job.migrationImportDetail.showerSystem.pan.color} · Drain: {job.migrationImportDetail.showerSystem.pan.drain}
                        </span>
                      )}
                      {job.migrationImportDetail.showerSystem.walls && (
                        <span className="text-muted-foreground">
                          {" "}· Walls: {job.migrationImportDetail.showerSystem.walls.material} {job.migrationImportDetail.showerSystem.walls.color} {job.migrationImportDetail.showerSystem.walls.height}
                        </span>
                      )}
                    </div>
                  )}
                  {job.migrationImportDetail.installation && (
                    <div className="md:col-span-2">
                      <span className="text-muted-foreground">Installation:</span>{" "}
                      {job.migrationImportDetail.installation.removal && <span>Removal Bath {job.migrationImportDetail.installation.removal.bathroom}</span>}
                      {job.migrationImportDetail.installation.thresholds && <span> · Thresholds Bath {job.migrationImportDetail.installation.thresholds.bathroom}</span>}
                      {job.migrationImportDetail.installation.new_installation && <span> · New Install Bath {job.migrationImportDetail.installation.new_installation.bathroom}</span>}
                    </div>
                  )}
                  {job.migrationImportDetail.plumbing && (
                    <div className="md:col-span-2">
                      <span className="text-muted-foreground">Plumbing:</span>{" "}
                      <span className="font-medium">{job.migrationImportDetail.plumbing.items?.join(", ") ?? ""}</span>
                      {job.migrationImportDetail.plumbing.notes && <span className="text-muted-foreground"> — {job.migrationImportDetail.plumbing.notes}</span>}
                    </div>
                  )}
                  {job.migrationImportDetail.doors && (
                    <div className="md:col-span-2">
                      <span className="text-muted-foreground">Doors:</span>{" "}
                      {job.migrationImportDetail.doors.bathroom_1 && <span>Bath 1: {job.migrationImportDetail.doors.bathroom_1.type} {job.migrationImportDetail.doors.bathroom_1.glass} {job.migrationImportDetail.doors.bathroom_1.finish}</span>}
                      {job.migrationImportDetail.doors.bathroom_2 && <span> · Bath 2: {job.migrationImportDetail.doors.bathroom_2.type} {job.migrationImportDetail.doors.bathroom_2.glass} {job.migrationImportDetail.doors.bathroom_2.finish}</span>}
                      {job.migrationImportDetail.doors.window_kit && <span> · Window Kit: {job.migrationImportDetail.doors.window_kit.type} {job.migrationImportDetail.doors.window_kit.color}</span>}
                    </div>
                  )}
                  {job.migrationImportDetail.fixtures && (
                    <div><span className="text-muted-foreground">Fixtures:</span> <span className="font-medium">{job.migrationImportDetail.fixtures.type}</span> {job.migrationImportDetail.fixtures.finish && <span className="text-muted-foreground">· {job.migrationImportDetail.fixtures.finish}</span>}</div>
                  )}
                  {job.migrationImportDetail.vanity && (
                    <div><span className="text-muted-foreground">Vanity:</span> <span className="font-medium">{job.migrationImportDetail.vanity.type} {job.migrationImportDetail.vanity.size}</span> <span className="text-muted-foreground">· {job.migrationImportDetail.vanity.style}</span></div>
                  )}
                  {job.migrationImportDetail.toilet && (
                    <div><span className="text-muted-foreground">Toilet:</span> <span className="font-medium">{job.migrationImportDetail.toilet.type}</span> <span className="text-muted-foreground">· {job.migrationImportDetail.toilet.height} · {job.migrationImportDetail.toilet.rough_in} · {job.migrationImportDetail.toilet.finish}</span></div>
                  )}
                  {job.migrationImportDetail.flooring && (
                    <div><span className="text-muted-foreground">Flooring:</span> <span className="font-medium">{job.migrationImportDetail.flooring.type} {job.migrationImportDetail.flooring.color}</span> <span className="text-muted-foreground">· {job.migrationImportDetail.flooring.sqft} sq ft</span></div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <LeadAttributionCard
            attribution={leadAttribution}
            canEdit={canEditJob}
            onChange={() => setLeadAttributionOpen(true)}
          />

          {/* Action Required — single panel merging blockers + suggested actions + next step */}
          <ActionRequiredPanel
            blockers={blockers}
            suggested={suggested}
            nextAction={nextAction}
            lastAct={lastAct}
            hasBlocker={hasBlocker}
            onActionClick={handleActionClick}
          />

          {/* 3-col Operational strip */}
          <div>
            <SectionEyebrow>Operations</SectionEyebrow>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
              <ExecutionCard
                icon={Shield}
                label="Permit"
                rightLink={{ to: "/permitting", label: "Tracker" }}
                rows={[
                  ["Required", job.permitRequired ? "Yes" : "No"],
                  ["Status", permittingBypassed ? "Permitting Bypassed" : <StatusBadge type="permit" value={job.permitRequired ? job.permitStatus : "Not Required"} />],
                  job.permitDetail && ["Type", job.permitDetail.permitType] as [string, React.ReactNode],
                  job.permitDetail && ["Municipality", job.permitDetail.municipality] as [string, React.ReactNode],
                ].filter(Boolean) as Array<[string, React.ReactNode]>}
                cta={!permittingBypassed && job.permitRequired && job.permitStatus !== "Approved" ? {
                  to: "/permitting",
                  label: job.permitStatus === "Revision Needed" ? "Address Revision" : job.permitStatus === "Submitted" ? "Follow Up" : "Submit Permit",
                } : undefined}
                footer={permittingBypassed ? (
                  <button
                    type="button"
                    onClick={handleActivatePermitting}
                    disabled={activatingPermitting}
                    className="w-full mt-2 rounded px-2 py-1.5 text-[11px] font-medium border border-dashed border-muted-foreground/40 text-muted-foreground hover:border-foreground/40 hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    {activatingPermitting ? "Activating…" : "Activate Permitting"}
                  </button>
                ) : undefined}
              />
              <MaterialsGateCard
                job={job}
                purchaseOrders={jobPurchaseOrders}
                jobAllocations={jobAllocations}
                jobIssues={jobIssues}
                jobMaterials={authoritativeJobMaterials}
                orderingBypassed={orderingBypassed}
                onRefetch={handleMaterialsRefetch}
              />
              <ExecutionCard
                icon={Truck}
                label="Schedule & Dispatch"
                rightLink={{ to: `/scheduling-dispatch?lens=plan&job=${encodeURIComponent(job.id)}`, label: "Calendar" }}
                rows={[
                  ["Install Date", job.scheduledInstallDate ? formatDispatchDateRange(job.scheduledInstallDate, job.scheduledStartTime, job.scheduledEndTime, job.targetInstallDate) : "TBD"],
                  ["Time Slot", formatDispatchTimeSlot(job.timeSlot, job.scheduledStartTime, job.scheduledEndTime)],
                  ["Assigned", job.assignmentType === "Subcontractor" ? displayIdentity(job.assignedSubcontractor, "Pending") : displayIdentity(job.assignedCrew, "Unassigned")],
                  ["Dispatch", <StatusBadge type="dispatch" value={job.dispatchStatus} />],
                ]}
                cta={(!job.scheduledInstallDate || job.assignmentType === "Unassigned") ? {
                  to: `/scheduling-dispatch?lens=plan&job=${encodeURIComponent(job.id)}`,
                  label: job.assignmentType === "Unassigned" ? "Assign Crew" : "Schedule Install",
                } : undefined}
                ctaPermission="scheduling_edit"
                footer={<>
                  {subAssignments.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/40 space-y-1.5">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Sub-Contractors</p>
                      {subAssignments.map((sa) => (
                        <div key={sa.id} className="flex items-center justify-between gap-2 text-[11px]">
                          <span className="font-medium truncate">{sa.subContractorCompany ?? sa.subContractorName}</span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {sa.scope && <span className="text-muted-foreground truncate max-w-[80px]">{sa.scope}</span>}
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              sa.status === "active" ? "bg-good/10 text-good" :
                              sa.status === "complete" ? "bg-muted text-muted-foreground" :
                              "bg-warn/10 text-warn"
                            }`}>{sa.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {conflicts.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {conflicts.map((c, i) => (
                        <div key={i} className={`flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-[11px] ${
                          c.type === "crew_overlap" ? "border-destructive/30 bg-destructive/5 text-destructive" : "border-warning/30 bg-warning/5 text-warning"
                        }`}>
                          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                          <div>
                            <p className="font-semibold">{c.label}</p>
                            <p className="opacity-80">{c.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>}
              />
            </div>
          </div>

          {/* Two-stage Prep → Push order surface (only while pre-order) */}
          {!orderingBypassed && job.materialReadiness === "Not Ordered" && (
            <OrderPrepCard jobId={job.id} />
          )}

          {/* Materials / BOM — read-only canonical view */}
          <JobBomPanel
            jobId={job.id}
            bound={job.boundJobSpec}
            job={job}
            materialKit={jobMaterialKit}
            purchaseOrders={jobPurchaseOrders}
            jobAllocations={jobAllocations}
            dbMaterials={authoritativeJobMaterials}
          />

          {/* Compact financial summary — Overview only, gated */}
          {fin$.viewFinancialSummary && <FinancialSummaryStrip job={job} openIssues={openIssues} credits={credits} invoiceRefs={invoiceRefs} onInvoiceClick={() => setTab("financials")} />}
        </TabsContent>

        {/* ───── ACTIVITY ───── */}
        <TabsContent value="activity" className="mt-5">
          <Card className="shadow-none">
            <CardContent className="p-4">
              <SectionEyebrow>Activity Log</SectionEyebrow>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-4">
                All changes to this job — who made them, when, and what was updated.
              </p>
              <JobAuditTimeline jobId={job.id} job={job} purchaseOrders={jobPurchaseOrders} jobAllocations={jobAllocations} fieldMaterials={job.fieldMaterials ?? []} linkedLead={pipelineLead} appointmentResults={pipelineAppts} consultations={pipelineConsultations} buildReviewEvents={buildReviewEvents.filter((e) => e.jobId === job.id)} permitDetail={job.permitDetail} schedulingEvents={schedulingEvents} installerOffers={installerOffers} phaseExecutions={phaseExecutions} jobCredits={scopedCredits} jobIssues={jobIssues} serviceTickets={serviceTickets} refreshToken={auditRefreshToken} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ───── IMAGES ───── */}
        <TabsContent value="images" className="mt-5 space-y-5">
          <JobPhotoGallery
            job={job}
            linkedQuoteId={linkedQuoteId}
            allIssues={jobIssues}
          />
        </TabsContent>

        {/* ───── QUALITY ───── */}
        <TabsContent value="quality" className="mt-5 space-y-5">
           <QualityZone
             job={job}
             openIssues={openIssues}
             resolvedIssues={jobIssues.filter((i) => i.status === "Resolved" || i.status === "Closed")}
             onOpenExecution={() => setExecutionOpen(true)}
             onGenerateBoundSpec={() => { void handleGenerateBoundSpec(); }}
             isGeneratingBoundSpec={isGeneratingBoundSpec}
             canReviewQA={canReviewQA}
             linkedQuoteId={linkedQuoteId}
             executionProofs={executionProofs}
             isExternalManaged={isExternalManaged}
           />
        </TabsContent>

        {/* ───── FINANCIALS ───── */}
        <TabsContent value="financials" className="mt-5 space-y-5">
          {fin$.viewFinancialSummary ? (
            <>
              <JobFinancialsPanel job={job} onJobUpdate={(updated) => setJob((prev) => prev && prev.id === updated.id ? updated : prev)} canManage={fin$.viewFinancialSummary} />
              <JobEconomicsBreakdown job={job} />
              <JobOtherExpensesCard job={job} canManage={fin$.viewFinancialSummary} onJobUpdate={(updated) => setJob((prev) => prev && prev.id === updated.id ? updated : prev)} />
              {(canCreateChangeOrder || (job.changeOrders ?? []).length > 0) && (
                <Card className="shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <SectionEyebrow>Change Orders {(job.changeOrders?.length ?? 0) > 0 && `(${job.changeOrders!.length})`}</SectionEyebrow>
                      {canCreateChangeOrder && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setChangeOrderOpen(true)}>
                          <FileText className="h-3 w-3" /> New Change Order
                        </Button>
                      )}
                    </div>
                    {(job.changeOrders ?? []).length > 0 ? (
                      <ChangeOrderHistory changeOrders={job.changeOrders ?? []} currentContractValue={getJobDisplayValue(job)} />
                    ) : (
                      <p className="text-xs text-muted-foreground italic">No change orders recorded.</p>
                    )}
                  </CardContent>
                </Card>
              )}
              {fin$.viewCredits && (
                <Card className="shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <SectionEyebrow>Customer Credits</SectionEyebrow>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setCreditFormOpen(true)}>
                        Issue Credit
                      </Button>
                    </div>
                    {credits.filter((c) => c.jobId === job.id || c.jobId === job.jobNumber).length === 0 ? (
                      <p className="text-xs text-muted-foreground">No credits issued for this job.</p>
                    ) : (
                      <div className="space-y-2">
                        {credits.filter((c) => c.jobId === job.id || c.jobId === job.jobNumber).map((c) => (
                          <div key={c.id} className="flex justify-between text-xs border rounded-md px-3 py-2">
                            <span>{c.creditType} · {c.reasonCategory}</span>
                            <span className="font-semibold">${c.creditAmount.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="shadow-none">
              <CardContent className="p-6 text-center text-muted-foreground text-sm">
                Financial details are restricted for your role.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ───── TIMELINE ───── */}
        <TabsContent value="timeline" className="mt-5">
          <UnifiedJobTimeline job={job} issues={jobIssues} wrapRecords={wrapRecords} purchaseOrders={jobPurchaseOrders} jobAllocations={jobAllocations} fieldMaterials={job.fieldMaterials ?? []} linkedLead={pipelineLead} appointmentResults={pipelineAppts} consultations={pipelineConsultations} buildReviewEvents={buildReviewEvents.filter((e) => e.jobId === job.id)} permitDetail={job.permitDetail} schedulingEvents={schedulingEvents} installerOffers={installerOffers} phaseExecutions={phaseExecutions} jobCredits={scopedCredits} serviceTickets={serviceTickets} refreshToken={auditRefreshToken} />
        </TabsContent>

        {/* ───── DOCUMENTS ───── */}
        <TabsContent value="documents" className="mt-5">
          <DocumentCenter documents={buildJobDocuments(documentJob ?? job, documentQuotes, jobIssues, jobAttachments.filter(a => a.attachmentType === "permit"))} />
        </TabsContent>
      </Tabs>

      <Dialog open={leadAttributionOpen} onOpenChange={setLeadAttributionOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Change Lead Attribution</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select which lead/source should receive credit for this job. This updates the lead link, the linked quote when available, and the job marketing attribution used by reporting.
            </p>
            {leadAttribution.candidateLeads.length === 0 ? (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                No candidate leads were found for this customer yet.
              </div>
            ) : (
              <>
                <label className="block space-y-1.5">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Credit this lead</span>
                  <select
                    value={selectedLeadAttributionId}
                    onChange={(event) => setSelectedLeadAttributionId(event.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    {leadAttribution.candidateLeads.map((lead) => {
                      const source = getLeadSourceDisplay(lead);
                      const enteredAt = formatLeadAttributionDate(getLeadEnteredAt(lead));
                      const name = lead.primaryName ?? lead.primaryEmail ?? lead.primaryPhone ?? lead.sourceId ?? lead.id.slice(0, 8);
                      return (
                        <option key={lead.id} value={lead.id}>
                          {source} · {enteredAt} · {name}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                  {leadAttribution.candidateLeads.map((lead) => {
                    const selected = lead.id === selectedLeadAttributionId;
                    const linked = lead.id === leadAttribution.currentLead?.id;
                    return (
                      <button
                        key={lead.id}
                        type="button"
                        onClick={() => setSelectedLeadAttributionId(lead.id)}
                        className={`w-full rounded-lg border p-3 text-left transition-colors ${
                          selected ? "border-primary bg-primary/10" : "border-border bg-muted/20 hover:bg-muted/40"
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-foreground">{getLeadSourceDisplay(lead)}</p>
                              {linked && <Badge variant="outline" className="text-[10px]">Current</Badge>}
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Entered {formatLeadAttributionDate(getLeadEnteredAt(lead))}
                              {lead.primaryName ? ` · ${lead.primaryName}` : ""}
                            </p>
                          </div>
                          <p className="text-xs font-semibold text-muted-foreground">{formatLeadCost(lead.leadCost)}</p>
                        </div>
                        <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                          <span>Provider: {lead.provider ?? "—"}</span>
                          <span>Source ID: {lead.sourceId ?? "—"}</span>
                          <span>Status: {lead.status ?? lead.stage ?? "—"}</span>
                          <span>Project: {lead.projectType ?? "—"}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setLeadAttributionOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSaveLeadAttribution}
                disabled={!selectedLeadAttributionId || savingLeadAttribution}
              >
                {savingLeadAttribution ? "Saving..." : "Save Attribution"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={materialsEditorOpen}
        onOpenChange={(open) => {
          setMaterialsEditorOpen(open);
          if (!open) {
            setActiveMaterialMatcherId(null);
            setActiveFieldMaterialMatcherId(null);
            setMaterialsEditorTab("core");
          }
        }}
      >
        <DialogContent className="flex h-[88vh] max-h-[88vh] w-[96vw] max-w-6xl flex-col overflow-hidden p-0">
          <DialogHeader className="shrink-0">
            <div className="px-6 pt-6">
            <DialogTitle>Manage Material SKUs</DialogTitle>
            </div>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col px-6 pb-6">
            <p className="shrink-0 text-sm text-muted-foreground">
              Keep pre-ordered materials separate from field purchases made during install. Field materials can carry receipt uploads without changing the core ordering lane.
            </p>

            {job.unmappedSkusForReview && job.unmappedSkusForReview.length > 0 && (
              <div className="mt-3 shrink-0 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
                {job.unmappedSkusForReview.length} material SKU{job.unmappedSkusForReview.length === 1 ? "" : "s"} currently need catalog matching.
              </div>
            )}

            <div className="mt-3 grid shrink-0 grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="rounded-md border bg-muted/20 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Core Materials</p>
                <p className="text-sm font-semibold text-foreground">{formatCurrency(coreMaterialsDraftTotalCost)}</p>
              </div>
              <div className="rounded-md border bg-muted/20 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Field Materials</p>
                <p className="text-sm font-semibold text-foreground">{formatCurrency(fieldMaterialsDraftTotalCost)}</p>
              </div>
              <div className="rounded-md border bg-primary/5 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Overall Material Cost</p>
                <p className="text-sm font-semibold text-primary">{formatCurrency(overallMaterialsDraftTotalCost)}</p>
              </div>
            </div>

            <Tabs
              value={materialsEditorTab}
              onValueChange={(value) => setMaterialsEditorTab(value as "core" | "field")}
              className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden"
            >
              <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <TabsList className="h-9 shrink-0 self-start bg-muted/40">
                  <TabsTrigger value="core" className="text-xs">
                    Core / Pre-Ordered Materials
                  </TabsTrigger>
                  <TabsTrigger value="field" className="text-xs">
                    Field Materials
                  </TabsTrigger>
                </TabsList>
                {materialsEditorTab === "core" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 self-start text-xs sm:self-auto"
                    onClick={handleAddMaterialDraft}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add Core Material
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 self-start text-xs sm:self-auto"
                    onClick={handleAddFieldMaterialDraft}
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    Add Field Material
                  </Button>
                )}
              </div>

              <TabsContent value="core" className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden">
                <div className="mb-3 shrink-0">
                  <SectionEyebrow>Core / Pre-Ordered Materials</SectionEyebrow>
                  <p className="text-xs text-muted-foreground">
                    These rows feed the normal Product Registry, ordering, and warehouse workflow.
                  </p>
                </div>
                {(() => {
                  const unmatched = materialsEditorDraft.filter(
                    (m) => m.sku && !catalogProductBySku.get(normalizeMaterialSku(m.sku)),
                  );
                  return canRegisterProduct && unmatched.length > 0 ? (
                    <div className="mb-3 shrink-0 flex items-center justify-between rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
                      <span>
                        {unmatched.length} material{unmatched.length === 1 ? '' : 's'} not in Product Registry
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-warning hover:text-warning"
                        onClick={() => {
                          setQuickRegisterPrefill({ name: unmatched[0].itemName, sku: unmatched[0].sku });
                          setQuickRegisterOpen(true);
                        }}
                      >
                        Register first
                      </Button>
                    </div>
                  ) : null;
                })()}
                <div className="flex min-h-0 flex-1 flex-col rounded-md border overflow-hidden">
                  <div className="grid grid-cols-[minmax(160px,1.35fr)_minmax(120px,1fr)_72px_96px_80px_minmax(130px,0.75fr)_36px] gap-2 border-b bg-muted/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    <div>Material</div>
                    <div>SKU</div>
                    <div>Qty</div>
                    <div>Unit Cost</div>
                    <div>Tax %</div>
                    <div>Catalog Match</div>
                    <div />
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto p-3">
                    <div className="space-y-2">
                      {materialsEditorDraft.map((material, index) => {
                        const normalizedSku = normalizeMaterialSku(material.sku);
                        const matchedProduct = catalogProductBySku.get(normalizedSku);
                        const isMatcherOpen = activeMaterialMatcherId === material.editorId;
                        return (
                          <div
                            key={material.editorId}
                            className="grid grid-cols-[minmax(160px,1.35fr)_minmax(120px,1fr)_72px_96px_80px_minmax(130px,0.75fr)_36px] gap-2 rounded-md border bg-background px-2 py-2"
                          >
                            <div className="space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Material {index + 1}</Label>
                              <Input
                                className="h-8 text-xs"
                                value={material.itemName}
                                onChange={(event) => handleUpdateMaterialDraft(material.editorId, { itemName: event.target.value })}
                                placeholder="Panel set, drain kit, grab bar..."
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">SKU</Label>
                              <Input
                                className="h-8 text-xs font-mono"
                                value={material.sku}
                                readOnly
                                placeholder="Select from Product Registry"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Qty</Label>
                              <Input
                                type="number"
                                min={0}
                                className="h-8 text-xs"
                                value={material.quantityRequired}
                                onChange={(event) =>
                                  handleUpdateMaterialDraft(material.editorId, {
                                    quantityRequired: Math.max(0, Number(event.target.value) || 0),
                                  })
                                }
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Unit Cost</Label>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                className="h-8 text-xs"
                                value={material.unitCost ?? ""}
                                onChange={(event) => {
                                  const nextValue = event.target.value;
                                  handleUpdateMaterialDraft(material.editorId, {
                                    unitCost: nextValue === "" ? undefined : Math.max(0, Number(nextValue) || 0),
                                  });
                                }}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Tax %</Label>
                              <Input
                                type="number"
                                min={0}
                                step="0.01"
                                className="h-8 text-xs"
                                value={material.taxPercent ?? ""}
                                onChange={(event) => {
                                  const nextValue = event.target.value;
                                  handleUpdateMaterialDraft(material.editorId, {
                                    taxPercent: nextValue === "" ? undefined : Math.max(0, Number(nextValue) || 0),
                                  });
                                }}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Catalog Match</Label>
                              <Popover open={isMatcherOpen} onOpenChange={(open) => setActiveMaterialMatcherId(open ? material.editorId : null)}>
                                <PopoverTrigger asChild>
                                  <Button type="button" variant="outline" className="h-8 w-full min-w-0 justify-start px-2 text-xs">
                                    <span className="truncate">{matchedProduct ? matchedProduct.sku : "Match SKU"}</span>
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[340px] p-0" align="end">
                                  <Command className="overflow-visible">
                                    <CommandInput placeholder="Search Product Registry name or SKU..." />
                                    <CommandList className="max-h-[260px] overflow-y-scroll pointer-events-auto">
                                      <CommandEmpty>
                                        {catalogProductsLoading ? "Loading Product Registry..." : "No Product Registry SKU found"}
                                      </CommandEmpty>
                                      <CommandGroup>
                                        {catalogProducts.map((product) => (
                                          <CommandItem
                                            key={product.productId}
                                            value={`${product.sku} ${product.productName} ${product.category}`}
                                            onSelect={() => handleMatchMaterialSku(material.editorId, product)}
                                          >
                                            <div className="min-w-0">
                                              <div className="text-xs font-medium">{product.productName}</div>
                                              <div className="text-[10px] text-muted-foreground">{product.sku} · {product.category}</div>
                                            </div>
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                              <div className="flex items-center gap-1 flex-wrap">
                                {matchedProduct ? (
                                  <Badge variant="secondary" className="text-[10px]">Matched</Badge>
                                ) : (
                                  <Badge variant="outline" className="border-warning/40 text-[10px] text-warning">Unmatched</Badge>
                                )}
                                {matchedProduct && (
                                  <span className="truncate text-[10px] text-muted-foreground">{matchedProduct.productName}</span>
                                )}
                                {!matchedProduct && canRegisterProduct && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 px-1.5 text-[10px] text-warning hover:text-warning"
                                    onClick={() => {
                                      setQuickRegisterPrefill({ name: material.itemName, sku: material.sku });
                                      setQuickRegisterOpen(true);
                                    }}
                                  >
                                    + Register
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-end justify-end">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleRemoveMaterialDraft(material.editorId)}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="field" className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1 data-[state=inactive]:hidden">
                <div className="mb-3">
                  <SectionEyebrow>Field Materials</SectionEyebrow>
                  <p className="text-xs text-muted-foreground">
                    Materials bought in the field during installation. These stay separate from the core/pre-ordered BOM and can include receipt evidence.
                  </p>
                </div>
                <div className="space-y-3 pb-1">
                  <div className="rounded-md border border-dashed px-3 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Shared Receipt Library</Label>
                        <p className="text-xs text-muted-foreground">
                          Upload a receipt once, then link it to every field material item that appears on that same store receipt.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <label className="inline-flex cursor-pointer items-center rounded-md border px-2 py-1 text-[11px] text-foreground hover:bg-muted">
                          <Camera className="mr-1 h-3 w-3" />
                          Take Pic
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(event) => {
                              handleAttachFieldMaterialReceipts(event.target.files);
                              event.currentTarget.value = "";
                            }}
                          />
                        </label>
                        <label className="inline-flex cursor-pointer items-center rounded-md border px-2 py-1 text-[11px] text-foreground hover:bg-muted">
                          <FileText className="mr-1 h-3 w-3" />
                          Upload Receipt
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            multiple
                            className="hidden"
                            onChange={(event) => {
                              handleAttachFieldMaterialReceipts(event.target.files);
                              event.currentTarget.value = "";
                            }}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1">
                      {fieldReceiptLibraryDisplayGroups.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground">No shared receipts attached yet.</p>
                      ) : (
                        fieldReceiptLibraryDisplayGroups.map(({ receipt, receiptClientIds }) => {
                          const existingAttachment = receipt.attachmentId ? jobAttachmentById.get(receipt.attachmentId) : null;
                          const linkedCount = fieldMaterialsDraft.filter((material) =>
                            receiptClientIds.some((clientId) => material.receiptClientIds.includes(clientId)),
                          ).length;
                          const isParsingThisReceipt = fieldReceiptParsingId === receipt.clientId;
                          return (
                            <div key={receipt.clientId} className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-[11px]">
                              <div className="min-w-0">
                                <p className="truncate font-medium">{receipt.fileName || existingAttachment?.fileName || "Receipt"}</p>
                                <p className="truncate text-muted-foreground">
                                  {receipt.file ? "Pending upload" : "Saved receipt"} · linked to {linkedCount} item{linkedCount === 1 ? "" : "s"}
                                </p>
                                {receiptClientIds.length > 1 && (
                                  <p className="truncate text-muted-foreground">
                                    Duplicate copies consolidated into one receipt event.
                                  </p>
                                )}
                                {(formatOptionalCurrency(receipt.total) || formatOptionalCurrency(receipt.tax)) && (
                                  <p className="truncate text-muted-foreground">
                                    {formatOptionalCurrency(receipt.total) ? `Total ${formatOptionalCurrency(receipt.total)}` : ""}
                                    {formatOptionalCurrency(receipt.total) && formatOptionalCurrency(receipt.tax) ? " · " : ""}
                                    {formatOptionalCurrency(receipt.tax) ? `Sales tax ${formatOptionalCurrency(receipt.tax)}` : ""}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-[11px]"
                                  onClick={() => handleParseFieldMaterialReceipt(receipt.clientId)}
                                  disabled={Boolean(fieldReceiptParsingId && !isParsingThisReceipt)}
                                >
                                  {isParsingThisReceipt ? "Parsing..." : "Parse"}
                                </Button>
                                {!receipt.file && receipt.attachmentId && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-[11px]"
                                    onClick={() => handleViewFieldMaterialReceipt(receipt.attachmentId!)}
                                  >
                                    View
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                    className="h-6 px-2 text-[11px] text-destructive"
                                    onClick={() => handleRemoveFieldMaterialReceiptGroup(receiptClientIds)}
                                    disabled={isParsingThisReceipt}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    {fieldMaterialGroupsByReceipt.map(({ receipt, materials }, receiptIndex) => {
                      const existingAttachment = receipt.attachmentId ? jobAttachmentById.get(receipt.attachmentId) : null;
                      return (
                        <div key={receipt.clientId} className="rounded-md border">
                          <div className="flex flex-wrap items-start justify-between gap-3 border-b bg-muted/20 px-3 py-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <SectionEyebrow>Receipt {receiptIndex + 1}</SectionEyebrow>
                                <Badge variant="outline" className="text-[10px]">
                                  {materials.length} item{materials.length === 1 ? "" : "s"}
                                </Badge>
                              </div>
                              <p className="truncate text-sm font-medium">
                                {receipt.fileName || existingAttachment?.fileName || "Receipt"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {receipt.file ? "Pending upload" : "Saved receipt"} · linked field materials grouped below
                              </p>
                              {(formatOptionalCurrency(receipt.total) || formatOptionalCurrency(receipt.tax)) && (
                                <p className="text-xs text-muted-foreground">
                                  {formatOptionalCurrency(receipt.total) ? `Total ${formatOptionalCurrency(receipt.total)}` : ""}
                                  {formatOptionalCurrency(receipt.total) && formatOptionalCurrency(receipt.tax) ? " · " : ""}
                                  {formatOptionalCurrency(receipt.tax) ? `Sales tax ${formatOptionalCurrency(receipt.tax)}` : ""}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {!receipt.file && receipt.attachmentId && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 text-xs"
                                  onClick={() => handleViewFieldMaterialReceipt(receipt.attachmentId!)}
                                >
                                  View Receipt
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => handleParseFieldMaterialReceipt(receipt.clientId)}
                                disabled={Boolean(fieldReceiptParsingId && fieldReceiptParsingId !== receipt.clientId)}
                              >
                                {fieldReceiptParsingId === receipt.clientId ? "Parsing..." : "Parse Receipt"}
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-2 p-3">
                            {materials.map((material, materialIndex) =>
                              renderFieldMaterialEditorRow(material, `Receipt ${receiptIndex + 1} • Item ${materialIndex + 1}`),
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {unlinkedFieldMaterials.length > 0 && (
                      <div className="rounded-md border border-dashed">
                        <div className="border-b border-dashed bg-muted/10 px-3 py-3">
                          <SectionEyebrow>Unlinked Field Materials</SectionEyebrow>
                          <p className="text-xs text-muted-foreground">
                            These items are not tied to any receipt yet. Link a receipt from the library above so they can be grouped and posted cleanly.
                          </p>
                        </div>
                        <div className="space-y-2 p-3">
                          {unlinkedFieldMaterials.map((material, index) =>
                            renderFieldMaterialEditorRow(material, `Unlinked Item ${index + 1}`),
                          )}
                        </div>
                      </div>
                    )}

                    {fieldMaterialsDraft.length === 0 && (
                      <div className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
                        No field materials yet. Add one only when the crew buys something during the install.
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex shrink-0 items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">
                  Core rows with unmatched SKUs will stay editable and be flagged for review. Field materials stay separate and do not feed the pre-ordered material lane.
                </p>
                <p className="mt-1 text-xs font-medium text-foreground">
                  Overall material cost: {formatCurrency(overallMaterialsDraftTotalCost)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setMaterialsEditorOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSaveMaterialsEditor} disabled={materialsEditorSaving}>
                  {materialsEditorSaving ? "Saving..." : "Save Materials"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <QuickRegisterProductModal
        open={quickRegisterOpen}
        onOpenChange={setQuickRegisterOpen}
        prefill={quickRegisterPrefill}
        onRegistered={(product) => {
          setCatalogProducts((prev) => [...prev, product]);
        }}
      />

      {/* Modals */}
      <JobEditDialog
        open={jobEditOpen}
        onOpenChange={setJobEditOpen}
        job={job}
        onSave={handleSaveJobDetails}
      />
      <OrderingModal open={orderingOpen} onOpenChange={setOrderingOpen} job={job} openIssues={openIssues} onSendToOrdering={handleOrderingSubmit} />
      <RescheduleModal open={rescheduleOpen} onOpenChange={setRescheduleOpen} job={job} />
      <QuickAssignModal open={assignOpen} onOpenChange={setAssignOpen} job={job} />
      {resolveIssueTarget && (
        <QuickResolveIssueModal
          open={!!resolveIssueTarget}
          onOpenChange={(o) => { if (!o) setResolveIssueTarget(null); }}
          issue={resolveIssueTarget}
        />
      )}
      {creditFormOpen && (
        <Dialog open={creditFormOpen} onOpenChange={setCreditFormOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Issue Credit</DialogTitle></DialogHeader>
            <CreateCreditForm
              jobId={job.jobNumber || job.id}
              customerId={job.customerName}
              onSubmit={async (credit: CustomerCredit) => {
                const { data, error } = await CreditService.create(credit);
                if (error || !data) {
                  console.error("[JobDetailCockpit] Failed to create credit:", error);
                  toast.error(error instanceof Error ? error.message : "Credit could not be issued");
                  return;
                }
                setScopedCredits((prev) => [data, ...prev.filter((existing) => existing.id !== data.id)]);
                setCredits((prev) => [data, ...prev.filter((existing) => existing.id !== data.id)]);
                setCreditFormOpen(false);
                toast.success("Credit issued");
                void refreshData();
              }}
              onCancel={() => setCreditFormOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Push-chain modals */}
      {pushChain.reschedule && (
        <RescheduleModal
          open={!!pushChain.reschedule}
          onOpenChange={(o) => !o && pushChain.closeAll()}
          job={pushChain.reschedule}
          openIssues={dbJobIssues.filter((i) => i.jobId === pushChain.reschedule!.id)}
          onChooseNextStep={(step) => pushChain.chainTo(step, pushChain.reschedule!)}
        />
      )}
      {pushChain.assign && (
        <QuickAssignModal
          open={!!pushChain.assign}
          onOpenChange={(o) => !o && pushChain.closeAll()}
          job={pushChain.assign}
          openIssues={dbJobIssues.filter((i) => i.jobId === pushChain.assign!.id)}
          onChooseNextStep={(step) => pushChain.chainTo(step, pushChain.assign!)}
        />
      )}
      {pushChain.permit && (
        <PermitQuickViewModal
          open={!!pushChain.permit}
          onOpenChange={(o) => !o && pushChain.closeAll()}
          job={pushChain.permit}
          onChooseNextStep={(step) => pushChain.chainTo(step, pushChain.permit!)}
        />
      )}
      {pushChain.resolve && (
        <QuickResolveIssueModal
          open={!!pushChain.resolve}
          onOpenChange={(o) => !o && pushChain.closeAll()}
          issue={pushChain.resolve}
          onChooseNextStep={(step) => {
            const stepJob = job && (job.id === pushChain.resolve!.jobId || job.jobNumber === pushChain.resolve!.jobId)
              ? job
              : null;
            if (stepJob) pushChain.chainTo(step, stepJob);
          }}
        />
      )}

      {/* Execution dialog */}
      <Dialog open={executionOpen} onOpenChange={setExecutionOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Installer Execution · {job.jobNumber || job.id}</DialogTitle>
          </DialogHeader>
          <MaterialKitPanel jobId={job.id} bound={job.boundJobSpec} currentStage={job.currentStage} />
          <JobSpecExecutionPanel jobId={job.id} bound={job.boundJobSpec} />
        </DialogContent>
      </Dialog>

{/* Change Order dialog */}
       <ChangeOrderDialog
         open={changeOrderOpen}
         onOpenChange={setChangeOrderOpen}
         job={job}
         existingOrders={job.changeOrders ?? []}
         onSubmit={async (co: ChangeOrder) => {
           const updatedOrders = [...(job.changeOrders ?? []), co];
           const updatedJob = {
             ...job,
             changeOrders: updatedOrders,
             financials: job.financials
               ? { ...job.financials, contractValue: co.revisedContractValue }
               : job.financials,
             activityHistory: [
               {
                 id: `act-${Date.now()}`,
                 date: new Date().toISOString(),
                 action: `Change Order CO-${co.orderNumber} signed — contract revised to $${co.revisedContractValue.toLocaleString()}`,
                 user: performedByLabel,
                 noteType: "ops" as const,
               },
               ...job.activityHistory,
             ],
           };
           const { data, error } = await JobService.update(job.id, updatedJob);
           if (error || !data) {
             console.error("[JobDetailCockpit] Failed to save change order:", error);
             toast.error("Failed to save change order");
             return;
           }
           setJob(data);
         }}
       />

      {/* Build Review — Push-Mode workspace scoped to this job */}
      <IntakeReviewWorkspace
        job={reviewOpen ? job : null}
        open={reviewOpen}
        buildReviewEvents={buildReviewEvents.filter((e) => e.jobId === job.id)}
        onClose={() => setReviewOpen(false)}
        onApprove={handleReviewApprove}
        onRequestRevision={handleReviewRevision}
        openIssues={openIssues}
        onApproveAndContinue={handleReviewApproveAndContinue}
        onContinueChain={() => {
          setReviewOpen(false);
          setTab("financials");
        }}
      />
    </CockpitPage>
  );
}

/* ─────────── Sub-components ─────────── */

type JobEditForm = {
  customerName: string;
  phone: string;
  email: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  projectType: ProjectType;
  permitRequired: boolean;
  permitStatus: Job["permitStatus"];
  permitType: string;
  permitMunicipality: string;
  permitNumber: string;
  permitCost: string;
  permitNotes: string;
  scopeSummary: string;
  notes: string;
  checklistItems: JobChecklistItem[];
};

type JobEditPatch = Partial<Job> & { title?: string };

function buildJobEditForm(job: Job): JobEditForm {
  return {
    customerName: job.customerName ?? "",
    phone: job.phone ?? "",
    email: job.email ?? "",
    streetAddress: job.address?.street ?? "",
    city: job.address?.city ?? "",
    state: job.address?.state ?? "",
    zip: job.address?.zip ?? "",
    projectType: job.projectType ?? "Other",
    permitRequired: Boolean(job.permitRequired),
    permitStatus: job.permitStatus ?? "Not Required",
    permitType: job.permitDetail?.permitType ?? "",
    permitMunicipality: job.permitDetail?.municipality ?? "",
    permitNumber: job.permitDetail?.permitNumber ?? "",
    permitCost:
      typeof job.permitDetail?.permitCost === "number" && Number.isFinite(job.permitDetail.permitCost)
        ? String(job.permitDetail.permitCost)
        : "",
    permitNotes: job.permitDetail?.permitNotes ?? "",
    scopeSummary: job.scopeSummary ?? "",
    notes: job.notes ?? "",
    checklistItems: resolveJobChecklist(job).items,
  };
}

function JobEditDialog({
  open,
  onOpenChange,
  job,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job;
  onSave: (patch: JobEditPatch) => Promise<void>;
}) {
  const [form, setForm] = useState<JobEditForm>(() => buildJobEditForm(job));
  const [saving, setSaving] = useState(false);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [checklistDirty, setChecklistDirty] = useState(false);
  const [generatingAiScope, setGeneratingAiScope] = useState(false);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [uploadedContractDocument, setUploadedContractDocument] = useState<Job["contractDocument"] | null>(null);
  const [aiScopeDraft, setAiScopeDraft] = useState<MigrationAiScopeDraft | null>(null);
  const [aiScopeModel, setAiScopeModel] = useState("");
  const contractUploadInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setForm(buildJobEditForm(job));
    setChecklistDirty(false);
    setUploadedContractDocument(null);
    const cachedDraft = job.migrationImportDetail?.aiScopeDraft;
    if (cachedDraft?.scopeSummary) {
      const normalizedDraft = normalizeAiScopeDraft(cachedDraft, cachedDraft.model, job.contractDocument?.fileName);
      setAiScopeDraft(normalizedDraft);
      setAiScopeModel(normalizedDraft.model ?? "");
    } else {
      setAiScopeDraft(null);
      setAiScopeModel("");
    }
    setLoadingChecklist(true);

    loadPersistedJobChecklist(job)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("[JobEditDialog] Failed to load persisted job checklist:", error);
          toast.warning("Using draft checklist", {
            description: "Could not load the portal checklist rows for this job.",
          });
          return;
        }
        if (data?.items?.length) {
          setForm((prev) => ({ ...prev, checklistItems: data.items }));
          setChecklistDirty(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingChecklist(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, job]);

  const contractDocument = uploadedContractDocument ?? job.contractDocument;
  const hasKnownContractDocument = Boolean(contractDocument?.filePath);
  const hasKnownPdfContract = Boolean(contractDocument?.filePath && isPdfContractDocument(contractDocument));
  const canGenerateAiScope = Boolean(job.id) && (!hasKnownContractDocument || hasKnownPdfContract);

  const uploadContractForAiScope = async (files: FileList | null) => {
    const file = Array.from(files ?? [])[0];
    if (!file) return;

    const candidate = { fileName: file.name, mimeType: file.type, filePath: file.name } as Job["contractDocument"];
    if (!isPdfContractDocument(candidate)) {
      toast.warning("Upload a PDF contract to generate an AI scope.");
      return;
    }

    setUploadingContract(true);
    try {
      const { data: authData } = await supabase.auth.getUser();
      const createdBy = authData.user?.id ?? "job-edit";
      const attachment = await uploadAttachment(
        file,
        "job",
        job.id,
        "contract",
        createdBy,
        "Contract uploaded from Edit Job for AI scope generation.",
      );
      const nextContract = buildJobEditContractDocument(job, attachment, file);
      setUploadedContractDocument(nextContract);
      setAiScopeDraft(null);
      setAiScopeModel("");
      toast.success("Contract uploaded", {
        description: "Generate AI Scope will use this PDF. Save Job to keep it as the job contract.",
      });
    } catch (error) {
      console.error("[JobEditDialog] Contract upload failed:", error);
      toast.error("Contract upload failed", {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setUploadingContract(false);
      if (contractUploadInputRef.current) contractUploadInputRef.current.value = "";
    }
  };

  const generateAiScope = async () => {
    if (contractDocument?.filePath && !isPdfContractDocument(contractDocument)) {
      toast.warning("AI scope generation currently supports PDF contracts only.");
      return;
    }

    setGeneratingAiScope(true);
    try {
      const { data, error } = await supabase.functions.invoke<ImportScopeGenerationResponse>(
        "import-scope-generate",
        {
          body: {
            jobId: job.id,
            organizationId: job.organizationId,
            contractDocument: contractDocument?.filePath
              ? {
                  fileName: contractDocument.fileName,
                  filePath: contractDocument.filePath,
                  mimeType: contractDocument.mimeType,
                  fileSize: contractDocument.fileSize,
                  contractTotal: contractDocument.contractTotal,
                }
              : undefined,
          },
        },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.detail || data.error);
      if (!data?.generation?.scopeSummary) {
        throw new Error("The AI response did not include a usable scope.");
      }

      const draft = normalizeAiScopeDraft(data.generation, data.model, contractDocument?.fileName);
      setAiScopeDraft(draft);
      setAiScopeModel(data.model ?? draft.model ?? "");
      toast.success("AI installer scope draft generated.");
    } catch (error) {
      console.error("[JobEditDialog] AI scope generation failed:", error);
      toast.error("AI scope generation failed", {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setGeneratingAiScope(false);
    }
  };

  const updateField = <K extends keyof JobEditForm>(key: K, value: JobEditForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateChecklistItem = <K extends keyof JobChecklistItem>(index: number, key: K, value: JobChecklistItem[K]) => {
    setChecklistDirty(true);
    setForm((prev) => ({
      ...prev,
      checklistItems: prev.checklistItems.map((item, i) => i === index ? { ...item, [key]: value } : item),
    }));
  };

  const updatePhaseLabel = (phaseKey: JobChecklistPhaseKey, phaseLabel: string) => {
    setChecklistDirty(true);
    setForm((prev) => ({
      ...prev,
      checklistItems: prev.checklistItems.map((item) =>
        item.phaseKey === phaseKey ? { ...item, phaseLabel } : item,
      ),
    }));
  };

  const resetChecklistToDefault = async (projectType = form.projectType) => {
    const confirmed = window.confirm(
      "Replace this job's installer portal checklist with the default template? Existing matching checked states are preserved, but checklist structure can change.",
    );
    if (!confirmed) return;
    const next = await loadDefaultJobChecklist({
      organizationId: job.organizationId,
      projectType,
      scopeSummary: form.scopeSummary,
      notes: form.notes,
      projectConfig: job.projectConfig,
    });
    setChecklistDirty(true);
    setForm((prev) => ({ ...prev, checklistItems: next.items }));
  };

  const clearChecklistItems = () => {
    const confirmed = window.confirm(
      "Clear this job's installer portal checklist structure? This will remove or deactivate checklist rows on save. Checked status is not reset unless rows are explicitly removed.",
    );
    if (!confirmed) return;
    setChecklistDirty(true);
    setForm((prev) => ({ ...prev, checklistItems: [] }));
  };

  const addChecklistItem = (phaseKey: JobChecklistPhaseKey = "install") => {
    setChecklistDirty(true);
    setForm((prev) => ({
      ...prev,
      checklistItems: [
        ...prev.checklistItems,
        {
          phaseKey,
          phaseLabel: prev.checklistItems.find((item) => item.phaseKey === phaseKey)?.phaseLabel
            || DEFAULT_JOB_CHECKLIST_PHASE_LABELS[phaseKey],
          itemIndex: Math.max(
            -1,
            ...prev.checklistItems
              .filter((item) => item.phaseKey === phaseKey)
              .map((item) => Number(item.itemIndex) || 0),
          ) + 1,
          label: "",
          proofLabel: null,
          requiresProof: false,
          sortOrder: prev.checklistItems.length,
          isActive: true,
        },
      ],
    }));
  };

  const addCustomPhase = () => {
    const phaseLabel = window.prompt("Custom phase name");
    const label = String(phaseLabel ?? "").trim();
    if (!label) return;

    setChecklistDirty(true);
    setForm((prev) => {
      const usedPhases = new Set(prev.checklistItems.map((item) => item.phaseKey));
      const phaseKey = !usedPhases.has("custom")
        ? "custom"
        : JOB_CHECKLIST_AUTHORING_PHASES.find((phase) => !usedPhases.has(phase)) ?? "install";
      const phaseItems = prev.checklistItems.filter((item) => item.phaseKey === phaseKey);

      return {
        ...prev,
        checklistItems: [
          ...prev.checklistItems,
          {
            phaseKey,
            phaseLabel: label,
            itemIndex: phaseItems.length,
            label: "",
            proofLabel: null,
            requiresProof: false,
            sortOrder: phaseItems.length,
            isActive: true,
          },
        ],
      };
    });
  };

  const checklistItemsByPhase = useMemo(() => {
    const groups = new Map<JobChecklistPhaseKey, Array<{ item: JobChecklistItem; index: number }>>();
    for (const phase of JOB_CHECKLIST_PHASES) groups.set(phase, []);
    form.checklistItems.forEach((item, index) => {
      const phase = JOB_CHECKLIST_PHASES.includes(item.phaseKey) ? item.phaseKey : "install";
      groups.get(phase)?.push({ item, index });
    });
    for (const rows of groups.values()) {
      rows.sort((a, b) => {
        const sortA = Number.isFinite(Number(a.item.sortOrder)) ? Number(a.item.sortOrder) : a.index;
        const sortB = Number.isFinite(Number(b.item.sortOrder)) ? Number(b.item.sortOrder) : b.index;
        if (sortA !== sortB) return sortA - sortB;
        return a.item.itemIndex - b.item.itemIndex;
      });
    }
    return groups;
  }, [form.checklistItems]);

  const phaseLabelsByPhase = useMemo(() => {
    const labels = new Map<JobChecklistPhaseKey, string>();
    for (const phase of JOB_CHECKLIST_PHASES) labels.set(phase, DEFAULT_JOB_CHECKLIST_PHASE_LABELS[phase]);
    for (const item of form.checklistItems) {
      if (JOB_CHECKLIST_PHASES.includes(item.phaseKey) && item.phaseLabel) {
        labels.set(item.phaseKey, item.phaseLabel);
      }
    }
    return labels;
  }, [form.checklistItems]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const nextCustomerName = form.customerName.trim();
      const nextProjectType = form.projectType;
      const nextJobTitle = `${nextCustomerName}${nextProjectType ? ` - ${nextProjectType}` : ""}` || "Untitled Job";

      const patch: JobEditPatch = {
        customerName: nextCustomerName,
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: {
          ...(job.address ?? { street: "", city: "", state: "", zip: "" }),
          street: form.streetAddress.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zip: form.zip.trim(),
        },
        projectType: nextProjectType,
        permitRequired: form.permitRequired,
        permitStatus: form.permitRequired ? form.permitStatus : "Not Required",
        permitDetail: {
          ...(job.permitDetail ?? {
            permitType: "",
            municipality: "",
            submittedDate: "",
            detailedStage: "Not Required",
            lastUpdate: "",
            permitNotes: "",
            assignedCoordinator: "",
            blockers: "",
            targetApprovalDate: "",
          }),
          permitType: form.permitType.trim(),
          municipality: form.permitMunicipality.trim(),
          permitNumber: form.permitNumber.trim(),
          permitCost:
            form.permitCost.trim().length > 0 && Number.isFinite(Number(form.permitCost))
              ? Number(form.permitCost)
              : undefined,
          permitNotes: form.permitNotes.trim(),
        },
        scopeSummary: form.scopeSummary.trim(),
        notes: form.notes.trim(),
        title: nextJobTitle,
      };

      if (uploadedContractDocument?.filePath) {
        patch.contractDocument = uploadedContractDocument;
      }

      if (aiScopeDraft?.scopeSummary) {
        patch.migrationImportDetail = {
          ...(job.migrationImportDetail ?? {}),
          aiScopeDraft: normalizeAiScopeDraft(
            aiScopeDraft,
            aiScopeModel || aiScopeDraft.model,
            job.contractDocument?.fileName,
          ),
        };
      }

      if (checklistDirty) {
        const normalizedChecklistItems = normalizeChecklistItems(form.checklistItems);
        const checklistTemplate = await loadDefaultJobChecklist({
          organizationId: job.organizationId,
          projectType: form.projectType,
          scopeSummary: form.scopeSummary,
          notes: form.notes,
          projectConfig: job.projectConfig,
        });
        patch.checklist = {
          templateKey: checklistTemplate.templateKey,
          vertical: checklistTemplate.vertical,
          items: normalizedChecklistItems,
          updatedAt: new Date().toISOString(),
        };
      }

      await onSave(patch);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
            <DialogTitle>Edit Job · {job.jobNumber || job.id}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Customer</span>
              <Input value={form.customerName} onChange={(e) => updateField("customerName", e.target.value)} />
            </label>
            <label className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Project Type</span>
              <select
                value={form.projectType}
                onChange={(e) => {
                  const nextType = e.target.value as ProjectType;
                  updateField("projectType", nextType);
                }}
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {PROJECT_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>
            <label className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Phone</span>
              <Input value={form.phone} onChange={(e) => updateField("phone", e.target.value)} />
            </label>
            <label className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Email</span>
              <Input type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
            <label className="space-y-1.5 sm:col-span-3">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Street</span>
              <Input value={form.streetAddress} onChange={(e) => updateField("streetAddress", e.target.value)} />
            </label>
            <label className="space-y-1.5 sm:col-span-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">City</span>
              <Input value={form.city} onChange={(e) => updateField("city", e.target.value)} />
            </label>
            <label className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">State</span>
              <Input value={form.state} onChange={(e) => updateField("state", e.target.value)} />
            </label>
            <label className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">ZIP</span>
              <Input value={form.zip} onChange={(e) => updateField("zip", e.target.value)} />
            </label>
          </div>

          <div className="space-y-3 rounded-lg border bg-muted/10 p-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Permit</p>
              <p className="text-xs text-muted-foreground">
                Use this for imported jobs or permits that were handled outside the app.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label className="space-y-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Permit Required</span>
                <select
                  value={form.permitRequired ? "yes" : "no"}
                  onChange={(e) => updateField("permitRequired", e.target.value === "yes")}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>
              <label className="space-y-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Status</span>
                <select
                  value={form.permitRequired ? form.permitStatus : "Not Required"}
                  disabled={!form.permitRequired}
                  onChange={(e) => updateField("permitStatus", e.target.value as Job["permitStatus"])}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <option value="Not Required">Not Required</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Approved">Approved</option>
                  <option value="Revision Needed">Revision Needed</option>
                </select>
              </label>
              <label className="space-y-1.5 lg:col-span-2">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Permit Type</span>
                <Input
                  value={form.permitType}
                  onChange={(e) => updateField("permitType", e.target.value)}
                  placeholder="Plumbing, bathroom remodel, demo..."
                />
              </label>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <label className="space-y-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Municipality</span>
                <Input
                  value={form.permitMunicipality}
                  onChange={(e) => updateField("permitMunicipality", e.target.value)}
                  placeholder="City / county / AHJ"
                />
              </label>
              <label className="space-y-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Permit # / Reference</span>
                <Input
                  value={form.permitNumber}
                  onChange={(e) => updateField("permitNumber", e.target.value)}
                  placeholder="Permit number or outside reference"
                />
              </label>
              <label className="space-y-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Permit Cost</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.permitCost}
                  onChange={(e) => updateField("permitCost", e.target.value)}
                  placeholder="0.00"
                />
              </label>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <label className="space-y-1.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Permit Notes</span>
                <Input
                  value={form.permitNotes}
                  onChange={(e) => updateField("permitNotes", e.target.value)}
                  placeholder="Reference number, outside-app notes, follow-up info..."
                />
              </label>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Scope</span>
                {!hasKnownContractDocument && (
                  <p className="text-[11px] text-muted-foreground">
                    Upload a PDF contract here, or AI generation will look for one in this job&apos;s Documents.
                  </p>
                )}
                {hasKnownContractDocument && !hasKnownPdfContract && (
                  <p className="text-[11px] text-muted-foreground">
                    AI generation needs a PDF contract. The attached contract is not a PDF.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={contractUploadInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(event) => void uploadContractForAiScope(event.currentTarget.files)}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={saving || generatingAiScope || uploadingContract}
                  onClick={() => contractUploadInputRef.current?.click()}
                >
                  <FileText className="mr-1.5 h-3.5 w-3.5" />
                  {uploadingContract
                    ? "Uploading..."
                    : hasKnownContractDocument
                      ? "Replace Contract PDF"
                      : "Upload Contract PDF"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={saving || generatingAiScope || uploadingContract || !canGenerateAiScope}
                  onClick={() => void generateAiScope()}
                >
                  {generatingAiScope ? (
                    <>
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                      {aiScopeDraft ? "Regenerate AI Scope" : "Generate AI Scope"}
                    </>
                  )}
                </Button>
              </div>
            </div>
            {contractDocument?.filePath && (
              <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                Using contract: <span className="font-medium text-foreground">{contractDocument.fileName || "Uploaded PDF"}</span>
                {uploadedContractDocument?.filePath && (
                  <span className="ml-2 text-amber-600">Save Job to keep this upload on the job record.</span>
                )}
              </div>
            )}
            <Textarea
              rows={7}
              value={form.scopeSummary}
              onChange={(e) => updateField("scopeSummary", e.target.value)}
              placeholder="Describe the contracted work scope..."
            />
          </div>

          {aiScopeDraft && (
            <div className="space-y-3 rounded-lg border bg-muted/10 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    AI Installer Scope Draft
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Edit the generated draft here, then apply it to the job scope above.
                  </p>
                </div>
                <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                  <div>
                    <span className="font-medium text-foreground">Confidence:</span> {Math.round((aiScopeDraft.confidence ?? 0) * 100)}%
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Work area:</span> {aiScopeDraft.workArea || "Not specified"}
                  </div>
                </div>
              </div>
              <Textarea
                rows={10}
                className="font-mono text-xs"
                value={aiScopeDraft.scopeSummary}
                onChange={(event) =>
                  setAiScopeDraft((current) =>
                    current ? { ...current, scopeSummary: event.target.value } : current,
                  )
                }
              />
              {(aiScopeDraft.colorSelections.length > 0 || aiScopeDraft.panelBreakdown.length > 0) && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border bg-background p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                      Color / Finish Selections
                    </p>
                    {aiScopeDraft.colorSelections.length > 0 ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
                        {aiScopeDraft.colorSelections.map((selection, index) => (
                          <li key={`${selection}-${index}`}>{selection}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-xs text-muted-foreground">No colors found in the contract.</p>
                    )}
                  </div>
                  <div className="rounded-md border bg-background p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                      Panel Breakdown
                    </p>
                    {aiScopeDraft.panelBreakdown.length > 0 ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
                        {aiScopeDraft.panelBreakdown.map((panel, index) => (
                          <li key={`${panel}-${index}`}>{panel}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-xs text-muted-foreground">No panel breakdown found.</p>
                    )}
                  </div>
                </div>
              )}
              {(aiScopeDraft.warnings.length > 0 || aiScopeDraft.missingFields.length > 0) && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-700">Warnings</p>
                    {aiScopeDraft.warnings.length > 0 ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
                        {aiScopeDraft.warnings.map((warning, index) => (
                          <li key={`${warning}-${index}`}>{warning}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-xs text-muted-foreground">No warnings returned.</p>
                    )}
                  </div>
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-destructive">Missing Fields</p>
                    {aiScopeDraft.missingFields.length > 0 ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-xs">
                        {aiScopeDraft.missingFields.map((field, index) => (
                          <li key={`${field}-${index}`}>{field}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-xs text-muted-foreground">No missing fields returned.</p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => updateField("scopeSummary", aiScopeDraft.scopeSummary)}
                >
                  Apply Draft to Scope
                </Button>
              </div>
            </div>
          )}

          <label className="block space-y-1.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Description / Notes</span>
            <Textarea
              rows={4}
              value={form.notes}
              onChange={(e) => updateField("notes", e.target.value)}
              placeholder="Internal notes, customer context, special handling..."
            />
          </label>

          <div className="space-y-3 rounded-lg border bg-muted/10 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Installer Portal Checklist</p>
                <p className="text-xs text-muted-foreground">
                  {loadingChecklist
                    ? "Loading current portal checklist rows..."
                    : "These job-specific rows sync to the installer portal. Checked execution state is preserved after dispatch."}
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => void resetChecklistToDefault()}>
                  Use default
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={clearChecklistItems} disabled={form.checklistItems.length === 0}>
                  Clear list
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={addCustomPhase}>
                  Add custom phase
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => addChecklistItem()}>
                  Add item
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              {JOB_CHECKLIST_PHASES.map((phase) => {
                const rows = checklistItemsByPhase.get(phase) ?? [];
                if (rows.length === 0 && phase === "rework") return null;
                const phaseLabel = phaseLabelsByPhase.get(phase) ?? DEFAULT_JOB_CHECKLIST_PHASE_LABELS[phase];

                return (
                  <section key={phase} className="rounded-lg border bg-background/40 p-3">
                    <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                      <div className="min-w-[240px] flex-1">
                        <p className="text-sm font-semibold text-foreground">
                          {phaseLabel}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {rows.length} item{rows.length === 1 ? "" : "s"}
                        </p>
                        <Input
                          value={phaseLabel}
                          onChange={(e) => updatePhaseLabel(phase, e.target.value)}
                          placeholder="Phase label"
                          className="mt-2 h-8 max-w-md text-xs"
                        />
                      </div>
                      <Button type="button" size="sm" variant="outline" onClick={() => addChecklistItem(phase)}>
                        Add to {phaseLabel}
                      </Button>
                    </div>

                    {rows.length === 0 ? (
                      <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                        No items in this phase yet.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {rows.map(({ item, index }) => {
                          const phaseOptions = item.phaseKey === "rework"
                            ? JOB_CHECKLIST_PHASES
                            : JOB_CHECKLIST_AUTHORING_PHASES;

                          return (
                          <div key={`${item.phaseKey}-${item.itemIndex}-${index}`} className="grid gap-2 rounded-md border bg-background/60 p-2 sm:grid-cols-[120px_minmax(260px,1.5fr)_110px_minmax(240px,1fr)_76px_auto]">
                            <select
                              value={item.phaseKey}
                              onChange={(e) => {
                                const nextPhase = e.target.value as JobChecklistPhaseKey;
                                updateChecklistItem(index, "phaseKey", nextPhase);
                                updateChecklistItem(index, "phaseLabel", phaseLabelsByPhase.get(nextPhase) ?? DEFAULT_JOB_CHECKLIST_PHASE_LABELS[nextPhase]);
                              }}
                              className="h-9 rounded-md border border-input bg-background px-2 text-xs"
                              aria-label="Checklist phase"
                            >
                              {phaseOptions.map((phaseOption) => (
                                <option key={phaseOption} value={phaseOption}>
                                  {phaseLabelsByPhase.get(phaseOption) ?? DEFAULT_JOB_CHECKLIST_PHASE_LABELS[phaseOption]}
                                </option>
                              ))}
                            </select>
                            <Input
                              value={item.label}
                              onChange={(e) => updateChecklistItem(index, "label", e.target.value)}
                              placeholder="Checklist item label"
                              className="h-9 text-xs"
                              title={item.label}
                            />
                            <label className="flex h-9 items-center gap-2 rounded-md border px-2 text-xs text-muted-foreground">
                              <input
                                type="checkbox"
                                checked={item.requiresProof}
                                onChange={(e) => updateChecklistItem(index, "requiresProof", e.target.checked)}
                              />
                              Proof
                            </label>
                            <Input
                              value={item.proofLabel ?? ""}
                              onChange={(e) => updateChecklistItem(index, "proofLabel", e.target.value)}
                              placeholder="Proof caption / upload slot label"
                              className="h-9 text-xs"
                              title={item.proofLabel ?? ""}
                            />
                            <Input
                              type="number"
                              value={item.sortOrder ?? index}
                              onChange={(e) => updateChecklistItem(index, "sortOrder", Number(e.target.value) || 0)}
                              className="h-9 text-xs"
                              aria-label="Sort order"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setChecklistDirty(true);
                                setForm((prev) => ({
                                  ...prev,
                                  checklistItems: prev.checklistItems.filter((_, i) => i !== index),
                                }));
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving || !form.customerName.trim()}>
              {saving ? "Saving..." : "Save Job"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LeadAttributionCard({
  attribution,
  canEdit,
  onChange,
}: {
  attribution: JobLeadAttribution;
  canEdit?: boolean;
  onChange: () => void;
}) {
  const lead = attribution.currentLead;
  const enteredAt = getLeadEnteredAt(lead);
  const source = getLeadSourceDisplay(lead);
  const hasCandidates = attribution.candidateLeads.length > 0;

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <SectionEyebrow>Lead Attribution</SectionEyebrow>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <p className="text-lg font-semibold text-foreground">
                {lead ? source : "No linked lead"}
              </p>
              {attribution.candidateLeads.length > 1 && (
                <Badge variant="outline" className="text-[10px]">
                  {attribution.candidateLeads.length} possible leads
                </Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {lead
                ? `Entered system ${formatLeadAttributionDate(enteredAt)}`
                : "No lead attribution resolved for this job yet."}
            </p>
          </div>
          {canEdit && (
            <Button type="button" variant="outline" size="sm" onClick={onChange} disabled={!hasCandidates}>
              Change lead
            </Button>
          )}
        </div>

        {lead ? (
          <div className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-4">
            <div className="rounded-md border bg-muted/20 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Provider</p>
              <p className="mt-1 truncate font-medium">{lead.provider ?? "—"}</p>
            </div>
            <div className="rounded-md border bg-muted/20 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Source ID</p>
              <p className="mt-1 truncate font-medium">{lead.sourceId ?? "—"}</p>
            </div>
            <div className="rounded-md border bg-muted/20 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Lead Cost</p>
              <p className="mt-1 font-medium">{formatLeadCost(lead.leadCost)}</p>
            </div>
            <div className="rounded-md border bg-muted/20 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Lead Status</p>
              <p className="mt-1 truncate font-medium">{lead.status ?? lead.stage ?? "—"}</p>
            </div>
          </div>
        ) : hasCandidates ? (
          <div className="mt-4 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-warning">
            Candidate leads exist for this customer. Choose one to credit the job/sale.
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ScopeDetailsCard({
  job,
  canEdit,
  onEdit,
  onManageMaterials,
}: {
  job: Job;
  canEdit?: boolean;
  onEdit?: () => void;
  onManageMaterials?: () => void;
}) {
  const scope = String(job.scopeSummary ?? "").trim();
  const notes = String(job.notes ?? "").trim();
  const showNotes = notes.length > 0 && notes !== scope;
  const materials = Array.isArray(job.materialsList) ? job.materialsList : [];
  const fieldMaterials = Array.isArray(job.fieldMaterials) ? job.fieldMaterials : [];
  const unmatchedSkus = Array.isArray(job.unmappedSkusForReview) ? job.unmappedSkusForReview : [];
  const photos = Array.isArray(job.photos) ? job.photos : [];
  const hasScopeContent = scope.length > 0 || showNotes;

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <div>
              <SectionEyebrow>Scope & Work Details</SectionEyebrow>
              <p className="text-xs text-muted-foreground">
                Imported scope, notes, and supporting job context.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={onEdit}>
                Edit job
              </Button>
            )}
            {canEdit && (
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={onManageMaterials}>
                Manage materials
              </Button>
            )}
            {materials.length > 0 && (
              <Badge variant="secondary" className="gap-1 text-xs">
                <Package className="h-3 w-3" />
                {materials.length} material{materials.length === 1 ? "" : "s"}
              </Badge>
            )}
            {fieldMaterials.length > 0 && (
              <Badge variant="outline" className="gap-1 text-xs">
                <Wrench className="h-3 w-3" />
                {fieldMaterials.length} field purchase{fieldMaterials.length === 1 ? "" : "s"}
              </Badge>
            )}
            {unmatchedSkus.length > 0 && (
              <Badge variant="outline" className="gap-1 text-xs border-warning/40 text-warning">
                {unmatchedSkus.length} unmatched SKU{unmatchedSkus.length === 1 ? "" : "s"}
              </Badge>
            )}
            {photos.length > 0 && (
              <Badge variant="secondary" className="gap-1 text-xs">
                <Camera className="h-3 w-3" />
                {photos.length} photo{photos.length === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
</div>

        <div className="mt-4 space-y-4">
          {scope && (
            <div className="rounded-md border bg-muted/20 p-3">
              <div className="mb-2 flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-primary/40" />
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Scope Summary
                </div>
              </div>
              <p className="whitespace-pre-line text-sm leading-6 text-foreground">{scope}</p>
            </div>
          )}

          {showNotes && (
            <div className="rounded-md border bg-muted/20 p-3">
              <div className="mb-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Description / Notes
              </div>
              <p className="whitespace-pre-line text-sm leading-6 text-muted-foreground">{notes}</p>
            </div>
          )}

          {!hasScopeContent && (
            <div className="rounded-md border border-dashed bg-muted/10 p-3 text-sm text-muted-foreground">
              No scope or description has been captured for this job yet.
            </div>
          )}

          {materials.length > 0 && (
            <div className="rounded-md border bg-muted/10 p-3">
              <div className="mb-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Captured Materials
              </div>
              <div className="flex flex-wrap gap-1.5">
                {materials.slice(0, 8).map((material, index) => {
                  const label = material.itemName || material.sku || `Material ${index + 1}`;
                  const qty = material.quantityRequired > 0 ? ` (${material.quantityRequired})` : "";
                  return (
                        <Badge
                          key={`${label}-${index}`}
                          variant="outline"
                          className="max-w-full truncate text-[11px]"
                        >
                          {label}{qty}
                        </Badge>
                      );
                })}
                {materials.length > 8 && (
                  <Badge variant="outline" className="text-[11px]">
                    +{materials.length - 8} more
                  </Badge>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ActionRequiredPanel({ blockers, suggested, nextAction, lastAct, hasBlocker, onActionClick }: {
  blockers: ReturnType<typeof getJobBlockers>;
  suggested: SuggestedAction[];
  nextAction: ReturnType<typeof getJobNextAction>;
  lastAct: ReturnType<typeof getLastActivity>;
  hasBlocker: boolean;
  onActionClick: (a: SuggestedAction) => void;
}) {
  // Dedup blockers already represented by an action
  const actionKeywords = new Set(suggested.flatMap((a) => [a.icon, a.label.toLowerCase()]));
  const dedupedBlockers = blockers.filter((b) => {
    const lbl = b.label.toLowerCase();
    if (actionKeywords.has("permit") && lbl.includes("permit")) return false;
    if (actionKeywords.has("materials") && (lbl.includes("material") || lbl.includes("missing"))) return false;
    if (actionKeywords.has("crew") && (lbl.includes("crew") || lbl.includes("assign"))) return false;
    if (actionKeywords.has("schedule") && lbl.includes("schedul")) return false;
    if (actionKeywords.has("issue") && lbl.includes("issue")) return false;
    if (actionKeywords.has("qa") && lbl.includes("qa")) return false;
    return true;
  });

  const prio = (p: string) => (p === "high" ? 0 : p === "medium" ? 1 : 2);
  const sev = (s: string) => (s === "critical" ? 0 : s === "warning" ? 1 : 2);
  const sortedBlockers = [...dedupedBlockers].sort((a, b) => sev(a.severity) - sev(b.severity));
  const remaining = nextAction ? suggested.filter((a) => !(a.label === nextAction.label && a.icon === nextAction.icon)) : suggested;
  const sortedActions = [...remaining].sort((a, b) => prio(a.priority) - prio(b.priority));

  const MAX = 4;
  const blockerSlots = Math.min(sortedBlockers.length, MAX);
  const visibleBlockers = sortedBlockers.slice(0, blockerSlots);
  const visibleActions = sortedActions.slice(0, MAX - blockerSlots);
  const total = dedupedBlockers.length + suggested.length;

  if (total === 0) {
    return (
      <div className="rounded-lg border border-success/30 bg-success/[0.03] p-4 flex items-center gap-3">
        <CheckCircle className="h-5 w-5 text-success shrink-0" />
        <div>
          <p className="text-sm font-bold text-success">No actions needed</p>
          <p className="text-xs text-muted-foreground">Job is on track. All gates clear.</p>
        </div>
      </div>
    );
  }

  return (
    <Card className={`shadow-none border-2 ${hasBlocker ? "border-destructive/30 bg-destructive/[0.02]" : "border-primary/20 bg-primary/[0.015]"}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between pb-2 mb-3 border-b border-border/70">
          <div className="flex items-center gap-2">
            {hasBlocker ? <OctagonAlert className="h-4 w-4 text-destructive" /> : <Zap className="h-4 w-4 text-primary" />}
            <h2 className={`text-sm font-bold ${hasBlocker ? "text-destructive" : ""}`}>Action Required</h2>
            <span className="text-[10px] font-semibold text-muted-foreground bg-muted/60 rounded-full px-2 py-0.5">{total}</span>
          </div>
           {lastAct && (
             <span className="text-[10px] text-muted-foreground hidden sm:inline">
               Last activity: <span className="font-medium text-foreground">{formatRelativeTime(lastAct.date)}</span> by {displayIdentity(lastAct.user, "User")}
             </span>
           )}
        </div>

        {nextAction && (() => {
          const NextIcon = ACTION_ICONS[nextAction.icon] || Zap;
          const ctaLabel = nextAction.cta || nextAction.label;
          return (
            <div className="mb-3 rounded-lg border-2 border-primary bg-primary/[0.06] px-4 py-3 flex items-start gap-3">
              <div className="p-2 rounded-md bg-primary/15 border border-primary/30 shrink-0">
                <NextIcon className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-primary bg-primary/10 border border-primary/30 rounded px-1.5 py-0.5">Next Step</span>
                  <p className="text-sm font-bold truncate">{nextAction.label}</p>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{nextAction.description}</p>
              </div>
              <PermissionGate permission="jobs_edit">
                <Button size="sm" onClick={() => onActionClick(nextAction)} className="h-8 gap-1 text-xs font-bold shrink-0">
                  {ctaLabel} <ArrowRight className="h-3 w-3" />
                </Button>
              </PermissionGate>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {visibleBlockers.map((b, i) => (
            <div key={`b-${i}`} className={`flex items-start gap-2.5 rounded-md border px-3 py-2 ${
              b.severity === "critical" ? "border-destructive/30 bg-destructive/5" :
              b.severity === "warning" ? "border-warning/30 bg-warning/5" : "border-muted bg-muted/20"
            }`}>
              {b.severity === "critical" ? <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" /> : <Clock className="h-4 w-4 text-warning mt-0.5 shrink-0" />}
              <div className="min-w-0">
                <p className={`text-xs font-semibold ${b.severity === "critical" ? "text-destructive" : "text-warning"}`}>{b.label}</p>
                <p className="text-[11px] text-muted-foreground">{b.detail}</p>
              </div>
            </div>
          ))}
          {visibleActions.map((a, i) => {
            const Icon = ACTION_ICONS[a.icon] || Zap;
            const isHigh = a.priority === "high";
            return (
              <div key={`a-${i}`} className={`flex items-start gap-2.5 rounded-md border px-3 py-2 ${isHigh ? "border-primary/30 bg-primary/[0.03]" : "border-border bg-muted/20"}`}>
                <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${isHigh ? "text-primary" : "text-muted-foreground"}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-semibold ${isHigh ? "text-primary" : "text-foreground"}`}>{a.label}</p>
                  <p className="text-[11px] text-muted-foreground">{a.description}</p>
                  <PermissionGate permission="jobs_edit">
                    <Button size="sm" variant={isHigh ? "default" : "outline"} onClick={() => onActionClick(a)} className="h-7 gap-1 text-[11px] font-semibold px-2.5 mt-1.5">
                      {a.cta || a.label} <ArrowRight className="h-2.5 w-2.5" />
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function ExecutionCard({ icon: Icon, label, rightLink, rows, cta, ctaPermission, footer }: {
  icon: React.ElementType;
  label: string;
  rightLink?: { to: string; label: string };
  rows: Array<[string, React.ReactNode]>;
  cta?: { to: string; label: string };
  /** If provided, the CTA link is permission-gated — shows disabled tooltip when lacking access */
  ctaPermission?: string | string[];
  footer?: React.ReactNode;
}) {
  const { hasPermission } = usePermissions();

  const ctaAllowed = ctaPermission == null
    ? true
    : Array.isArray(ctaPermission)
      ? ctaPermission.some((p) => hasPermission(p))
      : hasPermission(ctaPermission);

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <div className="flex items-center justify-between pb-1.5 mb-2 border-b">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">{label}</h3>
          </div>
          {rightLink && (
            <Link to={rightLink.to} className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
              {rightLink.label} <ExternalLink className="h-2.5 w-2.5" />
            </Link>
          )}
        </div>
        <div className="divide-y">
          {rows.map(([k, v], i) => (
            <div key={i} className="flex items-center justify-between py-1.5">
              <span className="text-xs text-muted-foreground">{k}</span>
              <span className="text-xs font-medium text-right max-w-[60%]">{v}</span>
            </div>
          ))}
        </div>
        {cta && (
          ctaAllowed ? (
            <Link to={cta.to} className="mt-3 inline-flex items-center justify-center w-full gap-1 px-3 py-1.5 text-[11px] font-semibold rounded-md bg-warning/10 text-warning border border-warning/30 hover:bg-warning/15 transition-colors">
              {cta.label} <ArrowRight className="h-2.5 w-2.5" />
            </Link>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="mt-3 inline-flex items-center justify-center w-full gap-1 px-3 py-1.5 text-[11px] font-semibold rounded-md bg-muted/30 text-muted-foreground border border-border/50 opacity-50 cursor-not-allowed">
                  {cta.label} <ArrowRight className="h-2.5 w-2.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="flex items-center gap-1.5 text-xs max-w-[220px]">
                <Lock className="h-3 w-3 shrink-0" />
                You don't have permission for this action
              </TooltipContent>
            </Tooltip>
          )
        )}
        {footer}
      </CardContent>
    </Card>
  );
}

function FinancialSummaryStrip({ job, openIssues, credits, invoiceRefs, onInvoiceClick }: { job: import("@/data/types").Job; openIssues: import("@/data/types").Issue[]; credits: import("@/data/types").CustomerCredit[]; invoiceRefs: Array<{ id: string; invoiceNumber: string | null; type: string; status: string }>; onInvoiceClick?: () => void }) {
  const fin$ = useFinancialPermissions();
  const fin = getJobFinancialSummary(job, credits);
  const collect = getCollectionSummary(job);
  const financeApplicationPaymentDisplay = getFinancingApplicationPaymentDisplay(job);
  const priority = getJobPriority(job, openIssues, credits);
  const { journalEntries } = useGeneralLedger();
  // Installer-reimbursed field materials posted to this job's WIP — GL-derived,
  // deliberately separate from job.fieldMaterials (the staff-entered draft rows
  // already folded into JobEconomicsBreakdown's Material Cost tile) to avoid
  // double-counting the same underlying spend from two unreconciled sources.
  const fieldMaterialReimbursement = useMemo(() => {
    let total = 0;
    for (const je of journalEntries) {
      if (je.source !== "installer_reimbursement" || je.status === "void") continue;
      for (const line of je.lines) {
        if (line.side === "debit" && line.accountId === "1200" && line.jobId === job.id) {
          total += line.amount;
        }
      }
    }
    return total;
  }, [journalEntries, job.id]);
  const netContract = Math.max(0, fin.currentContract - fin.totalCredits);
  const netOutstanding = Math.max(0, netContract - collect.totalPaid);
  const priorityColor = priority.level === "Critical"
    ? "text-destructive bg-destructive/10 border-destructive/20"
    : priority.level === "Attention Needed"
    ? "text-warning bg-warning/10 border-warning/20"
    : "text-success bg-success/10 border-success/20";

  const statusLabel: Record<string, string> = {
    no_invoice_plan: "No Invoice Plan",
    deposit_pending: "Deposit Pending",
    deposit_paid: "Deposit Paid",
    progress_pending: "Progress Pending",
    final_pending: "Final Pending",
    paid_in_full: "Paid in Full",
    overdue: "Overdue",
  };
  const statusTone =
    collect.paymentStatus === "overdue" ? "text-destructive"
    : collect.paymentStatus === "paid_in_full" ? "text-success"
    : collect.paymentStatus === "no_invoice_plan" ? "text-muted-foreground"
    : "text-warning";
  const resolvedStatusLabel = financeApplicationPaymentDisplay?.label
    ?? statusLabel[collect.paymentStatus]
    ?? collect.paymentStatus;
  const resolvedStatusTone = financeApplicationPaymentDisplay?.textTone ?? statusTone;
  const outstandingTone = netOutstanding > 0 ? "text-destructive" : "text-success";

  const coreItems = [
    fin$.viewContractValue && { label: "Contract", value: `$${fin.currentContract.toLocaleString()}`, tone: "text-primary" },
    fin$.viewContractValue && { label: "Invoiced", value: `$${collect.totalInvoiced.toLocaleString()}`, tone: collect.totalInvoiced === 0 ? "text-muted-foreground" : "" },
    fin$.viewContractValue && { label: "Collected", value: `$${collect.totalPaid.toLocaleString()}`, tone: collect.totalPaid > 0 ? "text-success" : "text-muted-foreground" },
    fin$.viewContractValue && { label: "Outstanding", value: `$${netOutstanding.toLocaleString()}`, tone: outstandingTone },
    { label: "Status", value: resolvedStatusLabel, tone: resolvedStatusTone },
  ].filter(Boolean) as Array<{ label: string; value: string; tone: string }>;

  const conditionalItems = [
    fin$.viewChangeOrderValue && fin.changeOrderCount > 0 && {
      label: "Change Orders",
      value: `$${fin.totalChangeOrderValue.toLocaleString()}`,
      tone: "",
    },
    fin$.viewCredits && fin.creditCount > 0 && {
      label: "Credits",
      value: `$${fin.totalCredits.toLocaleString()}`,
      tone: "text-destructive",
    },
    fin$.viewInternalCost && fieldMaterialReimbursement > 0 && {
      label: "Field Materials",
      value: `$${fieldMaterialReimbursement.toLocaleString()}`,
      tone: "",
    },
  ].filter(Boolean) as Array<{ label: string; value: string; tone: string }>;

  const items = [...coreItems, ...conditionalItems];

  if (items.length === 0) return null;

  return (
    <Card className="shadow-none">
      <CardContent className="p-4">
        <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Financial Summary</h2>
          </div>
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${priorityColor}`}>{priority.level}</span>
        </div>
        <div className={`grid grid-cols-2 gap-3 ${
          items.length >= 5 ? "sm:grid-cols-5" :
          items.length === 4 ? "sm:grid-cols-4" :
          items.length === 3 ? "sm:grid-cols-3" :
          items.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1"
        }`}>
          {items.map((it) => (
            <div key={it.label} className="p-3 rounded-md border bg-muted/20 text-center">
              <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">{it.label}</p>
              <p className={`text-base font-bold mt-0.5 ${it.tone}`}>{it.value}</p>
            </div>
          ))}
        </div>
        {invoiceRefs.length > 0 && (
          <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Invoices</span>
            {invoiceRefs.map((ref) => {
              const statusLabel =
                ref.status === "paid" ? "Paid" :
                ref.status === "issued" || ref.status === "partially_paid" ? "Sent" :
                ref.status === "overdue" ? "Overdue" :
                "Draft";
              const statusColor =
                ref.status === "paid" ? "text-success border-success/30 bg-success/5" :
                ref.status === "overdue" ? "text-destructive border-destructive/30 bg-destructive/5" :
                ref.status === "issued" || ref.status === "partially_paid" ? "text-primary border-primary/30 bg-primary/5" :
                "text-muted-foreground border-border/40 bg-muted/30";
              return (
                <button
                  key={ref.id}
                  type="button"
                  onClick={onInvoiceClick}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10px] font-medium transition-opacity hover:opacity-70 ${statusColor}`}
                >
                  <span className="font-mono tabular-nums">{ref.invoiceNumber ?? "—"}</span>
                  <span className="text-[9px] opacity-70">·</span>
                  <span className="capitalize">{statusLabel}</span>
                </button>
              );
            })}
          </div>
        )}
        {priority.reasons.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {priority.reasons.map((r) => (
              <span key={r} className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium ${priorityColor}`}>{r}</span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

type PortalChecklistPhaseSummary = {
  phaseKey: JobChecklistPhaseKey;
  phaseLabel: string;
  total: number;
  checked: number;
  items: PortalChecklistReviewItem[];
};

type PortalChecklistReviewItem = {
  id: string;
  label: string;
  checked: boolean;
  proofLabel: string | null;
  requiresProof: boolean;
  itemIndex: number;
  sortOrder: number;
};

type PortalChecklistQaStatus = "pending" | "passed" | "failed";

type PortalChecklistPhaseReview = {
  phaseKey: JobChecklistPhaseKey;
  phaseLabel: string;
  qaStatus: PortalChecklistQaStatus;
  qaReviewedAt: string | null;
  qaFailReason: string | null;
};

function normalizeProofCaption(value: string | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}

function captionMatchesPhase(caption: string, phaseLabel: string): boolean {
  const cap = normalizeProofCaption(caption);
  const label = normalizeProofCaption(phaseLabel);
  if (!cap || !label) return false;
  return (
    cap === label ||
    cap === normalizeProofCaption(`Additional (${phaseLabel})`) ||
    cap.includes(`(${label})`)
  );
}

function isProofImage(proof: ExecutionProof): boolean {
  return String(proof.mime_type ?? "").startsWith("image/");
}

function formatProofBytes(n: number | null): string {
  if (!n || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ChecklistProofTile({
  proof,
  getReadUrl,
  onOpen,
}: {
  proof: ExecutionProof;
  getReadUrl: (proofId: string) => Promise<string | null>;
  onOpen: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!isProofImage(proof)) {
      setPreviewUrl(null);
      return () => {
        cancelled = true;
      };
    }
    void getReadUrl(proof.id).then((url) => {
      if (!cancelled) setPreviewUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [proof, getReadUrl]);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group overflow-hidden rounded-md border bg-background/60 text-left hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      title={proof.caption ?? proof.file_name ?? "Installer proof"}
    >
      <div className="h-20 w-full bg-muted/50 flex items-center justify-center">
        {previewUrl ? (
          <img src={previewUrl} alt={proof.caption ?? proof.file_name ?? "Installer proof"} className="h-full w-full object-cover transition group-hover:scale-[1.02]" loading="lazy" />
        ) : (
          <Camera className="h-5 w-5 text-muted-foreground" />
        )}
      </div>
      <div className="space-y-0.5 p-2">
        <p className="truncate text-[11px] font-medium text-foreground">{proof.caption ?? proof.file_name ?? "Proof"}</p>
        <p className="truncate text-[10px] text-muted-foreground">
          {[formatRelativeTime(proof.uploaded_at), formatProofBytes(proof.byte_size)].filter(Boolean).join(" · ")}
        </p>
      </div>
    </button>
  );
}

function QualityZone({ job, openIssues, resolvedIssues, onOpenExecution, onGenerateBoundSpec, isGeneratingBoundSpec, canReviewQA, linkedQuoteId, executionProofs, isExternalManaged }: {
  job: import("@/data/types").Job;
  openIssues: import("@/data/types").Issue[];
  resolvedIssues: import("@/data/types").Issue[];
  onOpenExecution: () => void;
  onGenerateBoundSpec: () => void;
  isGeneratingBoundSpec: boolean;
  canReviewQA: boolean;
  linkedQuoteId: string | null;
  executionProofs: Array<{ id: string; url: string }>;
  isExternalManaged: boolean;
}) {
  const { specExecutions } = useAppData();
  const allIssues = useMemo(() => [...openIssues, ...resolvedIssues], [openIssues, resolvedIssues]);
  const [portalChecklistPhases, setPortalChecklistPhases] = useState<PortalChecklistPhaseSummary[]>([]);
  const [portalChecklistReviews, setPortalChecklistReviews] = useState<PortalChecklistPhaseReview[]>([]);
  const [portalChecklistLoading, setPortalChecklistLoading] = useState(false);
  const [proofGallery, setProofGallery] = useState<{ proofs: ExecutionProof[]; index: number } | null>(null);
  const [proofGalleryUrl, setProofGalleryUrl] = useState<string | null>(null);
  const [proofGalleryLoading, setProofGalleryLoading] = useState(false);

  const loadPortalChecklistReviews = useCallback(async () => {
    const { data, error } = await supabase
      .from("job_checklist_phase_reviews")
      .select("phase_key, phase_label, qa_status, qa_reviewed_at, qa_fail_reason")
      .eq("job_id", job.id);

    if (error) {
      console.warn("[QualityZone] Failed to load portal checklist QA reviews:", error);
      setPortalChecklistReviews([]);
      return;
    }

    const reviews = ((data ?? []) as Array<Record<string, unknown>>)
      .map((row): PortalChecklistPhaseReview | null => {
        const phaseRaw = String(row.phase_key ?? "");
        if (!JOB_CHECKLIST_PHASES.includes(phaseRaw as JobChecklistPhaseKey)) return null;
        const qaRaw = String(row.qa_status ?? "pending");
        const qaStatus: PortalChecklistQaStatus =
          qaRaw === "passed" || qaRaw === "failed" ? qaRaw : "pending";
        const phaseKey = phaseRaw as JobChecklistPhaseKey;
        return {
          phaseKey,
          phaseLabel: String(row.phase_label ?? "").trim() || DEFAULT_JOB_CHECKLIST_PHASE_LABELS[phaseKey],
          qaStatus,
          qaReviewedAt: String(row.qa_reviewed_at ?? "").trim() || null,
          qaFailReason: String(row.qa_fail_reason ?? "").trim() || null,
        };
      })
      .filter(Boolean) as PortalChecklistPhaseReview[];

    setPortalChecklistReviews(reviews);
  }, [job.id]);

  useEffect(() => {
    let cancelled = false;
    const loadPortalChecklistSummary = async () => {
      setPortalChecklistLoading(true);
      const fetchRows = async () => supabase
        .from("job_checklist_items")
        .select("id, phase_key, phase_label, label, proof_label, requires_proof, sort_order, item_index, is_custom, is_active, checked")
        .eq("job_id", job.id)
        .eq("is_active", true);

      const { data, error } = await fetchRows();

      if (cancelled) return;

      if (error) {
        setPortalChecklistLoading(false);
        console.warn("[QualityZone] Failed to load portal checklist summary:", error);
        setPortalChecklistPhases([]);
        return;
      }

      // Auto-sync: if the table has no rows for this job but the job's JSONB checklist
      // has items (set via Job Edit or portal), populate job_checklist_items now.
      let resolvedData = data ?? [];
      if (resolvedData.length === 0 && job.checklist?.items?.some((item) => String(item.label ?? "").trim())) {
        const { error: syncErr } = await syncJobChecklistItems({
          id: job.id,
          organizationId: job.organizationId,
          projectType: job.projectType,
          scopeSummary: job.scopeSummary,
          notes: job.notes,
          projectConfig: job.projectConfig,
          checklist: job.checklist,
          currentStage: job.currentStage,
          dispatchStatus: job.dispatchStatus,
          installStatus: job.installStatus,
        });
        if (!syncErr && !cancelled) {
          const { data: fresh } = await fetchRows();
          if (fresh) resolvedData = fresh;
        }
      }

      if (cancelled) {
        setPortalChecklistLoading(false);
        return;
      }
      setPortalChecklistLoading(false);

      const customRows = (resolvedData as Array<Record<string, unknown>>)
        .filter((row) => row.is_custom === true && String(row.label ?? "").trim())
        .map((row) => {
          const phaseRaw = String(row.phase_key ?? "");
          const phaseKey = JOB_CHECKLIST_PHASES.includes(phaseRaw as JobChecklistPhaseKey)
            ? phaseRaw as JobChecklistPhaseKey
            : "custom";
          return {
            id: String(row.id ?? ""),
            phaseKey,
            phaseLabel: String(row.phase_label ?? "").trim() || DEFAULT_JOB_CHECKLIST_PHASE_LABELS[phaseKey],
            label: String(row.label ?? "").trim(),
            checked: row.checked === true,
            proofLabel: String(row.proof_label ?? "").trim() || null,
            requiresProof: row.requires_proof === true,
            sortOrder: Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : Number.MAX_SAFE_INTEGER,
            itemIndex: Number.isFinite(Number(row.item_index)) ? Number(row.item_index) : Number.MAX_SAFE_INTEGER,
          };
        })
        .sort((a, b) => {
          const phaseDelta = JOB_CHECKLIST_PHASES.indexOf(a.phaseKey) - JOB_CHECKLIST_PHASES.indexOf(b.phaseKey);
          if (phaseDelta !== 0) return phaseDelta;
          if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
          return a.itemIndex - b.itemIndex;
        });

      const byPhase = new Map<JobChecklistPhaseKey, PortalChecklistPhaseSummary>();
      for (const row of customRows) {
        const existing = byPhase.get(row.phaseKey);
        const item: PortalChecklistReviewItem = {
          id: row.id || `${row.phaseKey}:${row.itemIndex}`,
          label: row.label,
          checked: row.checked,
          proofLabel: row.proofLabel,
          requiresProof: row.requiresProof,
          itemIndex: row.itemIndex,
          sortOrder: row.sortOrder,
        };
        if (existing) {
          existing.total += 1;
          if (row.checked) existing.checked += 1;
          existing.items.push(item);
        } else {
          byPhase.set(row.phaseKey, {
            phaseKey: row.phaseKey,
            phaseLabel: row.phaseLabel,
            total: 1,
            checked: row.checked ? 1 : 0,
            items: [item],
          });
        }
      }

      setPortalChecklistPhases(Array.from(byPhase.values()));
    };

    void loadPortalChecklistSummary();
    void loadPortalChecklistReviews();

    return () => {
      cancelled = true;
    };
  }, [job.id, loadPortalChecklistReviews]);

  const portalChecklistReviewByPhase = useMemo(() => {
    return new Map(portalChecklistReviews.map((review) => [review.phaseKey, review]));
  }, [portalChecklistReviews]);

  const reviewPortalChecklistPhase = useCallback(async (
    phase: PortalChecklistPhaseSummary,
    qaStatus: Exclude<PortalChecklistQaStatus, "pending">,
  ) => {
    const checkedComplete = phase.total > 0 && phase.checked === phase.total;
    if (qaStatus === "passed" && !checkedComplete) {
      toast.error("Checklist is not complete yet.", {
        description: `${phase.phaseLabel} is ${phase.checked}/${phase.total} checked.`,
      });
      return;
    }

    const reason = qaStatus === "failed"
      ? window.prompt(`Why is "${phase.phaseLabel}" failing QA?`)
      : null;
    if (qaStatus === "failed" && !String(reason ?? "").trim()) {
      toast.error("A fail reason is required.");
      return;
    }

    const organizationId = String(job.organizationId ?? "").trim() || await getCurrentOrganizationId();
    if (!organizationId) {
      toast.error("Missing organization context for checklist QA.");
      return;
    }

    const { data: authData } = await supabase.auth.getUser();
    const now = new Date().toISOString();
    const { error } = await supabase
      .from("job_checklist_phase_reviews")
      .upsert({
        job_id: job.id,
        organization_id: organizationId,
        phase_key: phase.phaseKey,
        phase_label: phase.phaseLabel,
        qa_status: qaStatus,
        qa_reviewed_by: authData.user?.id ?? null,
        qa_reviewed_at: now,
        qa_fail_reason: qaStatus === "failed" ? String(reason ?? "").trim() : null,
        updated_at: now,
      }, { onConflict: "job_id,phase_key" });

    if (error) {
      console.error("[QualityZone] Failed to save checklist QA review:", error);
      toast.error("Failed to save checklist QA review", {
        description: error.message,
      });
      return;
    }

    toast.success(qaStatus === "passed" ? "Checklist phase passed QA" : "Checklist phase failed QA", {
      description: phase.phaseLabel,
    });
    await loadPortalChecklistReviews();
  }, [job.id, job.organizationId, loadPortalChecklistReviews]);

  const hasPortalChecklistSummary = portalChecklistPhases.length > 0;

  const risk = useMemo(() => computeServiceCallRisk(job, allIssues), [job, allIssues]);
  const riskCfg = RISK_CONFIG[risk.level];

  const exec = useMemo(() => specExecutions.find((e) => e.jobId === job.id), [specExecutions, job.id]);
  const execSummary = useMemo(() => {
    if (!job.boundJobSpec || !exec) return null;
    const phases = exec.phases ?? [];
    // executionProofs is the canonical source for portal-uploaded photos.
    // Fall back to phase.photos count for legacy Koredor-side uploads.
    const photosUploaded = executionProofs.length > 0
      ? executionProofs.length
      : phases.reduce((acc, p) => acc + (p.photos?.length ?? 0), 0);
    const checksTotal = phases.reduce((acc, p) => acc + (p.checklist?.length ?? 0), 0);
    const checksDone = phases.reduce((acc, p) => acc + (p.checklist?.filter((c) => c.checked).length ?? 0), 0);
    const passed = phases.filter((p) => p.status === "qa_passed").length;
    const submitted = phases.filter((p) => p.status === "submitted").length;
    const lastSubmit = phases.map((p) => p.completedAt).filter(Boolean).sort().reverse()[0];
    return { phases: phases.length, passed, submitted, photosUploaded, checksDone, checksTotal, lastSubmit };
  }, [exec, job.boundJobSpec, executionProofs]);
  const portalChecklistSummary = useMemo(() => {
    const checksTotal = portalChecklistPhases.reduce((acc, phase) => acc + phase.total, 0);
    const checksDone = portalChecklistPhases.reduce((acc, phase) => acc + phase.checked, 0);
    const phasesComplete = portalChecklistPhases.filter((phase) => phase.total > 0 && phase.checked === phase.total).length;
    const reviewablePhases = portalChecklistPhases.filter((phase) => phase.total > 0 && phase.checked === phase.total);
    const phasesQaPassed = reviewablePhases.filter((phase) => portalChecklistReviewByPhase.get(phase.phaseKey)?.qaStatus === "passed").length;
    const phasesQaFailed = portalChecklistPhases.filter((phase) => portalChecklistReviewByPhase.get(phase.phaseKey)?.qaStatus === "failed").length;
    return { checksTotal, checksDone, phases: portalChecklistPhases.length, phasesComplete, phasesQaPassed, phasesQaFailed, reviewablePhases: reviewablePhases.length };
  }, [portalChecklistPhases, portalChecklistReviewByPhase]);
  const ledger = useExecutionProofs({ appJobId: job.id });
  const portalProofsByCaption = useMemo(() => {
    const map = new Map<string, ExecutionProof[]>();
    for (const proof of ledger.proofs) {
      if (proof.superseded_by) continue;
      const key = normalizeProofCaption(proof.caption);
      if (!key) continue;
      const list = map.get(key) ?? [];
      list.push(proof);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
    }
    return map;
  }, [ledger.proofs]);
  const ledgerQa = useMemo(
    () => deriveLedgerQaReadiness(ledger.executions, ledger.proofs),
    [ledger.executions, ledger.proofs],
  );
  const prototypeSubmitted = Boolean(job.boundJobSpec && exec?.phases.some((p) => p.status === "submitted"));
  const hasPrototypeExecution = Boolean(exec && exec.phases.length > 0);
  const hasModernQaFlow = hasPortalChecklistSummary || hasPrototypeExecution || ledgerQa.hasRealExecution;
  const derivedQa = useMemo(() => {
    if (hasPortalChecklistSummary) {
      const allChecklistPhasesPassed =
        portalChecklistSummary.phases > 0 &&
        portalChecklistSummary.phasesComplete === portalChecklistSummary.phases &&
        portalChecklistSummary.reviewablePhases > 0 &&
        portalChecklistSummary.phasesQaPassed === portalChecklistSummary.reviewablePhases;

      if (portalChecklistSummary.phasesQaFailed > 0) {
        return { label: "Failed", tone: "bad" as const, legacySubmitNeeded: false };
      }
      if (allChecklistPhasesPassed) {
        return { label: "Passed", tone: "good" as const, legacySubmitNeeded: false };
      }
      if (portalChecklistSummary.reviewablePhases > portalChecklistSummary.phasesQaPassed) {
        return { label: "Awaiting Review", tone: "warn" as const, legacySubmitNeeded: false };
      }
      if (portalChecklistSummary.checksDone > 0) {
        return { label: "In Progress", tone: "default" as const, legacySubmitNeeded: false };
      }
    }

    if (hasPrototypeExecution && execSummary) {
      const hasFailedPhase = exec.phases.some((phase) => phase.status === "qa_failed");
      if (hasFailedPhase) {
        return { label: "Failed", tone: "bad" as const, legacySubmitNeeded: false };
      }
      if (execSummary.phases > 0 && execSummary.passed === execSummary.phases) {
        return { label: "Passed", tone: "good" as const, legacySubmitNeeded: false };
      }
      if (execSummary.submitted > 0 || ledgerQa.hasSubmittedExecution) {
        return { label: "Awaiting Review", tone: "warn" as const, legacySubmitNeeded: false };
      }
      if (execSummary.checksDone > 0 || execSummary.photosUploaded > 0) {
        return { label: "In Progress", tone: "default" as const, legacySubmitNeeded: false };
      }
    }

    if (ledgerQa.hasSubmittedExecution) {
      return {
        label: ledgerQa.latestExecutionStatus === "completed" ? "Passed" : "Awaiting Review",
        tone: ledgerQa.latestExecutionStatus === "completed" ? ("good" as const) : ("warn" as const),
        legacySubmitNeeded: false,
      };
    }

    if (ledgerQa.hasProofs || hasModernQaFlow) {
      return { label: "In Progress", tone: "default" as const, legacySubmitNeeded: false };
    }

    if (job.qaSubmitted) {
      return { label: "Submitted", tone: "good" as const, legacySubmitNeeded: false };
    }

    return { label: "Pending", tone: "warn" as const, legacySubmitNeeded: true };
  }, [
    exec,
    execSummary,
    hasModernQaFlow,
    hasPortalChecklistSummary,
    hasPrototypeExecution,
    job.qaSubmitted,
    ledgerQa,
    portalChecklistSummary,
  ]);
  const showQaPanel = canReviewQA && !isExternalManaged && (prototypeSubmitted || ledgerQa.readyForQaReview);
  const qaSourceLabel = ledgerQa.hasRealExecution ? "Live execution ledger" : "Prototype execution state";
  const activeProof = proofGallery?.proofs[proofGallery.index] ?? null;
  const proofGalleryCount = proofGallery?.proofs.length ?? 0;

  useEffect(() => {
    let cancelled = false;
    if (!activeProof) {
      setProofGalleryUrl(null);
      setProofGalleryLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setProofGalleryLoading(true);
    setProofGalleryUrl(null);
    void ledger.getReadUrl(activeProof.id).then((url) => {
      if (cancelled) return;
      setProofGalleryLoading(false);
      if (url) setProofGalleryUrl(url);
      else toast.error("Couldn't fetch a signed view URL. Try refreshing.");
    });

    return () => {
      cancelled = true;
    };
  }, [activeProof?.id, ledger.getReadUrl]);

  const openProofGallery = useCallback((proofs: ExecutionProof[], index: number) => {
    setProofGallery({ proofs, index });
  }, []);

  const moveProofGallery = useCallback((delta: number) => {
    setProofGallery((current) => {
      if (!current || current.proofs.length === 0) return current;
      const nextIndex = (current.index + delta + current.proofs.length) % current.proofs.length;
      return { ...current, index: nextIndex };
    });
  }, []);

  return (
    <div className="space-y-5">
      {/* Execution Summary */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Execution Summary</h2>
            </div>
            <div className="flex items-center gap-2">
              {!job.boundJobSpec && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-[10px] gap-1"
                  onClick={onGenerateBoundSpec}
                  disabled={isGeneratingBoundSpec}
                >
                  <Sparkles className={`h-3 w-3 ${isGeneratingBoundSpec ? "animate-pulse" : ""}`} />
                  {isGeneratingBoundSpec ? "Generating..." : "Generate Bound Spec"}
                </Button>
              )}
              <button
                type="button"
                onClick={onOpenExecution}
                disabled={!job.boundJobSpec}
                className="text-[10px] inline-flex items-center gap-1 disabled:text-muted-foreground/60 disabled:cursor-not-allowed text-primary hover:underline disabled:no-underline"
              >
                {job.boundJobSpec ? "View Full Execution" : "Bind Spec to Open Execution"} <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          </div>
          {hasPortalChecklistSummary ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Phases Complete" value={`${portalChecklistSummary.phasesComplete}/${portalChecklistSummary.phases}`} tone={portalChecklistSummary.phases > 0 && portalChecklistSummary.phasesComplete === portalChecklistSummary.phases ? "good" : "default"} />
              <Stat label="Checklist" value={portalChecklistSummary.checksTotal > 0 ? `${Math.round((portalChecklistSummary.checksDone / portalChecklistSummary.checksTotal) * 100)}%` : "—"} tone={portalChecklistSummary.checksTotal > 0 && portalChecklistSummary.checksDone === portalChecklistSummary.checksTotal ? "good" : "default"} />
              <Stat label="Photos Captured" value={executionProofs.length} tone="default" />
              <Stat
                label="Checklist QA"
                value={portalChecklistSummary.phasesQaFailed > 0 ? "Failed" : `${portalChecklistSummary.phasesQaPassed}/${portalChecklistSummary.reviewablePhases || portalChecklistSummary.phases}`}
                tone={portalChecklistSummary.phasesQaFailed > 0 ? "bad" : portalChecklistSummary.reviewablePhases > 0 && portalChecklistSummary.phasesQaPassed === portalChecklistSummary.reviewablePhases ? "good" : "default"}
              />
            </div>
          ) : execSummary ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Phases QA Passed" value={`${execSummary.passed}/${execSummary.phases}`} tone={execSummary.phases > 0 && execSummary.passed === execSummary.phases ? "good" : execSummary.submitted > 0 ? "warn" : "default"} />
              <Stat label="Checklist" value={execSummary.checksTotal > 0 ? `${Math.round((execSummary.checksDone / execSummary.checksTotal) * 100)}%` : "—"} tone={execSummary.checksTotal > 0 && execSummary.checksDone === execSummary.checksTotal ? "good" : "default"} />
              <Stat label="Photos Captured" value={execSummary.photosUploaded} tone="default" />
              <Stat label="Last Submission" value={execSummary.lastSubmit ? formatRelativeTime(execSummary.lastSubmit) : "—"} tone="default" />
            </div>
          ) : portalChecklistLoading ? (
            <p className="text-xs text-muted-foreground">Loading portal checklist status...</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {job.boundJobSpec
                  ? "No execution activity recorded yet. Open the full panel to begin phase capture."
                  : "No JobSpec is bound to this job. Generate one from job_materials or open the full panel for spec-binding details."}
              </p>
              {!job.boundJobSpec && (
                <p className="text-[10px] text-muted-foreground">
                  Manual generation uses the current `job_materials` rows only.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {!isExternalManaged && <SpecReconciliationGate jobId={job.id} bound={job.boundJobSpec} />}

      {/* Build Integrity */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Build Integrity</h2>
            </div>
            <JobSpecDriftBadge job={job} />
          </div>
          <div className={`rounded-md border px-3 py-2.5 flex items-start gap-2.5 ${riskCfg.bg} ${riskCfg.border}`}>
            <Shield className={`h-4 w-4 mt-0.5 shrink-0 ${riskCfg.color}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className={`text-xs font-bold ${riskCfg.color}`}>Service Call Risk · {riskCfg.label}</p>
                <span className={`text-sm font-bold font-mono ${riskCfg.color}`}>{risk.score}</span>
              </div>
              {(() => {
                const active = risk.factors.filter((f) => f.present);
                return active.length > 0 ? (
                  <p className="text-[10px] text-muted-foreground mt-0.5 truncate">
                    {active.slice(0, 2).map((f) => f.label).join(" · ")}
                    {active.length > 2 && ` +${active.length - 2} more`}
                  </p>
                ) : (
                  <p className="text-[10px] text-muted-foreground mt-0.5">No elevated risk factors detected.</p>
                );
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      {showQaPanel && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-[10px]">
            <span
              className={`rounded px-1.5 py-0.5 font-semibold uppercase tracking-[0.1em] border ${
                ledgerQa.hasRealExecution
                  ? "bg-primary/10 text-primary border-primary/30"
                  : "bg-muted text-muted-foreground border-border"
              }`}
              title={qaSourceLabel}
            >
              {qaSourceLabel}
            </span>
            <span className="text-muted-foreground">{ledgerQa.reason}</span>
          </div>
          {job.boundJobSpec ? (
            <JobSpecQAPanel jobId={job.id} bound={job.boundJobSpec} />
          ) : (
            <p className="text-[11px] text-muted-foreground italic px-1">
              QA review ready from live execution ledger. JobSpec not bound — review proofs below.
            </p>
          )}
        </div>
      )}

{hasPortalChecklistSummary ? (
        <Card className="shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Phase Completion</h2>
              </div>
              <Badge
                variant={portalChecklistSummary.checksTotal > 0 && portalChecklistSummary.checksDone === portalChecklistSummary.checksTotal ? "default" : "outline"}
                className="text-[10px]"
              >
                {portalChecklistSummary.checksDone}/{portalChecklistSummary.checksTotal} checklist passed
              </Badge>
            </div>
            <div className="space-y-1.5">
              {portalChecklistPhases.map((phase) => {
                const complete = phase.total > 0 && phase.checked === phase.total;
                const active = phase.checked > 0 && !complete;
                const review = portalChecklistReviewByPhase.get(phase.phaseKey);
                const qaStatus = review?.qaStatus ?? "pending";
                const qaPassed = qaStatus === "passed";
                const qaFailed = qaStatus === "failed";
                const itemProofKeys = new Set(
                  phase.items
                    .map((item) => normalizeProofCaption(item.proofLabel || item.label))
                    .filter(Boolean),
                );
                const phaseProofs = ledger.proofs
                  .filter((proof) => {
                    if (proof.superseded_by) return false;
                    const caption = normalizeProofCaption(proof.caption);
                    if (!caption || itemProofKeys.has(caption)) return false;
                    if (captionMatchesPhase(caption, phase.phaseLabel)) return true;
                    if (portalChecklistPhases.length === 1 && (caption === "additional" || caption.startsWith("additional "))) return true;
                    return false;
                  })
                  .sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
                return (
                  <div key={phase.phaseKey} className="rounded-md bg-muted/30 px-2 py-2">
                    <div className="flex items-center gap-2.5">
                      {qaPassed ? (
                        <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                      ) : qaFailed ? (
                        <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                      ) : complete ? (
                        <Clock className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                      ) : active ? (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-primary" />
                      ) : (
                        <div className="h-3.5 w-3.5 shrink-0 rounded-full border border-muted-foreground/30" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-xs truncate ${qaPassed ? "text-foreground font-medium" : qaFailed ? "text-destructive font-medium" : complete ? "text-foreground" : "text-muted-foreground"}`}>
                            {phase.phaseLabel}
                          </span>
                          {qaFailed && review?.qaFailReason && (
                            <span className="text-[10px] text-muted-foreground truncate" title={review.qaFailReason}>
                              {review.qaFailReason}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`text-[10px] font-mono tabular-nums shrink-0 ${qaPassed ? "text-emerald-500" : qaFailed ? "text-destructive" : complete ? "text-amber-500" : active ? "text-primary" : "text-muted-foreground"}`}>
                        {qaPassed ? "QA Passed" : qaFailed ? "QA Failed" : complete ? "Awaiting QA" : `${phase.checked}/${phase.total}`}
                      </span>
                      {canReviewQA && (
                        qaPassed || qaFailed ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 shrink-0"
                                aria-label="Phase actions"
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                disabled={qaPassed}
                                onClick={() => void reviewPortalChecklistPhase(phase, "passed")}
                              >
                                <CheckCircle className="h-3.5 w-3.5 mr-2 text-emerald-500" />
                                Pass
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={qaFailed}
                                onClick={() => void reviewPortalChecklistPhase(phase, "failed")}
                                className="text-destructive focus:text-destructive"
                              >
                                <XCircle className="h-3.5 w-3.5 mr-2" />
                                Fail
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px]"
                              disabled={!complete}
                              onClick={() => void reviewPortalChecklistPhase(phase, "passed")}
                            >
                              Pass
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-6 px-2 text-[10px] text-destructive border-destructive/30 hover:text-destructive"
                              onClick={() => void reviewPortalChecklistPhase(phase, "failed")}
                            >
                              Fail
                            </Button>
                          </div>
                        )
                      )}
                    </div>
                    <div className="mt-2 space-y-1 border-t border-border/60 pt-2">
                      {phase.items.map((item) => {
                        const proofKey = normalizeProofCaption(item.proofLabel || item.label);
                        const itemProofs = proofKey ? portalProofsByCaption.get(proofKey) ?? [] : [];
                        return (
                          <div key={item.id} className="rounded-md border border-border/50 bg-background/35 p-2 text-xs">
                            <div className="flex items-start gap-2">
                              {item.checked ? (
                                <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                              ) : (
                                <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className={item.checked ? "text-foreground" : "text-muted-foreground"}>
                                  {item.label}
                                </p>
                                {(item.requiresProof || item.proofLabel) && (
                                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                                    Proof{item.requiresProof ? " required" : ""}{item.proofLabel ? ` · ${item.proofLabel}` : ""}
                                  </p>
                                )}
                              </div>
                              <span className={`shrink-0 text-[10px] font-mono ${item.checked ? "text-emerald-500" : "text-muted-foreground"}`}>
                                {item.checked ? "Checked" : "Open"}
                              </span>
                            </div>
                            {(item.requiresProof || item.proofLabel || itemProofs.length > 0) && (
                              <div className="mt-2 pl-5">
                                {itemProofs.length > 0 ? (
                                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                                    {itemProofs.map((proof, proofIndex) => (
                                      <ChecklistProofTile
                                        key={proof.id}
                                        proof={proof}
                                        getReadUrl={ledger.getReadUrl}
                                        onOpen={() => openProofGallery(itemProofs, proofIndex)}
                                      />
                                    ))}
                                  </div>
                                ) : (
                                  <div className="rounded-md border border-dashed px-2 py-1.5 text-[10px] text-muted-foreground">
                                    No uploaded proof matched this checklist item yet.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {phaseProofs.length > 0 && (
                        <div className="rounded-md border border-border/50 bg-background/35 p-2 text-xs">
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <div>
                              <p className="text-xs font-medium text-foreground">Additional proof</p>
                              <p className="text-[10px] text-muted-foreground">
                                Phase-level uploads matched to {phase.phaseLabel}.
                              </p>
                            </div>
                            <Badge variant="outline" className="text-[10px]">
                              {phaseProofs.length}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                            {phaseProofs.map((proof, proofIndex) => (
                              <ChecklistProofTile
                                key={proof.id}
                                proof={proof}
                                getReadUrl={ledger.getReadUrl}
                                onOpen={() => openProofGallery(phaseProofs, proofIndex)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : exec && exec.phases.length > 0 ? (
        <Card className="shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
              <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Phase Completion</h2>
            </div>
            <div className="space-y-1.5">
              {exec.phases.map((phase) => {
                const isPass = phase.status === "qa_passed";
                const isFail = phase.status === "qa_failed";
                const isSubmitted = phase.status === "submitted";
                const isActive = phase.status === "in_progress";
                const checksDone = phase.checklist.filter((c) => c.checked).length;
                const checksTotal = phase.checklist.length;
                return (
                  <div key={phase.phaseName} className="flex items-center gap-2.5 py-1.5 px-2 rounded-md bg-muted/30">
                    {isPass ? (
                      <CheckCircle className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                    ) : isFail ? (
                      <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
                    ) : isSubmitted ? (
                      <Clock className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                    ) : isActive ? (
                      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-primary" />
                    ) : (
                      <div className="h-3.5 w-3.5 shrink-0 rounded-full border border-muted-foreground/30" />
                    )}
                    <span className={`text-xs flex-1 truncate ${isPass ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                      {phase.phaseName}
                    </span>
                    <span className={`text-[10px] font-mono tabular-nums shrink-0 ${isPass ? "text-emerald-500" : isFail ? "text-destructive" : isSubmitted ? "text-amber-500" : "text-muted-foreground"}`}>
                      {isPass ? "Passed" : isFail ? "QA Failed" : isSubmitted ? "Submitted" : isActive ? `${checksDone}/${checksTotal}` : "Not Started"}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Quality Assurance</h2>
            </div>
            <div className="flex items-center gap-2">
              {(() => {
                const closeoutEligible =
                  job.currentStage === "QA Review" ||
                  job.currentStage === "Completed" ||
                  job.qaSubmitted === true ||
                  (job.closeoutStatus && job.closeoutStatus !== "Not Started");
                return closeoutEligible ? (
                  <Link to={`/closeout?jobId=${job.id}`}>
                    <Button size="sm" className="h-7 text-xs gap-1">
                      Open Closeout <ArrowRight className="h-3 w-3" />
                    </Button>
                  </Link>
                ) : null;
              })()}
              {derivedQa.legacySubmitNeeded && (
                <Link to={`/qa-form?jobId=${job.id}`}>
                  <Button size="sm" variant="outline" className="h-7 text-xs">Submit QA</Button>
                </Link>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="QA Status" value={derivedQa.label} tone={derivedQa.tone} />
            <Stat label="Open Issues" value={openIssues.length} tone={openIssues.length === 0 ? "good" : "bad"} />
            <Stat label="Resolved" value={resolvedIssues.length} tone="default" />
            <Stat label="Closeout" value={job.closeoutStatus || "Not Started"} tone={job.closeoutStatus === "Closed" ? "good" : "default"} />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Issues</h2>
            </div>
            {(openIssues.length > 0 || resolvedIssues.length > 0) && (
              <Link to={`/issues?job=${job.id}`} className="text-[10px] text-primary hover:underline">View all</Link>
            )}
          </div>
          {openIssues.length === 0 && resolvedIssues.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No issues recorded for this job.</p>
          ) : (
            <div className="space-y-1.5">
              {[...openIssues, ...resolvedIssues].slice(0, 8).map((iss) => (
                <Link key={iss.id} to={`/issues/${iss.id}`} className="flex items-center justify-between text-xs border rounded-md px-3 py-2 hover:bg-muted/30">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`h-2 w-2 rounded-full ${iss.status === "Open" || iss.status === "In Progress" ? "bg-destructive" : "bg-success"}`} />
                    <span className="truncate font-medium">{iss.description}</span>
                  </div>
                  <span className="text-muted-foreground text-[10px] shrink-0 ml-2">{iss.severity} · {iss.status}</span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(proofGallery)} onOpenChange={(open) => {
        if (!open) setProofGallery(null);
      }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-8">
              <span className="truncate">
                {activeProof?.caption ?? activeProof?.file_name ?? "Installer proof"}
              </span>
              {proofGalleryCount > 1 && (
                <span className="shrink-0 text-xs font-normal text-muted-foreground">
                  {(proofGallery?.index ?? 0) + 1} / {proofGalleryCount}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="relative min-h-[320px] overflow-hidden rounded-lg border bg-muted/20">
            {proofGalleryLoading && !proofGalleryUrl ? (
              <div className="flex min-h-[320px] items-center justify-center text-sm text-muted-foreground">
                Loading proof...
              </div>
            ) : proofGalleryUrl && activeProof && isProofImage(activeProof) ? (
              <img src={proofGalleryUrl} alt={activeProof.caption ?? activeProof.file_name ?? "Installer proof"} className="max-h-[75vh] w-full object-contain" />
            ) : proofGalleryUrl && activeProof ? (
              <iframe src={proofGalleryUrl} title={activeProof.caption ?? activeProof.file_name ?? "Installer proof"} className="h-[75vh] w-full bg-background" />
            ) : (
              <div className="flex min-h-[320px] items-center justify-center text-sm text-muted-foreground">
                Couldn't load this proof.
              </div>
            )}

            {proofGalleryCount > 1 && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="absolute left-3 top-1/2 h-9 w-9 -translate-y-1/2 bg-background/90"
                  onClick={() => moveProofGallery(-1)}
                  aria-label="Previous proof"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="absolute right-3 top-1/2 h-9 w-9 -translate-y-1/2 bg-background/90"
                  onClick={() => moveProofGallery(1)}
                  aria-label="Next proof"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
          {activeProof && (
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>{[formatRelativeTime(activeProof.uploaded_at), formatProofBytes(activeProof.byte_size)].filter(Boolean).join(" · ")}</span>
              <span className="truncate">{activeProof.file_name}</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoGalleryViewer({ photos }: { photos: string[] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const activePhoto = activeIndex === null ? null : photos[activeIndex] ?? null;
  const [activePhotoUrl, setActivePhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (activeIndex === null) return;
    if (photos.length === 0) {
      setActiveIndex(null);
      return;
    }
    if (activeIndex >= photos.length) {
      setActiveIndex(photos.length - 1);
    }
  }, [activeIndex, photos.length]);

  const movePhoto = useCallback((delta: number) => {
    setActiveIndex((current) => {
      if (current === null || photos.length === 0) return current;
      return (current + delta + photos.length) % photos.length;
    });
  }, [photos.length]);

  useEffect(() => {
    let cancelled = false;
    setActivePhotoUrl(null);
    if (!activePhoto) return;

    void createSignedStorageFullImageUrl(activePhoto, { bucket: IMPORT_PHOTO_BUCKET })
      .then((url) => {
        if (!cancelled) setActivePhotoUrl(url);
      })
      .catch(() => {
        if (!cancelled) setActivePhotoUrl(activePhoto);
      });

    return () => {
      cancelled = true;
    };
  }, [activePhoto]);

  if (photos.length === 0) {
    return <p className="text-xs text-muted-foreground py-6 text-center">No photos uploaded yet.</p>;
  }

  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {photos.slice(0, 18).map((photo, index) => (
          <button
            key={getImportPhotoStableKey(photo, `job-photo-${index}`)}
            type="button"
            onClick={() => setActiveIndex(index)}
            className="aspect-square rounded-md border bg-muted/30 overflow-hidden block focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label={`Open job photo ${index + 1}`}
          >
            <StorageThumbnailImage
              src={photo}
              bucket={IMPORT_PHOTO_BUCKET}
              alt={`Job photo ${index + 1}`}
              className="w-full h-full object-cover transition-transform hover:scale-105"
            />
          </button>
        ))}
      </div>

      {photos.length > 18 && (
        <p className="mt-2 text-[10px] text-muted-foreground">
          Showing 18 of {photos.length} photos. Open any photo to browse the full gallery.
        </p>
      )}

      <Dialog open={activeIndex !== null} onOpenChange={(open) => !open && setActiveIndex(null)}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-2 border-b">
            <DialogTitle className="text-sm">
              Job photo {activeIndex === null ? 0 : activeIndex + 1} of {photos.length}
            </DialogTitle>
          </DialogHeader>

          <div className="relative bg-background">
            <div className="flex min-h-[50vh] max-h-[72vh] items-center justify-center bg-black/80">
              {activePhoto && activePhotoUrl && (
                <img
                  src={activePhotoUrl}
                  alt={`Job photo ${activeIndex === null ? "" : activeIndex + 1}`}
                  className="max-h-[72vh] w-full object-contain"
                  decoding="async"
                />
              )}
            </div>

            {photos.length > 1 && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-background/90 shadow-lg"
                  onClick={() => movePhoto(-1)}
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-background/90 shadow-lg"
                  onClick={() => movePhoto(1)}
                  aria-label="Next photo"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </>
            )}
          </div>

          {photos.length > 1 && (
            <div className="flex gap-2 overflow-x-auto border-t p-3">
              {photos.map((photo, index) => (
                <button
                  key={`thumb-${getImportPhotoStableKey(photo, `job-photo-${index}`)}`}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  className={`h-14 w-14 shrink-0 overflow-hidden rounded border bg-muted/30 focus:outline-none focus:ring-2 focus:ring-primary ${
                    index === activeIndex ? "ring-2 ring-primary" : "opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Go to job photo ${index + 1}`}
                >
                  <StorageThumbnailImage
                    src={photo}
                    bucket={IMPORT_PHOTO_BUCKET}
                    alt={`Job photo thumbnail ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: React.ReactNode; tone: "default" | "good" | "warn" | "bad" }) {
  const toneCls = tone === "good" ? "text-success" : tone === "warn" ? "text-warning" : tone === "bad" ? "text-destructive" : "";
  return (
    <div className="p-3 rounded-md border bg-muted/20">
      <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">{label}</p>
      <p className={`text-base font-bold mt-0.5 ${toneCls}`}>{value}</p>
    </div>
  );
}
